import React, { useState, useEffect, useRef } from 'react';
import { useConfirm } from './ConfirmDialog';
import { DashboardSkeleton } from './Skeleton';
import EmptyState from './EmptyState';
import TaskLoader from '../Components/TaskLoader';
import FullPageLoader from '../Components/FullPageLoader';
// Charts extracted to KRICharts.js so KRIMatrix.js stays focused on app logic.
import { Sparkline as _Sparkline, DonutChart as _DonutChart, HBarChart as _HBarChart, GaugeChart as _GaugeChart } from './KRICharts';
import NMT_SMALL_LOGO from './nmtSmallLogo';

// Per-category UI hints for the Step 4 baseline form.
// Mirrors the backend's CATEGORY_PROFILES — keep keys in sync with kri_views.py.
const CATEGORY_UI = {
    edr: {
        thresholds: [95, 80], sync_hours_default: 24,
        asset_types: ['endpoint', 'server', 'virtual_machine', 'mobile'],
        asset_types_label: 'Apply to Asset Types (optional filter)',
        health: {
            title: 'Agent Health Compliance',
            blurb: 'A device with the agent installed but offline for too long is still a risk. Enable this to mark stale agents as non-compliant.',
            checkbox: 'Flag offline agents as non-compliant',
            threshold_label: 'Offline threshold:', threshold_default: 7,
        },
    },
    mdm: {
        thresholds: [98, 90], sync_hours_default: 24,
        asset_types: ['mobile', 'tablet', 'laptop', 'kiosk'],
        asset_types_label: 'Apply to Device Types (optional filter)',
        health: {
            title: 'Compliance State',
            blurb: 'Devices reporting "noncompliant" or "in grace period" should count against your KRI. Enable to flag any device not in fully compliant state.',
            checkbox: 'Flag noncompliant or grace-period devices',
            threshold_label: 'Grace-period threshold:', threshold_default: 7,
        },
    },
    mfa: {
        thresholds: [100, 95], sync_hours_default: 6,
        asset_types: ['employee', 'contractor', 'service_account', 'admin'],
        asset_types_label: 'Apply to User Types (optional filter)',
        health: {
            title: 'MFA & Activity Health',
            blurb: 'A user without MFA or with no recent login is a risk. Enable to flag unenrolled or dormant accounts.',
            checkbox: 'Flag users without MFA or recent login',
            threshold_label: 'Dormant-account threshold:', threshold_default: 30,
        },
    },
    siem: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['endpoint', 'server', 'virtual_machine', 'firewall', 'cloud'],
        asset_types_label: 'Apply to Log Source Types (optional filter)',
        health: {
            title: 'Log Source Health',
            blurb: 'A log source that has stopped forwarding events is a coverage gap. Enable to flag stale sources.',
            checkbox: 'Flag stale log sources as non-compliant',
            threshold_label: 'No-events threshold:', threshold_default: 1,
        },
    },
    dlp: {
        thresholds: [95, 80], sync_hours_default: 24,
        asset_types: ['endpoint', 'server', 'mobile'],
        asset_types_label: 'Apply to Asset Types (optional filter)',
        health: {
            title: 'Policy Enforcement Health',
            blurb: 'A device with the DLP agent installed but policy disabled is still a risk.',
            checkbox: 'Flag devices with disabled or missing policy',
            threshold_label: 'Disabled-policy threshold:', threshold_default: 7,
        },
    },
    vuln_scanner: {
        thresholds: [90, 70], sync_hours_default: 168,
        asset_types: ['endpoint', 'server', 'virtual_machine', 'cloud', 'network_device'],
        asset_types_label: 'Apply to Scan Targets (optional filter)',
        health: {
            title: 'Scan Recency',
            blurb: 'An asset that has not been scanned recently is a coverage gap regardless of whether the scanner can see it.',
            checkbox: 'Flag assets not scanned within threshold',
            threshold_label: 'Last-scan threshold:', threshold_default: 7,
        },
    },
    patch_mgmt: {
        thresholds: [90, 70], sync_hours_default: 168,
        asset_types: ['endpoint', 'server', 'virtual_machine'],
        asset_types_label: 'Apply to Asset Types (optional filter)',
        health: {
            title: 'Patch Currency',
            blurb: 'A device behind on critical patches is a risk even if it is reporting in.',
            checkbox: 'Flag devices behind on critical patches',
            threshold_label: 'Behind-on-critical threshold:', threshold_default: 30,
        },
    },
    pam: {
        thresholds: [100, 95], sync_hours_default: 6,
        asset_types: ['admin_account', 'service_account', 'shared_account', 'emergency_account'],
        asset_types_label: 'Apply to Account Types (optional filter)',
        health: {
            title: 'Session Logging Health',
            blurb: 'A privileged session that was not recorded is a control gap. Enable to flag accounts that bypass the vault.',
            checkbox: 'Flag accounts with un-vaulted or un-logged sessions',
            threshold_label: 'Audit window:', threshold_default: 7,
        },
    },
    encryption: {
        thresholds: [100, 95], sync_hours_default: 168,
        asset_types: ['endpoint', 'mobile', 'removable_media'],
        asset_types_label: 'Apply to Storage Types (optional filter)',
        health: {
            title: 'Encryption Status',
            blurb: 'Any device with encryption disabled or in a partial state is a critical finding.',
            checkbox: 'Flag devices with encryption disabled',
            threshold_label: '', threshold_default: 1,
        },
    },
    backup: {
        thresholds: [90, 70], sync_hours_default: 24,
        asset_types: ['server', 'database', 'virtual_machine', 'fileshare'],
        asset_types_label: 'Apply to System Types (optional filter)',
        health: {
            title: 'Backup Freshness',
            blurb: 'A system whose last backup is older than the RPO is a continuity risk.',
            checkbox: 'Flag systems with stale or failed backups',
            threshold_label: 'Stale-backup threshold:', threshold_default: 1,
        },
    },
    custom: {
        thresholds: [95, 80], sync_hours_default: 24,
        asset_types: ['endpoint', 'server', 'virtual_machine', 'mobile'],
        asset_types_label: 'Apply to Asset Types (optional filter)',
        health: {
            title: 'Reporting Health',
            blurb: 'A device that has stopped reporting is a coverage gap.',
            checkbox: 'Flag stale records as non-compliant',
            threshold_label: 'Stale threshold:', threshold_default: 7,
        },
    },
    cspm: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['cloud_account', 'aws', 'azure', 'gcp', 'kubernetes_cluster'],
        asset_types_label: 'Apply to Cloud Accounts (optional filter)',
        health: {
            title: 'Misconfiguration Health',
            blurb: 'Critical cloud misconfigurations (public S3, open SGs, exposed RDS) must be remediated quickly.',
            checkbox: 'Flag accounts with open critical misconfigurations',
            threshold_label: 'Critical-finding age threshold:', threshold_default: 3
        },
    },
    ciem: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['human_identity', 'service_account', 'role'],
        asset_types_label: 'Apply to Identity Types (optional filter)',
        health: {
            title: 'Privilege Drift',
            blurb: 'Identities with unused or excessive privileges are a risk.',
            checkbox: 'Flag identities with unused privileges (90d+)',
            threshold_label: 'Unused-privilege threshold:', threshold_default: 90
        },
    },
    casb: {
        thresholds: [95, 80], sync_hours_default: 24,
        asset_types: ['sanctioned_app', 'shadow_app', 'user'],
        asset_types_label: 'Apply to App Types (optional filter)',
        health: {
            title: 'Policy Enforcement',
            blurb: 'Unsanctioned SaaS or sensitive-data uploads to unmanaged apps are gaps.',
            checkbox: 'Flag unsanctioned SaaS detections',
            threshold_label: 'Detection-age threshold:', threshold_default: 7
        },
    },
    secure_web_gateway: {
        thresholds: [98, 90], sync_hours_default: 24,
        asset_types: ['user', 'endpoint', 'site'],
        asset_types_label: 'Apply to Traffic Sources (optional filter)',
        health: {
            title: 'Inspection Coverage',
            blurb: 'Traffic that bypasses the SWG is uninspected and unaccounted for.',
            checkbox: 'Flag users / endpoints with bypass traffic',
            threshold_label: 'Bypass-tolerance threshold:', threshold_default: 1
        },
    },
    email_security: {
        thresholds: [99, 95], sync_hours_default: 24,
        asset_types: ['mailbox', 'distribution_list', 'shared_mailbox'],
        asset_types_label: 'Apply to Mailbox Types (optional filter)',
        health: {
            title: 'Mailbox Protection',
            blurb: 'A mailbox without active filtering is exposed to phishing and BEC.',
            checkbox: 'Flag unprotected mailboxes',
            threshold_label: 'Protection lag threshold:', threshold_default: 1
        },
    },
    code_security: {
        thresholds: [90, 70], sync_hours_default: 168,
        asset_types: ['repo', 'project', 'package'],
        asset_types_label: 'Apply to Repo Types (optional filter)',
        health: {
            title: 'Scan Recency',
            blurb: 'Code that has not been scanned recently may have unaddressed CVEs.',
            checkbox: 'Flag repos not scanned within threshold',
            threshold_label: 'Last-scan threshold:', threshold_default: 7
        },
    },
    container_security: {
        thresholds: [90, 70], sync_hours_default: 24,
        asset_types: ['cluster', 'namespace', 'image', 'workload'],
        asset_types_label: 'Apply to Container Targets (optional filter)',
        health: {
            title: 'Image & Runtime Health',
            blurb: 'Unscanned images and unmonitored clusters are blind spots.',
            checkbox: 'Flag clusters / images outside coverage',
            threshold_label: 'Last-scan threshold:', threshold_default: 1
        },
    },
    threat_intel: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['feed', 'collection'],
        asset_types_label: 'Apply to Feed Types (optional filter)',
        health: {
            title: 'Feed Freshness',
            blurb: 'A TI feed that has stopped updating provides false confidence.',
            checkbox: 'Flag feeds with no updates in threshold',
            threshold_label: 'Stale-feed threshold:', threshold_default: 1
        },
    },
    asset_discovery: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['external_asset', 'internal_asset', 'cloud_resource'],
        asset_types_label: 'Apply to Asset Surfaces (optional filter)',
        health: {
            title: 'Discovery Freshness',
            blurb: 'An asset that has not been re-discovered recently may have drifted out of scope.',
            checkbox: 'Flag assets not re-discovered within threshold',
            threshold_label: 'Re-discovery threshold:', threshold_default: 7
        },
    },
    ndr: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['network_segment', 'sensor', 'cloud_vpc'],
        asset_types_label: 'Apply to Sensor Types (optional filter)',
        health: {
            title: 'Sensor Visibility',
            blurb: 'A sensor that has lost traffic is a blind spot for lateral-movement detection.',
            checkbox: 'Flag sensors with no recent traffic',
            threshold_label: 'No-traffic threshold:', threshold_default: 1
        },
    },
    soar: {
        thresholds: [90, 70], sync_hours_default: 24,
        asset_types: ['playbook', 'incident_type', 'integration'],
        asset_types_label: 'Apply to Playbook Categories (optional filter)',
        health: {
            title: 'Automation Coverage',
            blurb: 'Incident types without playbooks slow MTTR.',
            checkbox: 'Flag incident types without an automated playbook',
            threshold_label: '', threshold_default: 1
        },
    },
    itsm: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['security_ticket', 'change_request', 'incident'],
        asset_types_label: 'Apply to Ticket Types (optional filter)',
        health: {
            title: 'SLA Compliance',
            blurb: 'Tickets past SLA are audit findings waiting to happen.',
            checkbox: 'Flag tickets past SLA',
            threshold_label: 'Past-SLA grace:', threshold_default: 1
        },
    },
    secrets_mgmt: {
        thresholds: [100, 95], sync_hours_default: 24,
        asset_types: ['production_secret', 'service_account_credential', 'api_key'],
        asset_types_label: 'Apply to Secret Types (optional filter)',
        health: {
            title: 'Vault & Rotation',
            blurb: 'Stale or un-vaulted secrets are critical findings.',
            checkbox: 'Flag secrets older than rotation policy',
            threshold_label: 'Rotation threshold:', threshold_default: 90
        },
    },
    sspm: {
        thresholds: [95, 85], sync_hours_default: 24,
        asset_types: ['saas_app', 'tenant', 'integration'],
        asset_types_label: 'Apply to SaaS Apps (optional filter)',
        health: {
            title: 'SaaS Posture',
            blurb: 'Misconfigured SaaS apps (over-shared content, weak admin scopes) leak data.',
            checkbox: 'Flag apps with critical misconfigurations',
            threshold_label: 'Critical-finding age:', threshold_default: 3
        },
    },
};

const getCategoryUI = (cat) => CATEGORY_UI[(cat || 'custom').toLowerCase()] || CATEGORY_UI.custom;

// Customer-friendly display names for the Step 4 category picker. Mirrors the
// backend's CATEGORY_PROFILES[*]['display'] so the labels match across UI/API.
const CATEGORY_DISPLAY = {
    edr: 'EDR / Endpoint Detection & Response',
    mdm: 'MDM / Mobile Device Management',
    mfa: 'MFA / Identity',
    siem: 'SIEM / Log Management',
    dlp: 'DLP / Data Loss Prevention',
    vuln_scanner: 'Vulnerability Scanner',
    patch_mgmt: 'Patch Management',
    pam: 'Privileged Access Management',
    encryption: 'Disk / Data Encryption',
    backup: 'Backup & Recovery',
    custom: 'Custom Tool (generic)',
    cspm: 'CSPM / Cloud Security Posture',
    ciem: 'CIEM / Cloud Identity Entitlements',
    casb: 'CASB / Cloud Access Security Broker',
    secure_web_gateway: 'Secure Web Gateway / SSE',
    email_security: 'Email Security',
    code_security: 'Application & Code Security (SAST/SCA)',
    container_security: 'Container & Kubernetes Security',
    threat_intel: 'Threat Intelligence',
    asset_discovery: 'Asset Discovery & Attack Surface',
    ndr: 'NDR / Network Detection & Response',
    soar: 'SOAR / Security Orchestration',
    itsm: 'ITSM / IT Service Management',
    secrets_mgmt: 'Secrets Management',
    sspm: 'SaaS Security Posture Management',
};
const getCategoryDisplay = (key) => CATEGORY_DISPLAY[(key || 'custom').toLowerCase()] || (key || '').replace(/_/g, ' ');

// Category-aware CSV upload guidance (CSV Upload Only path).
// Asset-shape vs ticket-shape vs identity-shape categories show different
// required columns and matching semantics.
const CSV_UPLOAD_HINTS = {
    itsm: {
        title: 'Upload Ticket Export',
        blurb: 'Upload a CSV of your ITSM/help-desk tickets. NOVA AI auto-detects ticket headers; each ticket becomes one KRI compliance record. The score is "% SLA met" computed against the priority-tiered SLA matrix.',
        required_label: 'Ticket Number',
        columns_str: '<code>Ticket Number</code> (required), <code>Status</code>, <code>Priority</code>, <code>Created On</code>, <code>CloserOn</code>, <code>Pending Since</code>, <code>Reopen</code>, <code>Subcategory</code>',
        match_kind: 'ticket',
    },
    mfa: {
        title: 'Upload User Export',
        blurb: 'Upload a CSV of users from your identity provider. Each user becomes one KRI compliance record. The score is MFA enrollment + recent-login coverage.',
        required_label: 'user_email',
        columns_str: '<code>user_email</code> (required), <code>full_name</code>, <code>mfa_enabled</code>, <code>last_login</code>, <code>account_type</code> (employee / contractor / service_account)',
        match_kind: 'user',
    },
    secrets_mgmt: {
        title: 'Upload Secrets Inventory',
        blurb: 'Upload a CSV of secrets / vault entries. Each row becomes a KRI compliance record. Score = % vaulted with rotation within policy.',
        required_label: 'secret_id',
        columns_str: '<code>secret_id</code> (required), <code>account_name</code>, <code>last_rotation</code>, <code>vaulted</code>, <code>owner</code>',
        match_kind: 'secret',
    },
    code_security: {
        title: 'Upload Repo Inventory',
        blurb: 'Upload a CSV of repos / projects. Each row becomes a KRI compliance record. Score = % scanned recently with no critical findings.',
        required_label: 'repo_name',
        columns_str: '<code>repo_name</code> (required), <code>project_id</code>, <code>last_scan</code>, <code>open_critical</code>, <code>open_high</code>',
        match_kind: 'repo',
    },
    pam: {
        title: 'Upload Account Export',
        blurb: 'Upload a CSV of privileged accounts. Each account becomes a KRI compliance record. Score = % vaulted with session logging.',
        required_label: 'account_name',
        columns_str: '<code>account_name</code> (required), <code>account_type</code>, <code>vaulted</code>, <code>last_session_logged</code>, <code>owner</code>',
        match_kind: 'account',
    },
    // Default — asset shape
    _default: {
        title: 'Upload Tool Device List',
        blurb: 'Upload a CSV with the devices/agents managed by this tool. Must include a hostname column. After creating the integration, run a Sync to compare against your asset baseline.',
        required_label: 'hostname',
        columns_str: '<code>hostname</code> (required), <code>ip_address</code>, <code>serial_number</code>, <code>mac_address</code>, <code>agent_version</code>, <code>agent_status</code>',
        match_kind: 'asset',
    },
};
const getCsvUploadHints = (cat) => CSV_UPLOAD_HINTS[(cat || '').toLowerCase()] || CSV_UPLOAD_HINTS._default;

// Dashboard tile labels per category. Used wherever the dashboard renders
// the four-state breakdown (installed / healthy / unhealthy / missing) and
// the score badge text. Defaults match the EDR coverage paradigm.
const DASHBOARD_LABELS = {
    edr: { score: 'Compliant', installed: 'Installed', healthy: 'Healthy', unhealthy: 'Unhealthy', missing: 'Missing' },
    mdm: { score: 'Compliant', installed: 'Enrolled', healthy: 'Compliant State', unhealthy: 'Noncompliant', missing: 'Unenrolled' },
    mfa: { score: 'Coverage', installed: 'MFA Enabled', healthy: 'MFA + Recent Login', unhealthy: 'MFA but Dormant', missing: 'MFA Disabled' },
    siem: { score: 'Forwarding', installed: 'Forwarding', healthy: 'Recent Events', unhealthy: 'Stale Source', missing: 'Not Forwarding' },
    dlp: { score: 'Coverage', installed: 'Policy Active', healthy: 'Active & Enforcing', unhealthy: 'Disabled Policy', missing: 'No Agent' },
    vuln_scanner: { score: 'Coverage', installed: 'Scanned', healthy: 'Recently Scanned', unhealthy: 'Stale Scan', missing: 'Not Scanned' },
    patch_mgmt: { score: 'Currency', installed: 'Reporting', healthy: 'Up to Date', unhealthy: 'Behind on Critical', missing: 'Not Reporting' },
    pam: { score: 'Coverage', installed: 'Vaulted', healthy: 'Vaulted & Logged', unhealthy: 'Vaulted, No Logs', missing: 'Standing Privilege' },
    encryption: { score: 'Coverage', installed: 'Encrypted', healthy: 'Fully Encrypted', unhealthy: 'Partial / Suspended', missing: 'Not Encrypted' },
    backup: { score: 'Coverage', installed: 'Protected', healthy: 'Recent Backup', unhealthy: 'Stale or Failed', missing: 'No Backup' },
    custom: { score: 'Compliant', installed: 'Reporting', healthy: 'Healthy', unhealthy: 'Unhealthy', missing: 'Missing' },
    cspm: { score: 'Coverage', installed: 'Scanned', healthy: 'Clean', unhealthy: 'Misconfigured', missing: 'Not Scanned' },
    ciem: { score: 'Coverage', installed: 'Reviewed', healthy: 'Right-sized', unhealthy: 'Over-privileged', missing: 'Not Reviewed' },
    casb: { score: 'Coverage', installed: 'Sanctioned', healthy: 'Policy Active', unhealthy: 'Policy Issue', missing: 'Shadow / Unsanctioned' },
    secure_web_gateway: { score: 'Coverage', installed: 'Routed', healthy: 'Inspected', unhealthy: 'Bypass Detected', missing: 'Not Routed' },
    email_security: { score: 'Coverage', installed: 'Protected', healthy: 'Active Filtering', unhealthy: 'Filter Issue', missing: 'Unprotected' },
    code_security: { score: 'Coverage', installed: 'Scanned', healthy: 'Clean', unhealthy: 'Vulnerable', missing: 'Not Scanned' },
    container_security: { score: 'Coverage', installed: 'Scanned', healthy: 'Clean', unhealthy: 'Vulnerable / Drift', missing: 'Not Scanned' },
    threat_intel: { score: 'Freshness', installed: 'Active Feed', healthy: 'Up to Date', unhealthy: 'Stale Feed', missing: 'Inactive Feed' },
    asset_discovery: { score: 'Coverage', installed: 'Inventoried', healthy: 'Owner Assigned', unhealthy: 'Orphaned', missing: 'Unknown' },
    ndr: { score: 'Coverage', installed: 'Sensor Active', healthy: 'Traffic Captured', unhealthy: 'Sensor Issue', missing: 'No Sensor' },
    soar: { score: 'Coverage', installed: 'Automated', healthy: 'Playbook OK', unhealthy: 'Playbook Failed', missing: 'Manual Only' },
    itsm: { score: 'SLA Met', installed: 'Tracked', healthy: 'Within SLA', unhealthy: 'Reopened', missing: 'Past SLA' },
    secrets_mgmt: { score: 'Coverage', installed: 'Vaulted', healthy: 'Rotated', unhealthy: 'Stale Secret', missing: 'Plain Text' },
    sspm: { score: 'Coverage', installed: 'Onboarded', healthy: 'Compliant', unhealthy: 'Misconfigured', missing: 'Not Onboarded' },
};
// Prefer backend-supplied effective labels (which already merge per-tool overrides
// + category defaults). Fall back to local DASHBOARD_LABELS for offline/older
// payloads, so an old backend doesn't blank the UI.
const getDashboardLabels = (toolOrCat, maybeCat) => {
    if (toolOrCat && typeof toolOrCat === 'object') {
        if (toolOrCat.dashboard_labels && typeof toolOrCat.dashboard_labels === 'object') {
            return toolOrCat.dashboard_labels;
        }
        const cat = toolOrCat.tool_category || toolOrCat.tool_type || maybeCat;
        return DASHBOARD_LABELS[(cat || 'custom').toLowerCase()] || DASHBOARD_LABELS.custom;
    }
    return DASHBOARD_LABELS[(toolOrCat || 'custom').toLowerCase()] || DASHBOARD_LABELS.custom;
};

// Sparkline — small inline trend chart for drift over time
// Charts moved to KRICharts.js — these are thin local aliases for the imports
// so that the existing JSX usage (<Sparkline ... />) keeps working unchanged.
const Sparkline = _Sparkline;

// ToolInsightPanels — wraps the four post-sync panels rendered under the per-tool
// detail block on the KRI dashboard:
//   1. NOVA AI Baseline Recommender (Phase 2)
//   2. Tool Usage (Phase 1)
//   3. Baseline Drift (Phase 4)
//   4. Baseline Policies (Phase 3)
function ToolInsightPanels({ tool, aiRec, aiLoading, usageData, driftData, policiesData,
    onLoadAi, onLoadUsage, onLoadDrift, onLoadPolicies,
    onAfterApply }) {
    const [open, setOpen] = useState({ ai: true, usage: false, drift: false, policies: false });
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [applyState, setApplyState] = useState({ busy: false, msg: '' });
    useEffect(() => {
        if (open.usage && !usageData) onLoadUsage();
        if (open.drift && !driftData) onLoadDrift();
        if (open.policies && !policiesData) onLoadPolicies();
        // ai is on-demand only
    }, [open, usageData, driftData, policiesData]); // eslint-disable-line react-hooks/exhaustive-deps

    // When a fresh recommendation arrives, seed the edit form with its values.
    useEffect(() => {
        if (aiRec?.recommendation) {
            const r = aiRec.recommendation;
            setEditForm({
                match_field: r.match_field || 'hostname',
                compliance_threshold: r.compliance_threshold ?? 95,
                critical_threshold: r.critical_threshold ?? 80,
                os_types: (r.scope_filters?.os_types || []).join(', '),
                licenses_purchased: '',
            });
            setEditing(false);
        }
    }, [aiRec]);

    const applyBaseline = async (overrides) => {
        setApplyState({ busy: true, msg: '' });
        try {
            const { kri_apply_baseline_to_tool } = await import('../services');
            const payload = overrides || {
                match_field: aiRec?.recommendation?.match_field,
                compliance_threshold: aiRec?.recommendation?.compliance_threshold,
                critical_threshold: aiRec?.recommendation?.critical_threshold,
                scope_filters: aiRec?.recommendation?.scope_filters,
            };
            const r = await kri_apply_baseline_to_tool(tool.id, payload);
            const body = r?.statusCode || r;
            if (body?.status === 1 || body?.status === '1') {
                setApplyState({ busy: false, msg: body?.response_data?.data?.message || 'Applied successfully.' });
                setTimeout(() => setApplyState({ busy: false, msg: '' }), 4000);
                setEditing(false);
                // Real-time refresh — let the parent reload dashboard data so the
                // updated thresholds, match field, and policies show immediately.
                if (typeof onAfterApply === 'function') {
                    try { await onAfterApply(); } catch (e) { }
                }
            } else {
                setApplyState({ busy: false, msg: body?.error?.message || 'Apply failed.' });
            }
        } catch (e) {
            setApplyState({ busy: false, msg: 'Apply failed: ' + (e?.message || 'network error') });
        }
    };

    const Section = ({ id, title, badge, children }) => (
        <div style={{ borderTop: '1px solid #f0f2f5' }}>
            <div onClick={() => setOpen({ ...open, [id]: !open[id] })}
                style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: open[id] ? '#fafbfc' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a' }}>{title}</span>
                    {badge && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 8, color: '#fff', background: badge.color }}>{badge.text}</span>}
                </div>
                <span style={{ fontSize: 14, color: '#7a869a' }}>{open[id] ? '−' : '+'}</span>
            </div>
            {open[id] && <div style={{ padding: '12px 20px 16px' }}>{children}</div>}
        </div>
    );

    return (
        <>
            {/* 1. NOVA AI Baseline Recommender */}
            <Section id="ai" title="NOVA AI — Baseline Recommendation" badge={{ text: 'AI', color: '#7c3aed' }}>
                {!aiRec && !aiLoading && (
                    <div>
                        <div style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 10 }}>
                            Let NOVA AI analyse this tool's last sync and recommend the right match field, thresholds, in-scope filters, and gap-flags for your baseline.
                        </div>
                        <button className="ap-btn ap-btn--primary" style={{ background: '#7c3aed' }} onClick={onLoadAi}>
                            Get NOVA AI Recommendation
                        </button>
                    </div>
                )}
                {aiLoading && (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>NOVA AI is analysing the sync data…</div>
                )}
                {aiRec && !aiRec.error && aiRec.recommendation && (() => {
                    const rec = aiRec.recommendation;
                    const stats = aiRec.stats || {};
                    return (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 6, color: '#fff', background: rec.confidence === 'high' ? '#0f7b4f' : rec.confidence === 'medium' ? '#d97706' : '#dc2626' }}>
                                    {String(rec.confidence || '').toUpperCase()} CONFIDENCE
                                </span>
                                {(rec.category_recognised || stats.tool_type) && (
                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 6, color: '#fff', background: '#1b2a4a' }}>
                                        CATEGORY: {String(rec.category_recognised || stats.tool_type).replace(/_/g, ' ').toUpperCase()}
                                    </span>
                                )}
                                <span style={{ fontSize: 9, color: '#7a869a' }}>
                                    Source: {aiRec.recommendation.source === 'nova_ai' ? 'NOVA AI' : 'Heuristic fallback'} • Analysed {stats.total_evaluated} records
                                </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                <div style={{ padding: 10, background: '#f8f4ff', border: '1px solid #d8c8f5', borderRadius: 6 }}>
                                    <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700, marginBottom: 3 }}>SUGGESTED MATCH FIELD</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>{rec.match_field}</div>
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>{rec.match_field_reason}</div>
                                </div>
                                <div style={{ padding: 10, background: '#f8f4ff', border: '1px solid #d8c8f5', borderRadius: 6 }}>
                                    <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700, marginBottom: 3 }}>SUGGESTED THRESHOLDS</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>{rec.compliance_threshold}% / {rec.critical_threshold}%</div>
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>{rec.thresholds_reason}</div>
                                </div>
                            </div>
                            {rec.policies?.length > 0 && (
                                <div style={{ marginBottom: 10 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#1b2a4a', marginBottom: 5 }}>RECOMMENDED POLICIES</div>
                                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#3d4f6f' }}>
                                        {rec.policies.map((p, i) => <li key={i} style={{ marginBottom: 3 }}>{p}</li>)}
                                    </ul>
                                </div>
                            )}
                            {rec.gaps_to_flag?.length > 0 && (
                                <div style={{ padding: 10, background: '#fff5f0', border: '1px solid #ffd0b8', borderRadius: 6 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', marginBottom: 5 }}>FLAGGED GAPS</div>
                                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#3d4f6f' }}>
                                        {rec.gaps_to_flag.map((g, i) => <li key={i} style={{ marginBottom: 3 }}>{g}</li>)}
                                    </ul>
                                </div>
                            )}
                            {rec.note && <div style={{ fontSize: 9, color: '#7a869a', marginTop: 8, fontStyle: 'italic' }}>{rec.note}</div>}

                            {/* Action row: Apply / Customise / status */}
                            {!editing && (
                                <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
                                    <button className="ap-btn ap-btn--primary" disabled={applyState.busy}
                                        style={{ background: '#7c3aed' }}
                                        onClick={() => applyBaseline()}>
                                        {applyState.busy ? 'Applying…' : 'Apply Recommendation'}
                                    </button>
                                    <button className="ap-btn ap-btn--ghost" onClick={() => setEditing(true)}>
                                        Customise
                                    </button>
                                    <button className="ap-btn ap-btn--ghost" onClick={onLoadAi}>
                                        Re-analyse
                                    </button>
                                    {applyState.msg && (
                                        <span style={{ fontSize: 11, color: applyState.msg.toLowerCase().includes('fail') ? '#dc2626' : '#0f7b4f', fontWeight: 600, marginLeft: 'auto' }}>
                                            {applyState.msg}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Inline customise form, pre-filled from the recommendation */}
                            {editing && editForm && (
                                <div style={{ marginTop: 14, padding: 14, border: '1px dashed #7c3aed', borderRadius: 6, background: '#fbf9ff' }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', marginBottom: 10 }}>
                                        CUSTOMISE BEFORE APPLYING
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                                        <div>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Match Field</label>
                                            <select className="ap-input" value={editForm.match_field}
                                                onChange={e => setEditForm({ ...editForm, match_field: e.target.value })}>
                                                <option value="hostname">Hostname</option>
                                                <option value="serial_number">Serial Number</option>
                                                <option value="mac_address">MAC Address</option>
                                                <option value="ip_address">IP Address</option>
                                                <option value="imei">IMEI</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>In-scope OS Types</label>
                                            <input className="ap-input" value={editForm.os_types}
                                                placeholder="windows, linux, macos"
                                                onChange={e => setEditForm({ ...editForm, os_types: e.target.value })} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Compliance Threshold (%)</label>
                                            <input className="ap-input" type="number" min="0" max="100" value={editForm.compliance_threshold}
                                                onChange={e => setEditForm({ ...editForm, compliance_threshold: parseInt(e.target.value) || 0 })} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Critical Threshold (%)</label>
                                            <input className="ap-input" type="number" min="0" max="100" value={editForm.critical_threshold}
                                                onChange={e => setEditForm({ ...editForm, critical_threshold: parseInt(e.target.value) || 0 })} />
                                        </div>
                                        <div style={{ gridColumn: '1 / span 2' }}>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Licences Purchased (optional)</label>
                                            <input className="ap-input" type="number" min="0" value={editForm.licenses_purchased}
                                                placeholder="e.g. 1000"
                                                onChange={e => setEditForm({ ...editForm, licenses_purchased: e.target.value })} />
                                            <div style={{ fontSize: 9, color: '#7a869a', marginTop: 3 }}>Used to compute the License Utilisation tile in the Tool Usage section.</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <button className="ap-btn ap-btn--primary" disabled={applyState.busy}
                                            style={{ background: '#7c3aed' }}
                                            onClick={() => {
                                                const os_list = editForm.os_types.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                                                const payload = {
                                                    match_field: editForm.match_field,
                                                    compliance_threshold: editForm.compliance_threshold,
                                                    critical_threshold: editForm.critical_threshold,
                                                    scope_filters: { os_types: os_list },
                                                };
                                                const lic = parseInt(editForm.licenses_purchased);
                                                if (lic > 0) payload.licenses_purchased = lic;
                                                applyBaseline(payload);
                                            }}>
                                            {applyState.busy ? 'Applying…' : 'Apply Customised'}
                                        </button>
                                        <button className="ap-btn ap-btn--ghost" onClick={() => setEditing(false)}>
                                            Cancel
                                        </button>
                                        {applyState.msg && (
                                            <span style={{ fontSize: 11, color: applyState.msg.toLowerCase().includes('fail') ? '#dc2626' : '#0f7b4f', fontWeight: 600, marginLeft: 'auto' }}>
                                                {applyState.msg}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
                {aiRec?.error && <div style={{ fontSize: 11, color: '#dc2626' }}>Error: {aiRec.error}</div>}
            </Section>

            {/* 2. Tool Usage (Phase 1) */}
            <Section id="usage" title="Tool Usage">
                {!usageData ? (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Loading usage metrics…</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        {[
                            { label: 'Agents Reporting', value: usageData.agents_reporting, sub: 'last 7 days', color: '#0f7b4f' },
                            { label: 'Stale Agents', value: usageData.stale_agents, sub: 'no check-in 30d+', color: usageData.stale_agents > 0 ? '#d97706' : '#7a869a' },
                            { label: 'License Utilisation', value: usageData.license_utilisation_pct != null ? `${usageData.license_utilisation_pct}%` : '—', sub: usageData.licenses_purchased ? `${usageData.devices_seen} of ${usageData.licenses_purchased}` : 'set licence count', color: '#1b2a4a' },
                            { label: 'Last Sync', value: usageData.last_sync_age || '—', sub: usageData.last_sync_status || 'unknown', color: '#1b2a4a' },
                        ].map((m, i) => (
                            <div key={i} style={{ padding: 10, background: '#fafbfc', border: '1px solid #d8dce3', borderRadius: 6 }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a' }}>{m.label}</div>
                                <div style={{ fontSize: 9, color: '#7a869a' }}>{m.sub}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* 3. Baseline Drift (Phase 4) */}
            <Section id="drift" title="Baseline Drift">
                {!driftData ? (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Loading drift history…</div>
                ) : driftData.points?.length < 2 ? (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Not enough sync history yet — drift trend appears after a few syncs.</div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Sparkline values={driftData.points.map(p => p.score)} width={200} height={40} />
                        <div>
                            <div style={{ fontSize: 9, color: '#7a869a' }}>{driftData.points.length} syncs over {driftData.span_days} days</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: driftData.drift_pct >= 0 ? '#0f7b4f' : '#dc2626' }}>
                                {driftData.drift_pct >= 0 ? '▲' : '▼'} {Math.abs(driftData.drift_pct)}% vs first sync
                            </div>
                        </div>
                    </div>
                )}
            </Section>

            {/* 4. Baseline Policies (Phase 3 - demo grade, derived from config) */}
            <Section id="policies" title="Baseline Policies">
                {!policiesData ? (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Loading policies…</div>
                ) : !policiesData.policies?.length ? (
                    <div style={{ fontSize: 11, color: '#7a869a' }}>No policies configured. Use NOVA AI Recommendation above to generate them.</div>
                ) : (
                    <div>
                        {policiesData.policies.map((p, i) => (
                            <div key={i} style={{ padding: 10, marginBottom: 8, border: '1px solid #d8dce3', borderRadius: 6, background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                    <div style={{ fontSize: 11, color: '#1b2a4a', flex: 1 }}>{p.statement}</div>
                                    <span style={{
                                        fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
                                        color: p.status === 'pass' ? '#0f7b4f' : p.status === 'warn' ? '#d97706' : '#dc2626',
                                        background: (p.status === 'pass' ? '#0f7b4f' : p.status === 'warn' ? '#d97706' : '#dc2626') + '15'
                                    }}>
                                        {p.status?.toUpperCase()} • {p.current_value}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </>
    );
}

// D3 Donut Chart - shows compliance breakdown
const DonutChart = _DonutChart;
const HBarChart = _HBarChart;
const GaugeChart = _GaugeChart;

/**
 * Translate the raw `last_sync_error` string from a vendor adapter
 * into a single plain-language sentence suitable for non-technical
 * reviewers. The full technical text is preserved (the caller
 * decides whether to render it behind a "Show details" toggle).
 *
 * Pattern-matches common vendor-rejection shapes:
 *   - HTTP status codes  → "Vendor rejected the request"
 *   - "Token is invalid" → "Sign-in expired"
 *   - "Forbidden"        → "Permission missing in vendor console"
 *   - "Connection refused" / timeout → "Could not reach vendor"
 *   - "AADSTS65001"      → "Admin consent missing in Azure"
 *   - "trace-id="        → "Vendor rejected the credentials"
 *
 * Falls back to the first 80 chars when no pattern matches — better
 * than dumping a full Python stack trace into the dashboard.
 */
function humaniseSyncError(raw) {
    if (!raw) return '';
    const text = String(raw);
    const low = text.toLowerCase();
    if (low.includes('admin consent') || low.includes('aadsts65001')) {
        return 'Permissions added but admin consent not granted in Azure AD.';
    }
    if (low.includes('aadsts7000215') || low.includes('invalid_client') || low.includes('client authentication failed')) {
        return 'Vendor rejected the credentials. Check the Client ID / Secret.';
    }
    if (low.includes('aadsts7000222') || low.includes('client secret has expired') || low.includes('secret has expired')) {
        return 'Client Secret has expired. Generate a new one in the vendor console.';
    }
    if (low.includes('token is invalid') || low.includes('token has expired') || low.includes('revoked')) {
        return 'Sign-in expired. Open the integration and click Test Connection.';
    }
    if (low.includes('insufficient privileges') || low.includes('authorization_requestdenied')
        || low.includes('access denied') || low.includes('forbidden') || low.match(/\b403\b/)) {
        return 'Permission missing in the vendor console.';
    }
    if (low.includes('not authorized') || low.includes('unauthorized') || low.match(/\b401\b/)) {
        return 'Vendor rejected the credentials.';
    }
    if (low.includes('connection refused') || low.includes('timed out') || low.includes('timeout')
        || low.includes('cannot connect') || low.includes('unreachable') || low.includes('name resolution')) {
        return 'Could not reach the vendor — check the URL and network.';
    }
    if (low.includes('rate limit') || low.includes('too many requests') || low.match(/\b429\b/)) {
        return 'Vendor is rate-limiting us. Try again in a few minutes.';
    }
    if (low.includes('trace-id') || low.includes('please provide trace-id')) {
        return 'Vendor rejected the request.';
    }
    if (low.match(/\b5\d\d\b/) || low.includes('server error')) {
        return 'Vendor service is having a problem. Try again later.';
    }
    // Fallback — first 80 chars + ellipsis. No stack traces, no
    // "AttributeError: 'NoneType' object has no attribute 'get'".
    const trimmed = text.replace(/\s+/g, ' ').trim();
    return trimmed.length > 80 ? trimmed.slice(0, 80) + '…' : trimmed;
}

/** Format a timestamp into a relative + absolute hybrid: "5 min ago"
 * for very recent, "2 May 14:23" for older. Returns "—" for null. */
function fmtRelativeTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
        if (diffMin < 1) return 'just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)} h ago`;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch (e) { return '—'; }
}

/**
 * Last-connection-check cell. Shows three things:
 *   - Status pill (green ✓ / red ✗ / grey "Never" / yellow "Pending")
 *   - Plain-language one-liner ("Verified 2 min ago" / "Failed 1 h ago: Sign-in expired")
 *   - "Show details" toggle that reveals the raw technical error text
 *     for ops/support — collapsed by default to keep the table clean.
 */
function KriLastCheckCell({ tool }) {
    const [showDetails, setShowDetails] = useState(false);
    const t = tool;
    const isCustom = t.vendor === 'custom';
    const status = t.last_sync_status;
    const ts = t.last_sync_at;
    const neverChecked = !ts && !t.is_verified;

    let pill = { label: 'Never', class: 'ap-badge--ghost' };
    let line = '';
    let detail = '';

    if (status === 'pending') {
        // A pending status means an upload/sync was submitted and is being
        // verified — this can be true before the first timestamp lands
        // (last_sync_at is still null). Check it before `neverChecked` so a
        // just-uploaded custom tool shows progress, not "No upload yet".
        pill = { label: '⋯ Checking', class: 'ap-badge--warning' };
        line = isCustom ? 'Uploading in progress' : 'Sync in progress…';
    } else if (neverChecked) {
        pill = { label: 'Never', class: 'ap-badge--ghost' };
        line = isCustom
            ? 'No upload yet — click Upload to start tracking.'
            : 'Connection has not been tested yet.';
    } else if (status === 'success' || (t.is_verified && !status)) {
        pill = { label: '✓ Verified', class: 'ap-badge--success' };
        line = `Verified ${fmtRelativeTime(ts)}`;
    } else if (status === 'error') {
        pill = { label: '✗ Failed', class: 'ap-badge--danger' };
        line = `Failed ${fmtRelativeTime(ts)}: ${humaniseSyncError(t.last_sync_error)}`;
        detail = t.last_sync_error || '';
    } else if (t.is_verified) {
        pill = { label: '✓ Verified', class: 'ap-badge--success' };
        line = ts ? `Verified ${fmtRelativeTime(ts)}` : 'Previously verified.';
    }

    // Cap the inline message at one line. Long error messages
    // (vendor tracebacks, the asset-mapping-rules ProgrammingError
    // we surface verbatim) used to wrap and push the entire
    // Actions column off-screen on the right. Truncating the
    // inline view + always offering a "Show details" expand
    // for any failed run keeps the table dense.
    return (
        <div style={{ maxWidth: 360 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'nowrap' }}>
                <span className={`ap-badge ${pill.class}`} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{pill.label}</span>
                <span
                    title={line}
                    style={{
                        color: '#3d4f6f',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: '1 1 auto',
                    }}>
                    {line}
                </span>
            </div>
            {detail && (
                <div style={{ marginTop: 4 }}>
                    <button
                        type="button"
                        onClick={() => setShowDetails(s => !s)}
                        style={{
                            background: 'none', border: 'none', color: '#7a869a',
                            fontSize: 10, cursor: 'pointer', padding: 0,
                            textDecoration: 'underline'
                        }}>
                        {showDetails ? 'Hide details' : 'Show details'}
                    </button>
                    {showDetails && (
                        <pre style={{
                            background: '#f9fafb', border: '1px solid #e6e9ee',
                            borderRadius: 4, padding: 6, fontSize: 10, marginTop: 4,
                            maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word', color: '#4b5b7a'
                        }}>
                            {detail}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * KRI Matrix -Tool Compliance Monitoring
 * Sub-tabs: Dashboard, Tool Integrations, Asset Baseline, Coverage Matrix, Gap Report
 */
export default function KRIMatrix({ flash }) {
    // Centred confirmation dialog — replaces window.confirm everywhere
    // in this module so KRI matches the rest of the audit portal's
    // confirmation UX (centred modal, focus trap, ESC, danger variant).
    const { confirm: confirmDialog, dialog: confirmDialogEl } = useConfirm();
    // Persist the active KRI Matrix tab so refresh keeps the user on the same view.
    // Always start on Dashboard when the user lands on KRI Matrix from the
    // left sidebar (or hard-reloads the page). The previous behaviour
    // restored the last-visited tab from localStorage, but that surprised
    // users who clicked "KRI Matrix" expecting the overview and instead
    // landed on Settings / Mapping Rules. Per-mount reset matches the
    // mental model of "open the page" = "see the dashboard".
    const [tab, setTab] = useState('dashboard');
    // Scrub the legacy persisted tab so a stale value doesn't haunt anyone
    // who set it under the old behaviour and never re-visited.
    useEffect(() => { try { localStorage.removeItem('kri_matrix_tab'); } catch (e) { } }, []);
    const [loading, setLoading] = useState(true);

    // Scope filter (BU / Project / Region) was removed from the page
    // header on 2026-05-06 because vendor adapters can't populate the
    // underlying fields on imported assets — see the JSX comment near
    // the page header. `scope` is now a constant {} and `scopeParams`
    // returns an empty params object, leaving every loader to query
    // the full org scope.
    //
    // localStorage 'kri_scope' is scrubbed on first mount after this
    // change so a previously-persisted scope (set before the dropdowns
    // were removed) doesn't continue to silently filter the dashboard
    // with no UI to clear it.
    const scope = {};
    const scopeParams = () => ({});
    useEffect(() => {
        try { localStorage.removeItem('kri_scope'); } catch (e) { }
    }, []);
    const [scopeOptions, setScopeOptions] = useState({ business_units: [], projects: [], regions: [] });
    // Risk-create modal state for Gap Report
    const [riskModalOpen, setRiskModalOpen] = useState(false);
    const [riskForm, setRiskForm] = useState(null);
    const [riskSaving, setRiskSaving] = useState(false);

    // Dashboard. Hydrate synchronously from localStorage so a re-visit paints
    // content on first render instead of showing the skeleton for 1-5 sec
    // while the network round-trip lands. Stale-while-revalidate: loadDashboard
    // still fires on mount and replaces the cached payload with fresh data.
    // Cache key is per-org so a tenant switch doesn't show the previous tenant's
    // numbers; we lean on the 'access_token' presence as a coarse "is logged in"
    // gate — the cache reads are best-effort and tolerant of any failure.
    // Bumped to v2 (2026-05-07) to invalidate caches written before §50 —
    // the dashboard payload shape changed (added critical_asset_health,
    // bumped sync_frequency_hours default, etc.) and stale v1 entries
    // were rendering banners that no longer matched DB state. Bumping
    // the key forces every browser to re-hydrate from a fresh fetch.
    const KRI_DASH_CACHE_KEY = 'kri_dashboard_cache_v2';
    const KRI_DASH_CACHE_KEY_LEGACY = 'kri_dashboard_cache_v1';
    const [dashData, setDashData] = useState(() => {
        // Read v2 first; fall back to v1 if v2 is empty so users who upgraded
        // mid-session still get instant paint instead of a cold-load skeleton.
        // The fresh fetch on mount overwrites under v2, so v1 self-heals on
        // the next setDashData. Drop the v1 entry once v2 is populated.
        const readKey = (key) => {
            try {
                const raw = localStorage.getItem(key);
                if (!raw) return null;
                const parsed = JSON.parse(raw);
                if (parsed?._cached_at && Date.now() - parsed._cached_at > 24 * 60 * 60 * 1000) return null;
                return parsed?.data || null;
            } catch (e) { return null; }
        };
        const fromV2 = readKey(KRI_DASH_CACHE_KEY);
        if (fromV2) return fromV2;
        const fromV1 = readKey(KRI_DASH_CACHE_KEY_LEGACY);
        if (fromV1) {
            // Once we read v1, scrub it — the next setDashData writes v2 fresh.
            try { localStorage.removeItem(KRI_DASH_CACHE_KEY_LEGACY); } catch (e) { }
        }
        return fromV1;
    });
    const [selectedToolId, setSelectedToolId] = useState('all');
    // Per-tool tiles are operational detail — hidden by default on the
    // executive dashboard. CISOs/board viewers see only the rolled-up
    // metrics; operators expand this when they need per-tool drill-down.
    const [toolsExpanded, setToolsExpanded] = useState(false);

    // Tools
    const [tools, setTools] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [showToolModal, setShowToolModal] = useState(false);
    const [toolForm, setToolForm] = useState({});
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [baselineStep, setBaselineStep] = useState(1); // 1=vendor select, 2=credentials, 3=preview & fields, 4=baseline config
    const [testResult, setTestResult] = useState(null);
    const [syncing, setSyncing] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [aiRec, setAiRec] = useState(null);          // NOVA AI baseline recommendation per tool
    const [aiLoading, setAiLoading] = useState(false);
    const [aiToolId, setAiToolId] = useState(null);
    const [usageData, setUsageData] = useState(null);  // Tool usage metrics (Phase 1)
    const [driftData, setDriftData] = useState(null);  // Time-series drift (Phase 4)
    const [policiesData, setPoliciesData] = useState(null);  // Baseline policies (Phase 3)
    const [discoverInput, setDiscoverInput] = useState('');     // NOVA AI tool-discovery wizard
    const [discoverResult, setDiscoverResult] = useState(null);
    const [discoverLoading, setDiscoverLoading] = useState(false);
    const [diagnose, setDiagnose] = useState(null);          // NOVA AI diagnose-connection result
    const [diagnoseLoading, setDiagnoseLoading] = useState(false);
    const [autoConfig, setAutoConfig] = useState(null);      // NOVA AI auto-config from preview (Step 4 summary card)
    const [autoConfigLoading, setAutoConfigLoading] = useState(false);
    const [showCustomise, setShowCustomise] = useState(false); // toggle to reveal detailed config form

    // Edit Tool — separate, simpler modal than the Add wizard. Surfaces the
    // runtime knobs operators actually retune post-onboarding (record cap,
    // threshold, sync frequency) without dragging them through vendor select
    // and credentials again. Cap-hit recovery (the 100k OOM guard) is the
    // canonical use case: sync log shows "In Tool: 100,000" exactly → operator
    // bumps Records per Sync here without touching the API.
    const [editTool, setEditTool] = useState(null);
    const [editToolForm, setEditToolForm] = useState({});
    const [editToolSaving, setEditToolSaving] = useState(false);

    // Functional-setter helpers for the wizard's three forms. These eliminate
    // the stale-closure bug where rapid focus changes / fast typing could drop
    // field updates because two setStates in the same batch both spread the
    // same captured state. ALWAYS use these instead of updateToolForm({ ... }).
    const updateToolForm = (patch) => setToolForm(prev => ({ ...prev, ...patch }));
    const updateConnectorForm = (patch) => setConnectorForm(prev => ({ ...prev, ...patch }));
    const updateAssetForm = (patch) => setAssetForm(prev => ({ ...prev, ...patch }));

    // When the customer arrives at Step 4 with a non-custom vendor and we have
    // sample devices from the preview step, auto-trigger NOVA AI's
    // configure-from-preview so the customer sees a "Here's what we'll monitor"
    // summary instead of a blank configuration form.
    useEffect(() => {
        const isCustom = selectedVendor?.id === 'custom';
        const hasSample = Array.isArray(previewData?.sample_devices) && previewData.sample_devices.length > 0;
        if (baselineStep === 4 && !isCustom && hasSample && !autoConfig && !autoConfigLoading) {
            setAutoConfigLoading(true);
            (async () => {
                try {
                    const { kri_ai_auto_configure_from_preview } = await import('../services');
                    const r = await kri_ai_auto_configure_from_preview({
                        vendor: selectedVendor?.id || toolForm.vendor || '',
                        tool_category: selectedVendor?.tool_category || toolForm.tool_type || 'custom',
                        sample_devices: previewData.sample_devices,
                        available_fields: (previewData.available_fields || []).map(f => f.field || f),
                        baseline_size: previewData?.baseline_size || 0,
                    });
                    const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                    if (d) {
                        setAutoConfig(d);
                        // Pre-fill the form with NOVA AI's choices so the underlying
                        // config object is correct whether or not the customer customises.
                        if (d.config) {
                            setToolForm(prev => ({
                                ...prev,
                                compliance_threshold: d.config.compliance_threshold ?? prev.compliance_threshold,
                                critical_threshold: d.config.critical_threshold ?? prev.critical_threshold,
                                baseline_asset_field: d.config.match_field || prev.baseline_asset_field,
                                baseline_tool_field: d.config.match_field || prev.baseline_tool_field,
                                sync_frequency_hours: d.config.sync_frequency_hours ?? prev.sync_frequency_hours,
                                baseline_filters: { ...(prev.baseline_filters || {}), os_type: d.config.scope_filters?.os_types || [] },
                                additional_config: {
                                    ...(prev.additional_config || {}),
                                    sync_mode: d.config.sync_mode || 'incremental',
                                    health_config: d.config.health_config || (prev.additional_config?.health_config),
                                },
                            }));
                        }
                    }
                } catch (e) { /* fall through to manual form */ }
                setAutoConfigLoading(false);
            })();
        }
        // Reset when leaving Step 4 or starting over
        if (baselineStep < 4 && (autoConfig || showCustomise)) {
            setAutoConfig(null);
            setShowCustomise(false);
        }
    }, [baselineStep, selectedVendor, previewData]); // eslint-disable-line react-hooks/exhaustive-deps
    const [previewLoading, setPreviewLoading] = useState(false);
    const [toolCreating, setToolCreating] = useState(false);
    const [previewTaskId, setPreviewTaskId] = useState(null);
    const [previewError, setPreviewError] = useState('');
    const [selectedFields, setSelectedFields] = useState([]);

    // Universal connector builder
    const [connectorForm, setConnectorForm] = useState({ base_url: '', endpoint: '', auth_type: 'none', username: '', password: '', api_key: '', token: '', oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: '', oauth2_scope: '' });
    const [discoveredData, setDiscoveredData] = useState(null);
    const [fieldMapping, setFieldMapping] = useState({});
    const [aiMapping, setAiMapping] = useState(false);

    // Assets
    const [assets, setAssets] = useState([]);
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [assetForm, setAssetForm] = useState({ hostname: '', ip_address: '', os_type: 'windows', asset_type: 'endpoint', owner: '', department: '', criticality: 3 });
    const [assetSearch, setAssetSearch] = useState('');
    const [assetPage, setAssetPage] = useState(1);
    const [assetTotal, setAssetTotal] = useState(0);
    const ASSET_PAGE_SIZE = 100;
    // tabRefreshing: shows skeleton/dim during async load triggered by scope/filter change.
    // Distinct from `loading` (initial dashboard mount) — keep them separate.
    const [tabRefreshing, setTabRefreshing] = useState(false);

    // Matrix
    const [matrixData, setMatrixData] = useState(null);
    const [matrixFilter, setMatrixFilter] = useState('');
    const [matrixPage, setMatrixPage] = useState(1);
    const MATRIX_PAGE_SIZE = 100;
    const [matrixSearch, setMatrixSearch] = useState('');

    // Gaps — backend returns { asset_gaps: [...], ticket_gaps: [...],
    // total_asset_gaps, total_ticket_gaps, asset_page, asset_page_size,
    // ticket_page, ticket_page_size }. Two independent paginations
    // because asset gaps and ticket gaps render in separate tables.
    const [gaps, setGaps] = useState({ asset_gaps: [], ticket_gaps: [] });
    const [gapToolFilter, setGapToolFilter] = useState('');
    const [assetGapPage, setAssetGapPage] = useState(1);
    const [ticketGapPage, setTicketGapPage] = useState(1);
    const GAP_PAGE_SIZE = 100;

    // Asset → BU/Project/Location mapping rules. The form holds either a
    // create draft (no `id`) or an edit draft (has `id`); null = closed.
    const [mappingRules, setMappingRules] = useState([]);
    const [ruleForm, setRuleForm] = useState(null);
    const [ruleSaving, setRuleSaving] = useState(false);

    // Per-org KRI settings (today: NOVA AI on/off + audit trail when off).
    // null until first load. Form-mode shadows the saved state so a half-
    // typed disable reason doesn't get committed until the user clicks Save.
    const [orgSettings, setOrgSettings] = useState(null);
    const [orgSettingsForm, setOrgSettingsForm] = useState(null);
    const [orgSettingsSaving, setOrgSettingsSaving] = useState(false);

    // Sync Logs
    const [syncLogs, setSyncLogs] = useState([]);

    // Devices that the integrated tool reports but are NOT in the
    // baseline. Map of tool_id → {count, samples, computed_at}. The
    // backend persists this on every sync; we just fetch + render.
    const [unmatched, setUnmatched] = useState({});
    const [unmatchedLoading, setUnmatchedLoading] = useState(false);

    // KRI Definition catalogue (system-of-record). NMT owns the named
    // KRI list separately from the live tool sync; the catalogue stores
    // thresholds, owners, and framework mapping that survive a vendor
    // swap. The live `current_status` is filled in by the dashboard
    // computation when at least one tool is linked.
    const [definitions, setDefinitions] = useState([]);
    const [definitionsLoading, setDefinitionsLoading] = useState(false);
    const [definitionSearch, setDefinitionSearch] = useState('');
    const [definitionCategory, setDefinitionCategory] = useState('');
    const [showInactiveDefinitions, setShowInactiveDefinitions] = useState(false);
    const [showDefinitionModal, setShowDefinitionModal] = useState(false);
    const [definitionForm, setDefinitionForm] = useState(null);   // null = closed
    const [definitionSaving, setDefinitionSaving] = useState(false);
    const [importStage, setImportStage] = useState(null);         // null | 'preview' | 'committing'
    const [importPreview, setImportPreview] = useState(null);     // parsed payload from /import/preview
    const [importFile, setImportFile] = useState(null);
    const [importError, setImportError] = useState('');

    // NOVA AI insight panels — lazy-loaded, one set per tab so we don't burn calls on every render.
    const [aiDashSummary, setAiDashSummary] = useState(null);
    const [aiDashLoading, setAiDashLoading] = useState(false);
    const [aiCoverage, setAiCoverage] = useState(null);
    const [aiCoverageLoading, setAiCoverageLoading] = useState(false);
    const [aiGapAnalysis, setAiGapAnalysis] = useState(null);
    const [aiGapLoading, setAiGapLoading] = useState(false);
    const [aiBaselineAudit, setAiBaselineAudit] = useState(null);
    const [aiBaselineLoading, setAiBaselineLoading] = useState(false);
    const [bulkSelected, setBulkSelected] = useState(new Set());

    // NOVA AI "Why these numbers?" panel — field-detection summary for the
    // most recent CSV upload. Helps customers self-diagnose missing data
    // (e.g. all-No reopen column) instead of assuming the platform is broken.
    const [explainData, setExplainData] = useState(null);
    const [explainLoading, setExplainLoading] = useState(false);
    const [explainOpen, setExplainOpen] = useState(false);
    const [explainToolId, setExplainToolId] = useState(null);

    const openExplainUpload = async (tool) => {
        setExplainOpen(true);
        setExplainToolId(tool.id);
        setExplainLoading(true);
        setExplainData(null);
        try {
            const r = await svc('kri_ai_explain_upload', tool.id);
            const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
            setExplainData(d || { error: 'No data returned' });
        } catch (e) {
            setExplainData({ error: e?.message || 'Failed to load explanation' });
        }
        setExplainLoading(false);
    };
    const closeExplainUpload = () => { setExplainOpen(false); setExplainData(null); };

    // Pre-flight upload checklist — what customers see BEFORE they export.
    // Counterpart to the explain-upload diagnostic which runs AFTER.
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [checklistData, setChecklistData] = useState(null);
    const [checklistLoading, setChecklistLoading] = useState(false);
    const [checklistToolId, setChecklistToolId] = useState(null);

    const openUploadChecklist = async (tool) => {
        setChecklistOpen(true);
        setChecklistToolId(tool.id);
        setChecklistLoading(true);
        setChecklistData(null);
        try {
            const r = await svc('kri_get_upload_checklist', tool.id);
            const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
            setChecklistData(d || { error: 'No checklist returned' });
        } catch (e) {
            setChecklistData({ error: e?.message || 'Failed to load checklist' });
        }
        setChecklistLoading(false);
    };
    const closeUploadChecklist = () => { setChecklistOpen(false); setChecklistData(null); };

    const downloadSampleCsv = () => {
        if (!checklistData?.sample_csv) return;
        const blob = new Blob([checklistData.sample_csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kri_${checklistData.category || 'tool'}_sample.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
    };

    // Per-tool dashboard label override editor.
    // labelEditorToolId: which tool's editor is open. labelDraft: in-progress edits.
    // labelSuggestion: NOVA AI suggestion fetched from /tools/{id}/ai-suggest-dashboard-labels/.
    const [labelEditorToolId, setLabelEditorToolId] = useState(null);
    const [labelDraft, setLabelDraft] = useState({ score: '', installed: '', healthy: '', unhealthy: '', missing: '' });
    const [labelSuggestion, setLabelSuggestion] = useState(null);
    // Bucket-glossary modal: opens when the operator clicks the (i)
    // info button next to a tool tile's drilldown labels. Shows the
    // 4-bucket universal definitions mapped to this tool's category-
    // specific labels (e.g. for MDM: Installed=Enrolled,
    // Healthy=Compliant State, etc.).
    const [glossaryToolId, setGlossaryToolId] = useState(null);
    // Tool audit-log modal — surfaces who-changed-what-when for a single tool.
    // null = closed; tool id = open + showing that tool's history.
    const [auditLogToolId, setAuditLogToolId] = useState(null);
    const [auditLogEntries, setAuditLogEntries] = useState([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    // Dashboard-level glossary explains the universal Healthy / Unhealthy /
    // Missing buckets on the BY DISTINCT ASSET donut. Separate from the
    // per-tool glossary because the dashboard rollup uses worst-state-wins
    // across tools — the meanings don't depend on a single tool's labels.
    const [dashGlossaryOpen, setDashGlossaryOpen] = useState(false);
    const [labelSaving, setLabelSaving] = useState(false);

    const openLabelEditor = (tool) => {
        const current = (tool.dashboard_labels || {});
        setLabelEditorToolId(tool.id);
        setLabelDraft({
            score: current.score || '', installed: current.installed || '',
            healthy: current.healthy || '', unhealthy: current.unhealthy || '',
            missing: current.missing || '',
        });
        setLabelSuggestion(null);
    };
    const closeLabelEditor = () => { setLabelEditorToolId(null); setLabelSuggestion(null); };
    const requestLabelSuggestion = async (toolId) => {
        try {
            const r = await svc('kri_ai_suggest_dashboard_labels', toolId);
            const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
            setLabelSuggestion(d || { error: 'Could not generate' });
        } catch (e) { setLabelSuggestion({ error: e?.message || 'Could not generate' }); }
    };
    const applyLabelSuggestionToDraft = () => {
        if (!labelSuggestion?.suggested_labels) return;
        setLabelDraft(prev => ({ ...prev, ...labelSuggestion.suggested_labels }));
    };
    const saveLabelOverride = async (tool) => {
        // Build the override map: only include non-empty fields so the merge logic
        // on the backend cleanly falls back to category defaults for blanks.
        const override = {};
        for (const k of ['score', 'installed', 'healthy', 'unhealthy', 'missing']) {
            const v = (labelDraft[k] || '').trim();
            if (v) override[k] = v.slice(0, 30);
        }
        const merged_config = { ...(tool.additional_config || {}), dashboard_labels: override };
        // Empty override → drop the key entirely so the tool reverts to category defaults.
        if (Object.keys(override).length === 0) delete merged_config.dashboard_labels;
        setLabelSaving(true);
        try {
            await svc('kri_update_tool', tool.id, { additional_config: merged_config });
            flash('Dashboard labels updated.');
            closeLabelEditor();
            await loadDashboard({ silent: true });
        } catch (e) { flash('Failed to save labels: ' + (e?.message || 'unknown error')); }
        setLabelSaving(false);
    };
    // NOVA AI URL suggestion popover (shown next to API Base URL field)
    const [urlSuggestion, setUrlSuggestion] = useState(null);  // { suggestions: [...], confidence }
    const [urlSuggestionLoading, setUrlSuggestionLoading] = useState(false);
    const [urlSuggestionOpen, setUrlSuggestionOpen] = useState(false);

    const runAiDashSummary = async () => {
        setAiDashLoading(true);
        try {
            const r = await svc('kri_ai_summarise_dashboard');
            setAiDashSummary(r?.statusCode?.response_data?.data || r?.response_data?.data || null);
        } catch (e) { setAiDashSummary({ error: 'Could not generate summary.' }); }
        setAiDashLoading(false);
    };
    const runAiCoverage = async () => {
        setAiCoverageLoading(true);
        try {
            const r = await svc('kri_ai_analyze_coverage');
            setAiCoverage(r?.statusCode?.response_data?.data || r?.response_data?.data || null);
        } catch (e) { setAiCoverage({ error: 'Could not generate insights.' }); }
        setAiCoverageLoading(false);
    };
    const runAiGapAnalysis = async () => {
        setAiGapLoading(true);
        try {
            const r = await svc('kri_ai_analyze_gaps', gapToolFilter ? { tool_id: gapToolFilter } : {});
            setAiGapAnalysis(r?.statusCode?.response_data?.data || r?.response_data?.data || null);
        } catch (e) { setAiGapAnalysis({ error: 'Could not generate analysis.' }); }
        setAiGapLoading(false);
    };
    const runAiBaselineAudit = async () => {
        setAiBaselineLoading(true);
        try {
            const r = await svc('kri_ai_audit_baseline');
            setAiBaselineAudit(r?.statusCode?.response_data?.data || r?.response_data?.data || null);
        } catch (e) { setAiBaselineAudit({ error: 'Could not run audit.' }); }
        setAiBaselineLoading(false);
    };

    const runUrlSuggestion = async () => {
        setUrlSuggestionLoading(true);
        setUrlSuggestionOpen(true);
        try {
            const vendor = (selectedVendor?.id || toolForm?.vendor || '').toLowerCase();
            const description = toolForm?.name || selectedVendor?.name || '';
            const r = await svc('kri_ai_suggest_api_url', { vendor, description });
            setUrlSuggestion(r?.statusCode?.response_data?.data || r?.response_data?.data || null);
        } catch (e) { setUrlSuggestion({ suggestions: [], error: 'Could not load suggestions.' }); }
        setUrlSuggestionLoading(false);
    };

    const applyUrlSuggestion = (url) => {
        // Resolve any <placeholder> tokens inline so the user can see them in the field.
        updateToolForm({ api_base_url: url });
        setUrlSuggestionOpen(false);
    };

    // Map an asset criticality (1..4) to a risk impact score (2..5).
    const criticalityToImpact = (c) => ({ 1: 2, 2: 3, 3: 4, 4: 5 }[c] || 3);
    const isEmail = (s) => typeof s === 'string' && /\S+@\S+\.\S+/.test(s);

    const openRiskFromAssetGap = (g) => {
        setRiskForm({
            kind: 'asset_gap',
            source_id: g.id,
            risk: `${g.hostname} is non-compliant in ${g.tool_name} (${(g.tool_category || 'control')}).`,
            threat: `Coverage gap for ${g.tool_name} — asset ${g.hostname} is not reporting.`,
            vulnerability: `${g.os_type || 'Asset'} not protected by ${g.tool_name}; potential blind spot in detection or compliance.`,
            risk_owner: isEmail(g.owner) ? g.owner : '',
            controls_applied: '',
            residual_risk: '',
            accepted_by: '',
            transferred_to: '',
            treatment_option: 'mitigate',
            treatment_plan: `Deploy or re-enrol ${g.tool_name} agent on ${g.hostname}, then verify next sync.`,
            risk_type: 'technical',
            state: 'open',
            target_date: '',
            likelihood: 3,
            impact: criticalityToImpact(g.criticality),
            business_unit_id: g.business_unit_id || null,
            project_id: g.project_id || null,
        });
        setRiskModalOpen(true);
    };

    const openRiskFromTicketGap = (g) => {
        const slaWord = (g.sla_state || 'breach').replace(/_/g, ' ');
        setRiskForm({
            kind: 'ticket_gap',
            source_id: g.id,
            risk: `Ticket ${g.ticket_id || g.id} is ${slaWord} in ${g.tool_name}.`,
            threat: `ITSM SLA non-compliance — ${slaWord}.`,
            vulnerability: `Resolution exceeded the SLA target for this ticket priority.`,
            risk_owner: '',
            controls_applied: '',
            residual_risk: '',
            accepted_by: '',
            transferred_to: '',
            treatment_option: 'mitigate',
            treatment_plan: `Reassign or escalate ticket ${g.ticket_id || g.id}; review SLA matrix configuration if recurring.`,
            risk_type: 'operational',
            state: 'open',
            target_date: '',
            likelihood: 3, impact: 3,
            business_unit_id: null, project_id: null,
        });
        setRiskModalOpen(true);
    };

    const submitRisk = async () => {
        if (!riskForm?.risk?.trim()) { flash('Risk description is required.'); return; }
        // Idempotency guard — refresh gaps and check whether this gap already
        // has a Risk #N stamped in remediation_notes. This prevents double-create
        // on network retries / double-clicks even when the modal-side check missed.
        if (riskForm.source_id) {
            try {
                const fresh = await svc('kri_list_gaps');
                const data = fresh?.statusCode?.response_data?.data;
                const all = [...(data?.asset_gaps || []), ...(data?.ticket_gaps || [])];
                const match = all.find(g => g.id === riskForm.source_id);
                const linked = match && (match.remediation_notes || '').match(/Risk #(\d+)/);
                if (linked) {
                    flash(`This gap is already linked to Risk #${linked[1]}.`);
                    setRiskModalOpen(false);
                    return;
                }
            } catch (e) { /* fall through — best-effort guard */ }
        }
        setRiskSaving(true);
        try {
            const params = {
                risk: riskForm.risk.trim(),
                threat: riskForm.threat || '',
                vulnerability: riskForm.vulnerability || '',
                risk_owner: riskForm.risk_owner || '',
                controls_applied: riskForm.controls_applied || '',
                residual_risk: riskForm.residual_risk || '',
                accepted_by: riskForm.accepted_by || '',
                transferred_to: riskForm.transferred_to || '',
                treatment_option: riskForm.treatment_option || 'mitigate',
                treatment_plan: riskForm.treatment_plan || '',
                risk_type: riskForm.risk_type || 'technical',
                state: riskForm.state || 'open',
                target_date: riskForm.target_date || '',
                likelihood: parseInt(riskForm.likelihood) || 3,
                impact: parseInt(riskForm.impact) || 3,
            };
            if (riskForm.business_unit_id) params.business_unit_id = riskForm.business_unit_id;
            if (riskForm.project_id) params.project_id = riskForm.project_id;
            const r = await svc('add_new_risk', params);
            const created = r?.statusCode?.response_data?.data;
            const errMsg = r?.statusCode?.error?.message;
            if (errMsg) { flash(`Risk creation failed: ${errMsg}`); }
            else if (created) {
                // Mark the gap as "in progress" and store the linkage in remediation_notes
                // so the same gap doesn't spawn a second risk by mistake.
                try {
                    await svc('kri_mark_gap_remediated', riskForm.source_id, {
                        remediation_status: 'in_progress',
                        remediation_notes: `Linked to Risk #${created.id || ''} — moved to Risk Registry.`,
                    });
                } catch (e) { }
                flash('Risk created and added to Risk Registry.');
                setRiskModalOpen(false);
                loadGaps();
            } else {
                flash('Risk creation returned an unexpected response.');
            }
        } catch (e) {
            flash('Could not create risk: ' + (e?.message || 'unknown error'));
        }
        setRiskSaving(false);
    };

    useEffect(() => { loadDashboard(); loadScopeOptions(); }, []);

    // Auto-refresh tools list every 30 sec while ANY tool is in 'running'
    // state. Keeps the sync banner accurate even if the customer is on a
    // tab that isn't tools/dashboard. Stops polling once all syncs
    // complete.
    //
    // Pre-2026-05-06 this fired every 8 sec AND called loadDashboard()
    // alongside loadTools(). loadDashboard fans out to /dashboard/, /gaps/,
    // and /matrix/ in parallel — so each tick was 5 endpoints, ~250KB of
    // JSON, and once a sync was running with a fresh page-load layered on
    // top the worker OOM'd (server stopped silently, no traceback). Now we
    // only refetch /tools/ + /vendors/ here (cheap; the /tools/ endpoint
    // already has ETag → 304 when unchanged), and the per-tool sync poller
    // at line ~1739 still does the full loadDashboard() exactly once when
    // a sync transitions to 'success' — so the dashboard catches up
    // immediately on completion without ever being polled mid-run.
    useEffect(() => {
        const anyRunning = (tools || []).some(t => t.last_sync_status === 'running');
        if (!anyRunning) return;
        const id = setTimeout(() => { loadTools(); }, 30000);
        return () => clearTimeout(id);
    }, [tools]);

    // Poll sync logs every 8 s while the logs tab is active and at least one log is still running.
    useEffect(() => {
        if (tab !== 'logs') return;
        const anyRunning = syncLogs.some(l => l.status === 'running');
        if (!anyRunning) return;
        const id = setTimeout(() => { loadLogs(); }, 8000);
        return () => clearTimeout(id);
    }, [tab, syncLogs]); // eslint-disable-line react-hooks/exhaustive-deps

    // Esc-to-close on every modal. Tries riskModal first (newest), then asset, then tool wizard.
    useEffect(() => {
        const onKey = async (e) => {
            if (e.key !== 'Escape') return;
            if (riskModalOpen) {
                if (!riskSaving) setRiskModalOpen(false);
            } else if (showAssetModal) {
                const hasData = !!(assetForm?.hostname || assetForm?.ip_address || assetForm?.serial_number);
                if (!hasData) { setShowAssetModal(false); return; }
                const ok = await confirmDialog({
                    title: 'Discard changes?',
                    message: 'Close without saving? Your changes will be lost.',
                    confirmLabel: 'Discard', cancelLabel: 'Keep editing', danger: true,
                });
                if (ok) setShowAssetModal(false);
            } else if (showToolModal) {
                const hasData = !!(toolForm?.name || toolForm?.api_base_url || (selectedVendor && baselineStep > 1));
                if (!hasData) { setShowToolModal(false); return; }
                const ok = await confirmDialog({
                    title: 'Close wizard?',
                    message: 'Close the integration wizard? Unsaved progress will be lost.',
                    confirmLabel: 'Close', cancelLabel: 'Keep going', danger: true,
                });
                if (ok) setShowToolModal(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskModalOpen, showAssetModal, showToolModal, riskSaving, baselineStep, selectedVendor]);
    // 2026-05-06: scope dropdowns removed from the UI — see the
    // comment above the page header. The scope-change useEffect that
    // used to re-run every loader on scope change is no longer needed
    // because `scope` is fixed to `{}`. Loaders fire on tab change
    // and on explicit user action only.

    const containerRef = useRef(null);
    const captureScreenshot = async () => {
        if (!containerRef.current) return;
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(containerRef.current, {
                backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false,
            });
            const link = document.createElement('a');
            link.download = `kri-snapshot-${new Date().toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (e) { /* silent */ }
    };

    const svc = async (fn, ...args) => {
        const mod = await import('../services');
        return mod[fn](...args);
    };

    const loadScopeOptions = async () => {
        try {
            const [bRes, pRes] = await Promise.all([
                svc('risk_portal_bus_list', { include_inactive: 'false' }).catch(() => null),
                svc('risk_portal_projects_list').catch(() => null),
            ]);
            const busData = bRes?.statusCode?.response_data?.data || bRes?.data?.response_data?.data;
            const projData = pRes?.statusCode?.response_data?.data || pRes?.data?.response_data?.data;
            setScopeOptions({
                business_units: Array.isArray(busData?.business_units) ? busData.business_units : [],
                projects: Array.isArray(projData?.projects) ? projData.projects : [],
                regions: [],
            });
        } catch (e) { }
    };

    const loadDashboard = async ({ silent = false } = {}) => {
        // Silent refresh keeps the existing data on screen and only flips
        // the small `tabRefreshing` indicator. Used after every mutation
        // (sync, delete, baseline change) so the page does not blank to a
        // full-screen spinner. Initial mount still uses `setLoading(true)`.
        if (silent) setTabRefreshing(true); else setLoading(true);
        const sp = scopeParams();
        const params = Object.keys(sp).length ? sp : undefined;
        // 304 = "your client's data is still current." The interceptor
        // packs `notModified: true` into the response. We must NOT
        // reset state when we see it — the previous render is still
        // correct. Pre-fix, a 304 sent the call into the catch block
        // (axios rejected it), and the catch reset dashData to zero
        // — turning a "nothing changed, your view is fine" into "the
        // dashboard is empty," which is exactly the bug the user hit
        // on hard reload after the new ETag work landed.
        //
        // The three calls below fire in parallel but commit state
        // independently — pre-2026-05-06 we awaited Promise.all and the
        // skeleton stayed up until the slowest call settled, so a 5-sec
        // matrix on a 30k-asset tenant gated the dashboard tile that had
        // already returned in 200 ms. Now setDashData fires the moment the
        // dashboard call lands, the page un-skeletons (line ~1943 gates on
        // `loading && !dashData`), and gaps + matrix fill in as they arrive.
        const dashP = svc('kri_get_dashboard', params).then(r => {
            if (!r?.notModified) {
                const d = r?.statusCode?.response_data?.data;
                if (d) {
                    setDashData(d);
                    // Persist for next-visit instant paint. Best-effort —
                    // localStorage can throw under quota / private-mode.
                    try { localStorage.setItem(KRI_DASH_CACHE_KEY, JSON.stringify({ _cached_at: Date.now(), data: d })); } catch (e) { }
                }
                else if (!silent) setDashData({ total_assets: 0, total_tools: 0, overall_score: 0, tools: [] });
            }
        }).catch(() => {
            // Only reset to empty on hard initial load. A silent refresh failure
            // keeps the previous data on screen so a transient blip doesn't wipe
            // the user's view.
            if (!silent) setDashData({ total_assets: 0, total_tools: 0, overall_score: 0, tools: [] });
        });
        const gapsP = svc('kri_list_gaps', params).then(r => {
            if (!r?.notModified) {
                const d = r?.statusCode?.response_data?.data;
                if (d) setGaps(_normaliseGaps(d));
            }
        }).catch(() => { });
        const matrixP = svc('kri_get_compliance_matrix', params).then(r => {
            if (!r?.notModified) {
                const d = r?.statusCode?.response_data?.data;
                if (d) setMatrixData(d);
            }
        }).catch(() => { });
        await Promise.allSettled([dashP, gapsP, matrixP]);
        if (silent) setTabRefreshing(false); else setLoading(false);
    };

    // _showLoadError: surface previously-swallowed fetch failures to the user.
    // Network blips, 5xx, parse errors all used to disappear silently — now they flash.
    //
    // Categorize the failure by axios shape so the user can tell whether it's
    // their session, our server, the network in between, or a vendor / sync
    // problem. Pre-fix, every failure surfaced as bare `e.message`, and for
    // browser-to-API transport failures axios produces the literal string
    // "Network Error" — which a user reasonably misreads as "vendor sync had
    // a network problem" and starts checking firewall rules at the vendor.
    // It actually means "the browser could not reach our API," which is a
    // very different debugging path.
    const _showLoadError = (label, e) => {
        let detail;
        const message = (e && e.message) || '';
        const status = e && e.response && e.response.status;
        const body = e && e.response && e.response.data;
        const apiMsg = (body && (body.errors?.message || body.detail || body.error?.message)) || '';
        if (status === 401 || status === 403) {
            detail = `Session check failed (HTTP ${status}). ${apiMsg || 'Log out and back in.'}`;
        } else if (status >= 500) {
            detail = `Server error (HTTP ${status}) on /audit/kri/. ${apiMsg || 'Backend logs will have detail.'}`;
        } else if (status >= 400) {
            detail = `Request rejected (HTTP ${status}). ${apiMsg || message}`;
        } else if (message === 'Network Error') {
            detail = 'Browser could not reach the NMT API server. This is a transport-layer failure (DNS, firewall, API pod restart, or CORS) — NOT a vendor / sync issue. The background sync itself may still be running. Wait a few seconds and click again.';
        } else if ((e && e.code === 'ECONNABORTED') || message.toLowerCase().includes('timeout')) {
            detail = 'Request timed out before the server responded. The dataset may be unusually large; try again or narrow the scope.';
        } else if (message) {
            detail = message;
        } else {
            detail = 'Unknown error. Check the browser dev console for the underlying response.';
        }
        flash(`Could not load ${label}: ${detail.substring(0, 280)}`);
    };

    const loadTools = async () => {
        try {
            const [tRes, vRes] = await Promise.all([svc('kri_list_tools'), svc('kri_list_vendors')]);
            // Preserve previous state on 304 — see loadDashboard for the
            // full rationale. /tools/ has had ETag for a while; without
            // this guard a 304 wipes the tools list to [], which is what
            // happens silently after a hard reload while the backend's
            // ETag is unchanged.
            if (!tRes?.notModified) {
                setTools(tRes?.statusCode?.response_data?.data || []);
            }
            if (!vRes?.notModified) {
                setVendors(vRes?.statusCode?.response_data?.data || []);
            }
        } catch (e) { _showLoadError('tools list', e); }
    };

    const loadAssets = async () => {
        setTabRefreshing(true);
        try {
            const params = { ...scopeParams() };
            if (assetSearch) params.search = assetSearch;
            params.page = assetPage;
            const r = await svc('kri_list_assets', Object.keys(params).length ? params : undefined);
            const d = r?.statusCode?.response_data?.data;
            // Tolerate the legacy flat-array shape for backward compat with any
            // older deployment that hasn't shipped the paginated response yet.
            if (Array.isArray(d)) {
                setAssets(d);
                setAssetTotal(d.length);
            } else if (d) {
                setAssets(d.rows || []);
                setAssetTotal(d.total ?? (d.rows?.length || 0));
            } else {
                setAssets([]); setAssetTotal(0);
            }
        } catch (e) { _showLoadError('asset baseline', e); }
        finally { setTabRefreshing(false); }
    };

    const loadMatrix = async () => {
        setTabRefreshing(true);
        try {
            const params = { ...scopeParams() };
            if (matrixFilter) params.status = matrixFilter;
            if (matrixSearch) params.search = matrixSearch;
            params.page = matrixPage;
            params.page_size = MATRIX_PAGE_SIZE;
            const r = await svc('kri_get_compliance_matrix', Object.keys(params).length ? params : undefined);
            // Preserve previous state on 304 — see loadDashboard.
            if (!r?.notModified) setMatrixData(r?.statusCode?.response_data?.data);
        } catch (e) { _showLoadError('coverage matrix', e); }
        finally { setTabRefreshing(false); }
    };

    const loadGaps = async () => {
        setTabRefreshing(true);
        try {
            const params = { ...scopeParams() };
            if (gapToolFilter) params.tool_id = gapToolFilter;
            // Backend supports two independent paginations (asset vs
            // ticket). Send both so each table can navigate its own
            // window. Pre-fix the backend hardcoded [:1000] queryset
            // cap + [:500] response cap, silently truncating large
            // gap reports — operators with >500 gaps couldn't see
            // anything past row 500.
            params.asset_page = assetGapPage;
            params.asset_page_size = GAP_PAGE_SIZE;
            params.ticket_page = ticketGapPage;
            params.ticket_page_size = GAP_PAGE_SIZE;
            const r = await svc('kri_list_gaps', params);
            // Preserve previous state on 304 — see loadDashboard.
            if (!r?.notModified) setGaps(_normaliseGaps(r?.statusCode?.response_data?.data));
        } catch (e) { _showLoadError('gap report', e); }
        finally { setTabRefreshing(false); }
    };

    const loadMappingRules = async () => {
        setTabRefreshing(true);
        try {
            const r = await svc('kri_list_mapping_rules');
            setMappingRules(r?.statusCode?.response_data?.data || []);
        } catch (e) { _showLoadError('mapping rules', e); }
        finally { setTabRefreshing(false); }
    };

    const loadOrgSettings = async () => {
        setTabRefreshing(true);
        try {
            const r = await svc('kri_get_org_settings');
            const d = r?.statusCode?.response_data?.data || null;
            setOrgSettings(d);
            // Seed the form from saved state on every load so cancelling
            // a half-edit reverts cleanly.
            setOrgSettingsForm(d ? {
                ai_enabled: !!d.ai_enabled,
                reason: '',
                target_compliance_pct: d.target_compliance_pct ?? 95,
                rag_amber_threshold: d.rag_amber_threshold ?? 85,
                rag_red_threshold: d.rag_red_threshold ?? 70,
            } : null);
        } catch (e) { _showLoadError('org settings', e); }
        finally { setTabRefreshing(false); }
    };

    const loadLogs = async () => {
        setTabRefreshing(true);
        try {
            const r = await svc('kri_list_sync_logs');
            setSyncLogs(r?.statusCode?.response_data?.data || []);
        } catch (e) { _showLoadError('sync logs', e); }
        finally { setTabRefreshing(false); }
    };

    /**
     * Pull the persisted "tool reports devices not in baseline" snapshot
     * for every configured tool. Cheap — each call reads from the tool
     * row's additional_config, no vendor round-trip. We always re-fetch
     * the tools list first so a freshly-added integration shows up.
     */
    const loadUnmatched = async () => {
        setUnmatchedLoading(true);
        try {
            const tr = await svc('kri_list_tools');
            const toolList = tr?.statusCode?.response_data?.data || [];
            setTools(toolList);
            const out = {};
            for (const t of toolList) {
                try {
                    const r = await svc('kri_list_unmatched_tool_assets', t.id);
                    out[t.id] = r?.statusCode?.response_data?.data || { count: 0, samples: [] };
                } catch (e) {
                    out[t.id] = { count: 0, samples: [], error: 'load failed' };
                }
            }
            setUnmatched(out);
        } catch (e) { _showLoadError('unmatched devices', e); }
        finally { setUnmatchedLoading(false); }
    };

    const loadDefinitions = async () => {
        setDefinitionsLoading(true);
        try {
            const params = {};
            // "Show archived" must surface ONLY archived KRIs, not active+archived.
            // Backend only exposes include_inactive (returns both), so filter
            // client-side when the toggle is on.
            if (showInactiveDefinitions) params.include_inactive = '1';
            if (definitionCategory) params.risk_category = definitionCategory;
            if (definitionSearch.trim()) params.search = definitionSearch.trim();
            const r = await svc('kri_list_definitions', params);
            const list = r?.statusCode?.response_data?.data?.definitions || [];
            const filtered = showInactiveDefinitions
                ? list.filter(d => d.is_active === false || d.is_active === 0 || d.status === 'archived' || d.status === 'inactive')
                : list;
            setDefinitions(filtered);
        } catch (e) { _showLoadError('KRI library', e); }
        finally { setDefinitionsLoading(false); }
    };

    const openDefinitionForm = (existing) => {
        setDefinitionForm(existing ? { ...existing } : {
            name: '', description: '',
            risk_category: 'cyber', risk_type: '',
            frequency: 'monthly', direction: 'higher_is_better',
            threshold_green: '', threshold_amber: '', threshold_red: '',
            target_value: '', measurement_method: '', calculation_formula: '',
            owner_name: '', owner_email: '', reporting_manager: '',
            linked_frameworks: [], linked_risks: [], linked_controls: [],
            linked_tool_ids: [],
            current_value: '', current_status: 'unknown',
            is_active: true,
        });
        setShowDefinitionModal(true);
    };

    const saveDefinition = async () => {
        if (!definitionForm?.name?.trim()) { flash('KRI name is required.'); return; }
        setDefinitionSaving(true);
        try {
            const payload = { ...definitionForm };
            // Frameworks/risks/controls editors hand back arrays already; keep
            // the API contract clean by stripping empty strings.
            ['linked_frameworks', 'linked_risks', 'linked_controls'].forEach(k => {
                payload[k] = (payload[k] || []).map(s => String(s).trim()).filter(Boolean);
            });
            payload.linked_tool_ids = (payload.linked_tool_ids || []).map(n => Number(n)).filter(Boolean);
            const fn = definitionForm.id ? 'kri_update_definition' : 'kri_create_definition';
            const r = definitionForm.id
                ? await svc(fn, definitionForm.id, payload)
                : await svc(fn, payload);
            console.log("ss", r);

            const ok = r?.statusCode?.status === 1;
            const msg = r?.statusCode?.response_data?.data?.message
                || r?.statusCode?.response_data?.error_msg
                || (ok ? 'KRI saved.' : 'Failed to save.');
            console.log("msg", msg);

            flash(msg);
            if (ok) {
                setShowDefinitionModal(false);
                setDefinitionForm(null);
                loadDefinitions();
            }
        } catch (e) {
            setDefinitionSaving(false);
            flash('Failed to save KRI.');
        } finally { setDefinitionSaving(false); }
    };

    const restoreDefinition = async (def) => {
        try {
            const r = await svc('kri_update_definition', def.id, { ...def, is_active: true });
            const ok = r?.statusCode?.status === 1;
            flash(ok ? 'KRI restored.' : 'Failed to restore KRI.');
            if (ok) loadDefinitions();
        } catch (e) {
            flash('Failed to restore KRI.');
        }
    };

    const deleteDefinition = async (def) => {
        const ok = await confirmDialog({
            title: 'Archive this KRI?',
            message: `${def.kri_id} ${def.name} is archived. To reactivate it, go to Show Archived → Edit → Active.`,
            confirmLabel: 'Archive', cancelLabel: 'Cancel', danger: true,
        });
        if (!ok) return;
        try {
            await svc('kri_delete_definition', def.id);
            flash('KRI archived.');
            loadDefinitions();
        } catch (e) { flash('Failed to archive KRI.'); }
    };

    // Permanent (hard) delete — removes the KRI definition entirely via
    // DELETE /audit/kri/definitions/<id>/?permanent=1. Unlike Archive (soft),
    // this cannot be restored, so it uses a stronger confirmation.
    const permanentDeleteDefinition = async (def) => {
        const ok = await confirmDialog({
            title: 'Delete this KRI permanently?',
            message: `${def.kri_id} ${def.name} will be permanently deleted. This cannot be undone.`,
            confirmLabel: 'Delete', cancelLabel: 'Cancel', danger: true,
        });
        if (!ok) return;
        try {
            await svc('kri_permanent_delete_definition', def.id);
            flash('KRI deleted.');
            loadDefinitions();
        } catch (e) { flash('Failed to delete KRI.'); }
    };

    const downloadDefinitionTemplate = async () => {
        try {
            const r = await svc('kri_download_definition_template');
            // SbomApiIntercepter wraps every response as { statusCode: <body>, errors: ... }.
            // For blob requests, <body> IS the Blob. Extract it from statusCode first,
            // then fall back to r.data (raw axios), then wrap whatever we have.
            const rawBlob = r?.statusCode instanceof Blob ? r.statusCode : (r?.data instanceof Blob ? r.data : r?.data);
            const url = window.URL.createObjectURL(
                rawBlob instanceof Blob
                    ? rawBlob
                    : new Blob([rawBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            );
            const a = document.createElement('a');
            a.href = url; a.download = 'NMT_KRI_Register_Template.xlsx';
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); window.URL.revokeObjectURL(url);
        } catch (e) { flash('Could not download template.'); }
    };

    const previewImport = async (file) => {
        setImportError('');
        setImportFile(file);
        setImportStage('preview');
        try {
            const r = await svc('kri_import_definitions_preview', file);
            const data = r?.statusCode?.response_data?.data;
            if (!data) {
                setImportError(r?.statusCode?.response_data?.error_msg || 'Could not parse file.');
                setImportPreview(null);
                return;
            }
            setImportPreview(data);
            if (data.errors && data.errors.length) {
                setImportError(data.errors.join('; '));
            }
        } catch (e) { setImportError('Upload failed.'); }
    };

    const commitImport = async () => {
        if (!importPreview?.rows?.length) return;
        setImportStage('committing');
        try {
            const r = await svc('kri_import_definitions_commit', importPreview);
            const data = r?.statusCode?.response_data?.data;
            flash(data?.message || 'Import complete.');
            setImportStage(null);
            setImportPreview(null);
            setImportFile(null);
            loadDefinitions();
        } catch (e) {
            flash('Import failed.');
            setImportStage('preview');
        }
    };

    const handleTabChange = (t) => {
        setTab(t);
        if (t === 'dashboard') loadDashboard({ silent: true });
        else if (t === 'tools') loadTools();
        else if (t === 'assets') loadAssets();
        else if (t === 'matrix') loadMatrix();
        else if (t === 'gaps') { loadGaps(); if ((tools || []).length === 0) loadTools(); }
        else if (t === 'logs') loadLogs();
        else if (t === 'unmatched') loadUnmatched();
        else if (t === 'library') loadDefinitions();
        else if (t === 'mapping') {
            loadMappingRules();
            // Mapping form needs BU + Project dropdowns; loadScopeOptions
            // runs at mount but if it failed silently we re-try here.
            if ((scopeOptions.business_units || []).length === 0 && (scopeOptions.projects || []).length === 0) {
                loadScopeOptions();
            }
        }
        else if (t === 'settings') {
            loadOrgSettings();
            // Settings tab's grace-period UI lists tools — populate if cold.
            if ((tools || []).length === 0) loadTools();
        }
    };

    // Re-fetch gaps when the user paginates either table. Two
    // independent paginations so the asset table and ticket table
    // can navigate independently. Skips the very first render
    // (page 1 is already the default; loadGaps fires on tab change).
    useEffect(() => {
        if (tab !== 'gaps') return;
        loadGaps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetGapPage, ticketGapPage]);

    const openAddTool = async () => {
        try { const r = await svc('kri_list_vendors'); setVendors(r?.statusCode?.response_data?.data || []); } catch { }
        setSelectedVendor(null);
        setToolForm({});
        setBaselineStep(1);
        setTestResult(null);
        setPreviewError('');
        setPreviewData(null);
        setShowToolModal(true);
    };

    const openEditTool = (t) => {
        setEditTool(t);
        setEditToolForm({
            name: t.name || '',
            compliance_threshold: t.compliance_threshold ?? 95,
            sync_max_records: t.sync_max_records ?? 100000,
            sync_frequency_hours: t.sync_frequency_hours ?? 6,
        });
    };

    const saveEditTool = async () => {
        if (!editTool) return;
        setEditToolSaving(true);
        try {
            const payload = {
                name: editToolForm.name,
                compliance_threshold: parseInt(editToolForm.compliance_threshold, 10) || 0,
                sync_max_records: Math.max(0, parseInt(editToolForm.sync_max_records, 10) || 0),
                sync_frequency_hours: Math.max(1, parseInt(editToolForm.sync_frequency_hours, 10) || 6),
            };
            const r = await svc('kri_update_tool', editTool.id, payload);
            const errMsg = r?.statusCode?.error?.message || r?.errors?.message;
            if (errMsg) { flash(errMsg); setEditToolSaving(false); return; }
            // Keep org policy target in sync with this tool's compliance threshold
            if (payload.compliance_threshold != null) {
                try {
                    const sr = await svc('kri_get_org_settings');
                    const sd = sr?.statusCode?.response_data?.data || {};
                    await svc('kri_update_org_settings', {
                        ai_enabled: sd.ai_enabled ?? true,
                        reason: '',
                        target_compliance_pct: payload.compliance_threshold,
                        rag_amber_threshold: sd.rag_amber_threshold ?? 85,
                        rag_red_threshold: sd.rag_red_threshold ?? 70,
                    });
                } catch (_) { /* non-critical */ }
            }
            flash('Tool updated.');
            setEditTool(null);
            setEditToolForm({});
            loadTools();
        } catch (e) {
            flash(e?.response?.data?.error?.message || e?.message || 'Update failed.');
        }
        setEditToolSaving(false);
    };

    const testConnection = async (toolId) => {
        setTestResult(null);
        try {
            const r = await svc('kri_test_tool_connection', toolId);
            const d = r?.statusCode?.response_data?.data;
            setTestResult(d);
            if (d?.success) flash('Connection successful.');
            else flash(d?.message || 'Connection failed.');
        } catch (e) { setTestResult({ success: false, message: 'Request failed.' }); flash('Connection test failed.'); }
    };

    const syncTool = async (toolId, params) => {
        setSyncing(toolId);
        try {
            const r = await svc('kri_sync_tool', toolId, params || {});
            const d = r?.statusCode?.response_data?.data;
            const errMsg = r?.statusCode?.error?.message;
            if (errMsg) {
                const cutoff = errMsg.toLowerCase().indexOf('manual sync');
                flash(cutoff !== -1 ? errMsg.substring(0, cutoff + 'manual sync'.length) + '.' : errMsg);
                setSyncing(null);
                return;
            }
            // Async path: backend returns { sync_log_id, status: 'running', async: true }.
            // Poll /sync-status/ until status changes from 'running'.
            if (d?.async && d?.status === 'running') {
                flash(`Sync started in background. Polling for progress…`);
                let elapsed = 0;
                const poll = async () => {
                    try {
                        const sr = await svc('kri_get_sync_status', toolId);
                        const sd = sr?.statusCode?.response_data?.data;
                        if (!sd) return setTimeout(poll, 5000);
                        if (sd.status === 'success') {
                            flash(`Sync complete: ${sd.compliant_count} compliant, ${sd.non_compliant_count} gaps, ${sd.new_gaps} new.`);
                            setSyncing(null);
                            loadTools(); loadDashboard({ silent: true });
                            return;
                        }
                        if (sd.status === 'error') {
                            flash(`Sync failed: ${(sd.error_message || 'unknown').substring(0, 120)}`);
                            setSyncing(null);
                            loadTools(); loadDashboard({ silent: true });
                            return;
                        }
                        // Still running — continue polling. Show running time.
                        elapsed = sd.running_for_seconds || elapsed + 5;
                        setSyncing(toolId);  // keep button disabled
                        setTimeout(poll, 5000);
                    } catch (e) {
                        // Network blip — keep trying for a bit.
                        setTimeout(poll, 8000);
                    }
                };
                setTimeout(poll, 2500);  // first poll after 2.5s (backend should have started)
                return;
            }
            // Synchronous path (wait=true or small tool): completed inline.
            flash(d?.message || 'Sync complete.');
            loadTools();
            loadDashboard({ silent: true });
        } catch (e) { flash('Sync failed.'); }
        setSyncing(null);
    };

    const createTool = async () => {
        setToolCreating(true);
        try {
            // Strip _deviceFile — File objects can't be JSON-serialised.
            // It's uploaded separately via kri_upload_tool_devices after creation.
            const { _deviceFile: csvFile, ...toolPayload } = toolForm;
            console.log('[kri-create] csvFile present:', !!csvFile, csvFile?.name, 'size:', csvFile?.size);
            const r = await svc('kri_create_tool', toolPayload);
            if (r?.statusCode?.status === 0 || r?.errors?.message) {
                const msg = r?.errors?.message || r?.statusCode?.error?.message || 'Unknown error';
                setPreviewError(msg);
                flash(msg);
                return;
            }
            const d = r?.statusCode?.response_data?.data;
            console.log('[kri-create] create response data:', d);
            // Persist selected_fields locally so Coverage Matrix can show them even if
            // kri_list_tools / kri_get_compliance_matrix don't expose the field.
            if (d?.id && toolPayload.selected_fields?.length) {
                try {
                    const stored = JSON.parse(localStorage.getItem('kri_tool_fields_v1') || '{}');
                    stored[d.id] = toolPayload.selected_fields;
                    localStorage.setItem('kri_tool_fields_v1', JSON.stringify(stored));
                } catch (_) { }
            }

            // The new tool id can come back under several keys depending on backend
            // version — be tolerant so the follow-up upload doesn't silently skip.
            const capturedId = d?.id ?? d?.tool_id ?? d?.tool?.id;
            const capturedThreshold = toolPayload.compliance_threshold;

            // 1. AWAIT the CSV upload so the user sees one outcome (created + uploaded)
            //    instead of having to click "Upload Devices" on the listing afterwards.
            if (csvFile && capturedId) {
                try {
                    const ur = await svc('kri_upload_tool_devices', capturedId, csvFile);
                    console.log('[kri-create] upload response:', ur?.statusCode);
                    const uploadErr = ur?.statusCode?.error?.message || ur?.errors?.message;
                    if (uploadErr) {
                        flash(`Tool created, but CSV upload failed: ${uploadErr}. Use "Upload Devices" to retry.`);
                    } else {
                        flash(ur?.statusCode?.response_data?.data?.message || 'Tool integration created and device CSV uploaded.');
                    }
                } catch (uploadEx) {
                    console.error('[kri-create] upload exception:', uploadEx);
                    flash('Tool created, but CSV upload failed. Use "Upload Devices" to retry.');
                }
            } else if (d?.auto_sync_started) {
                flash('Tool created. Initial sync running in background — you can navigate freely.');
            } else {
                flash('Tool integration created.');
            }

            // Close modal once create + (optional) upload have both resolved.
            setShowToolModal(false);
            loadTools();
            loadDashboard({ silent: true });

            // Background sync polling (only applies when the backend kicked off
            // an auto-sync; harmless otherwise).
            if (d?.auto_sync_started && d?.id) {
                setSyncing(d.id);
                const pollNew = async () => {
                    try {
                        const sr = await svc('kri_get_sync_status', d.id);
                        const sd = sr?.statusCode?.response_data?.data;
                        if (sd?.status === 'success' || sd?.status === 'error') {
                            setSyncing(null);
                            loadTools(); loadDashboard({ silent: true });
                            if (sd.status === 'success') {
                                flash(`First sync complete: ${sd.compliant_count} compliant, ${sd.non_compliant_count} gaps.`);
                            } else {
                                flash(`First sync failed: ${(sd.error_message || 'unknown').substring(0, 120)}`);
                            }
                            return;
                        }
                        setTimeout(pollNew, 5000);
                    } catch (e) { setTimeout(pollNew, 8000); }
                };
                setTimeout(pollNew, 3000);
            }

            // 2. Sync compliance threshold → org policy target (fire-and-forget)
            if (capturedThreshold != null) {
                (async () => {
                    try {
                        const sr = await svc('kri_get_org_settings');
                        const sd = sr?.statusCode?.response_data?.data || {};
                        await svc('kri_update_org_settings', {
                            ai_enabled: sd.ai_enabled ?? true,
                            reason: '',
                            target_compliance_pct: capturedThreshold,
                            rag_amber_threshold: sd.rag_amber_threshold ?? 85,
                            rag_red_threshold: sd.rag_red_threshold ?? 70,
                        });
                    } catch (_) { }
                })();
            }
        } catch (e) {
            setToolCreating(false);
            const msg = e?.response?.data?.error?.message
                || e?.response?.data?.detail
                || e?.message
                || 'Failed to create tool.';
            setPreviewError(msg);
            flash(msg);
        } finally {
            setToolCreating(false);
        }
    };

    const uploadAssets = async (file) => {
        try {
            const r = await svc('kri_upload_assets', file);
            const d = r?.statusCode?.response_data?.data;
            flash(d?.message || 'Upload complete.');
            // New assets shift the baseline; refresh dependent views in real time.
            loadAssets();
            loadDashboard({ silent: true });
            loadMatrix();
        } catch (e) { flash('Upload failed.'); }
    };

    const fmtDate = (ts) => {
        if (!ts) return '-';
        try { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ts; }
    };


    // Tolerate the legacy flat-array shape so a stale backend doesn't blank the tab.
    // Preserve `total_asset_gaps` / `total_ticket_gaps` and the echoed pagination
    // params — pre-2026-05-06 this helper kept only the two arrays, which made
    // `gaps.total_asset_gaps` undefined and every UI counter fell back to
    // `array.length` (=100, the page size). Result: "1–100 of 100" + disabled
    // Next button on tenants with thousands of real gaps.
    function _normaliseGaps(payload) {
        if (Array.isArray(payload)) return { asset_gaps: payload, ticket_gaps: [] };
        return {
            asset_gaps: payload?.asset_gaps || [],
            ticket_gaps: payload?.ticket_gaps || [],
            total_asset_gaps: payload?.total_asset_gaps,
            total_ticket_gaps: payload?.total_ticket_gaps,
            asset_page: payload?.asset_page,
            asset_page_size: payload?.asset_page_size,
            ticket_page: payload?.ticket_page,
            ticket_page_size: payload?.ticket_page_size,
        };
    }

    const scoreColor = (score, threshold, critical) => {
        if (score >= threshold) return '#0f7b4f';
        if (score >= critical) return '#d97706';
        return '#dc2626';
    };

    const tabStyle = (t) => ({
        padding: '6px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
        borderBottom: `2px solid ${tab === t ? '#1b2a4a' : 'transparent'}`,
        background: 'none', color: tab === t ? '#1b2a4a' : '#7a869a', fontFamily: 'inherit',
    });

    if (loading && !dashData) return <>{confirmDialogEl}<DashboardSkeleton /></>;

    // Bucket glossary modal — context-aware help for the operator.
    // The 4-bucket model (installed/healthy/unhealthy/missing) is
    // universal; per-category labels (e.g. MDM uses Enrolled /
    // Compliant State / Noncompliant / Unenrolled) are renames. This
    // modal shows the active tool's labels next to the universal
    // definitions so a customer can understand what the dashboard
    // is actually telling them without leaving the page.
    const glossaryTool = glossaryToolId
        ? (dashData?.tools || []).find(t => t.id === glossaryToolId)
        : null;
    const renderGlossaryModal = () => {
        if (!glossaryTool) return null;
        const t = glossaryTool;
        const labels = t.dashboard_labels || {};
        const unit = (t.unit_label && t.unit_label.plural) || 'assets';
        // Each row: {bucket, label, what_it_counts}.
        // `what_it_counts` is the universal definition; the label is
        // the customer-facing rename for THIS tool's category.
        const rows = [
            {
                bucket: 'installed',
                label: labels.installed || 'Installed',
                count: t.installed ?? t.compliant ?? 0,
                what: `${unit} the tool reports a record for. For ${t.tool_type?.toUpperCase() || 'this category'}, this is "the tool has it on its books."`,
            },
            {
                bucket: 'healthy',
                label: labels.healthy || 'Healthy',
                count: t.healthy ?? 0,
                what: `Subset of ${labels.installed || 'Installed'} where the tool reports a good state — agent reporting normally, no policy violations, no SLA breach.`,
            },
            {
                bucket: 'unhealthy',
                label: labels.unhealthy || 'Unhealthy',
                count: t.health_degraded ?? 0,
                what: `Subset of ${labels.installed || 'Installed'} where the tool reports a degraded state — offline agent, RFM mode, policy violation, ticket reopened, etc.`,
            },
            {
                bucket: 'missing',
                label: labels.missing || 'Missing',
                count: t.not_installed ?? t.non_compliant ?? 0,
                what: `${unit} in the platform's baseline (e.g. from another tool or a CSV upload) that this tool has NO record for. The tool is silent on these — they're either un-enrolled, never reported, or removed.`,
            },
        ];
        const total = (t.total_checked ?? ((t.installed ?? 0) + (t.not_installed ?? 0))) || 0;
        return (
            <div className="ap-modal-overlay" onClick={() => setGlossaryToolId(null)}>
                <div className="ap-modal" style={{ maxWidth: 640, position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setGlossaryToolId(null)}
                        aria-label="Close"
                        style={{
                            position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                            fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                        }}>×</button>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>What do these labels mean?</h3>
                    <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 14 }}>
                        {t.name} · {(t.tool_type || '').toUpperCase()} · {total.toLocaleString()} {unit} in scope
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 14 }}>
                        <thead>
                            <tr style={{ background: '#fafbfc' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 130 }}>LABEL</th>
                                <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 80 }}>COUNT</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a' }}>WHAT IT MEANS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.bucket} style={{ borderTop: '1px solid #f0f2f5' }}>
                                    <td style={{ padding: '8px 10px', verticalAlign: 'top' }}>
                                        <div style={{ fontWeight: 700, color: '#1b2a4a' }}>{r.label}</div>
                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>= {r.bucket}</div>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', verticalAlign: 'top', fontWeight: 600, color: '#1b2a4a' }}>{r.count.toLocaleString()}</td>
                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', color: '#3d4f6f', lineHeight: 1.45 }}>{r.what}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e6e9ee', borderRadius: 6, padding: 10, fontSize: 11, color: '#3d4f6f', lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>How the score is computed</div>
                        <div>
                            <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #d8dce3' }}>
                                {labels.score || 'Score'} = {labels.healthy || 'Healthy'} ÷ ({labels.installed || 'Installed'} + {labels.missing || 'Missing'}) × 100%
                            </code>
                        </div>
                        <div style={{ fontSize: 10, color: '#7a869a', marginTop: 6 }}>
                            "Healthy over total" — counts assets that are both <i>known to the tool</i> AND <i>in a good state</i>. {labels.unhealthy || 'Unhealthy'} rows count against the score; only {labels.healthy || 'Healthy'} rows count for it. Pre-2026-05-05 the formula was Installed÷Total which inflated the score for tenants with many degraded agents.
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                        <button className="ap-btn ap-btn--primary" style={{ fontSize: 11 }} onClick={() => setGlossaryToolId(null)}>Got it</button>
                    </div>
                </div>
            </div>
        );
    };

    const renderDashGlossary = () => {
        if (!dashGlossaryOpen) return null;
        const dah = dashData?.distinct_asset_health || {};
        const rows = [
            {
                label: 'Healthy', count: dah.healthy ?? 0, color: '#0f7b4f',
                what: 'Every tool that tracks this asset reports it as compliant AND the agent is in a healthy state. The asset is enrolled, working, and being protected by every relevant tool.',
            },
            {
                label: 'Unhealthy', count: dah.unhealthy ?? 0, color: '#d97706',
                what: 'At least one tool has the agent installed but in a degraded state (offline, disconnected, inactive, unknown, reopened ticket, etc.). The asset is in scope but not currently being protected — usually the highest-leverage fix because no install is needed.',
            },
            {
                label: 'Missing', count: dah.missing ?? 0, color: '#dc2626',
                what: 'Either at least one tool reports this asset as non-compliant (tool doesn\'t see it — agent not installed or out of scope), OR no asset-shape tool has any record for this asset (entirely unmonitored). Same number as the Coverage Gaps tile.',
            },
        ];
        const total = dah.total ?? rows.reduce((s, r) => s + r.count, 0);
        return (
            <div className="ap-modal-overlay" onClick={() => setDashGlossaryOpen(false)}>
                <div className="ap-modal" style={{ maxWidth: 680, position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setDashGlossaryOpen(false)} aria-label="Close"
                        style={{
                            position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                            fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                        }}>×</button>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>How the dashboard buckets work</h3>
                    <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 14 }}>
                        Per-asset rollup across every tool that tracks it · {total.toLocaleString()} assets in baseline
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 14 }}>
                        <thead>
                            <tr style={{ background: '#fafbfc' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 110 }}>BUCKET</th>
                                <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 80 }}>COUNT</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a' }}>WHAT IT MEANS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.label} style={{ borderTop: '1px solid #f0f2f5' }}>
                                    <td style={{ padding: '8px 10px', verticalAlign: 'top' }}>
                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: r.color, marginRight: 6 }}></span>
                                        <span style={{ fontWeight: 700, color: '#1b2a4a' }}>{r.label}</span>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', verticalAlign: 'top', fontWeight: 600, color: r.color }}>{r.count.toLocaleString()}</td>
                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', color: '#3d4f6f', lineHeight: 1.45 }}>{r.what}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e6e9ee', borderRadius: 6, padding: 10, fontSize: 11, color: '#3d4f6f', lineHeight: 1.5, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>Worst-state-wins ordering</div>
                        <div>
                            <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #d8dce3' }}>Unhealthy &gt; Missing &gt; Healthy</code>
                            <span style={{ marginLeft: 6 }}>— if an asset is Healthy in one tool but Unhealthy in another, the asset's bucket is Unhealthy.</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#7a869a', marginTop: 6 }}>
                            Unhealthy outranks Missing because an installed-but-broken agent is the most actionable finding (no rollout needed — just fix the agent). The three buckets always sum to total assets, with no overlap.
                        </div>
                    </div>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e6e9ee', borderRadius: 6, padding: 10, fontSize: 11, color: '#3d4f6f', lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>How the compliance % is computed</div>
                        <div>
                            <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #d8dce3' }}>Compliance = Healthy ÷ Total Assets × 100%</code>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                        <button className="ap-btn ap-btn--primary" style={{ fontSize: 11 }} onClick={() => setDashGlossaryOpen(false)}>Got it</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div ref={containerRef}>
            <FullPageLoader loading={previewLoading || toolCreating || !!syncing} message={syncing ? 'Processing tool data — this may take a few moments…' : undefined} />
            {confirmDialogEl}
            {renderGlossaryModal()}
            {renderDashGlossary()}
            {auditLogToolId !== null && (() => {
                const tool = (dashData?.tools || []).find(t => t.id === auditLogToolId);
                return (
                    <div className="ap-modal-overlay" onClick={() => setAuditLogToolId(null)}>
                        <div className="ap-modal" style={{ maxWidth: 720, maxWidth: '92vw', position: 'relative' }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => setAuditLogToolId(null)} aria-label="Close"
                                style={{
                                    position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                    fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                                }}>×</button>
                            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Tool change history</h3>
                            <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 14 }}>
                                {tool?.name || 'Tool'} · who changed what, when
                            </div>
                            {auditLogLoading && <div style={{ fontSize: 12, color: '#7a869a', padding: '20px 0' }}>Loading…</div>}
                            {!auditLogLoading && auditLogEntries.length === 0 && (
                                <div style={{ fontSize: 12, color: '#7a869a', padding: '20px 0' }}>
                                    No changes recorded for this tool yet. Audit entries are written on create / update / delete events.
                                </div>
                            )}
                            {!auditLogLoading && auditLogEntries.length > 0 && (
                                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#fafbfc' }}>
                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 140 }}>WHEN</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 100 }}>EVENT</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', width: 180 }}>BY</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a' }}>CHANGES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditLogEntries.map(e => (
                                            <tr key={e.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                                                <td style={{ padding: '6px 10px', verticalAlign: 'top', color: '#3d4f6f' }}>{e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}</td>
                                                <td style={{ padding: '6px 10px', verticalAlign: 'top' }}>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                                                        background: e.event === 'created' ? '#e3f7e8' : e.event === 'deleted' ? '#fee2e2' : '#fef3c7',
                                                        color: e.event === 'created' ? '#0f7b4f' : e.event === 'deleted' ? '#dc2626' : '#92400e'
                                                    }}>
                                                        {e.event}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '6px 10px', verticalAlign: 'top', color: '#3d4f6f' }}>{e.actor_email || '(system)'}</td>
                                                <td style={{ padding: '6px 10px', verticalAlign: 'top', color: '#3d4f6f' }}>
                                                    {e.changes && Object.keys(e.changes).length > 0 ? (
                                                        <pre style={{ margin: 0, fontSize: 10, color: '#1b2a4a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {JSON.stringify(e.changes, null, 2)}
                                                        </pre>
                                                    ) : <span style={{ color: '#7a869a' }}>{e.notes || '—'}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                                <button className="ap-btn ap-btn--primary" style={{ fontSize: 11 }} onClick={() => setAuditLogToolId(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>KRI Matrix</h2>
                    <div style={{ fontSize: 11, color: '#7a869a' }}>Tool Compliance Monitoring -Key Risk Indicators</div>
                </div>
                {tab === 'dashboard' && (
                    <button
                        onClick={async () => {
                            try {
                                const dah = dashData?.distinct_asset_health || null;
                                const allTools = dashData?.tools || [];
                                const overallScore = dah ? dah.score : (dashData?.overall_score || 0);
                                const totalAssets = dashData?.total_assets || (dah ? dah.total : 0);
                                const totalHealthy = dah ? dah.healthy : allTools.reduce((s, t) => s + (t.healthy || 0), 0);
                                const totalUnhealthy = dah ? dah.unhealthy : allTools.reduce((s, t) => s + (t.health_degraded || 0), 0);
                                const totalMissing = dah ? dah.missing : allTools.reduce((s, t) => s + (t.not_installed || t.non_compliant || 0), 0);
                                // Coverage Gaps must match the dashboard tile, which counts
                                // DISTINCT assets missing from at least one asset-shape tool
                                // (dah.missing). The old sum of asset_gaps + ticket_gaps
                                // double-counted (per-record + ticket gaps) and showed ~2×.
                                const totalGaps = dah ? dah.missing
                                    : (typeof gaps.total_asset_gaps === 'number' ? gaps.total_asset_gaps : (gaps.asset_gaps || []).length)
                                    + (typeof gaps.total_ticket_gaps === 'number' ? gaps.total_ticket_gaps : (gaps.ticket_gaps || []).length);
                                const target = dashData?.target || null;
                                const policyTarget = target?.target_compliance_pct || 95;
                                const ragRed = target?.rag_red_threshold || 70;
                                const ragAmber = target?.rag_amber_threshold || 85;
                                const onTarget = overallScore >= policyTarget;
                                const ragColor = overallScore >= ragAmber ? '#0f7b4f' : overallScore >= ragRed ? '#d97706' : '#dc2626';
                                const ragLabel = overallScore >= policyTarget ? `On target (≥${policyTarget}%)` : overallScore >= ragAmber ? 'Below target, in amber band' : overallScore >= ragRed ? 'Amber — investigate' : 'Red — escalate';
                                const trend = Array.isArray(dashData?.trend) ? dashData.trend : [];
                                const trendVals = trend.map(t => Number(t.compliance_pct) || 0);
                                const pc = dashData?.period_comparison || null;
                                const weighted = dashData?.weighted_score;
                                const aging = dashData?.aging || null;
                                const missingSplit = dashData?.missing_split || null;
                                const bu = Array.isArray(dashData?.bu_breakdown) ? dashData.bu_breakdown : [];
                                const critAsset = dashData?.critical_asset_health || null;
                                // Build by-category from tools
                                const byCatMap = {};
                                for (const t of allTools) {
                                    const cat = (t.tool_type || 'other').toUpperCase();
                                    const b = byCatMap[cat] || (byCatMap[cat] = { total: 0, healthy: 0, count: 0 });
                                    b.total += t.total_checked || 0;
                                    b.healthy += (t.healthy ?? t.installed ?? t.compliant) || 0;
                                    b.count += 1;
                                }
                                const byCatRows = Object.entries(byCatMap).map(([cat, b]) => ({ cat, count: b.count, score: b.total ? Math.round(b.healthy / b.total * 1000) / 10 : 0 })).sort((a, b) => b.score - a.score);
                                const fmt = ts => { try { const d = new Date(ts); return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return '—'; } };
                                const fmtAgo = ts => { try { const s = Math.floor((Date.now() - new Date(ts)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; } catch { return '—'; } };
                                const sc = v => v >= ragAmber ? '#0f7b4f' : v >= ragRed ? '#d97706' : '#dc2626';
                                const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

                                const toB64 = url => new Promise(resolve => {
                                    const img = new Image();
                                    img.crossOrigin = 'anonymous';
                                    img.onload = () => {
                                        try {
                                            const c = document.createElement('canvas');
                                            c.width = img.naturalWidth || 100;
                                            c.height = img.naturalHeight || 50;
                                            c.getContext('2d').drawImage(img, 0, 0);
                                            resolve(c.toDataURL('image/png'));
                                        } catch (e) { resolve(null); }
                                    };
                                    img.onerror = () => resolve(null);
                                    img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
                                });
                                const [nmtLogo, liIcon, twIcon, ytIcon, fbIcon, igIcon] = await Promise.all([
                                    toB64('https://mediafile.nmtsecurity.com/nmtweb-security-img/image/emailTemp/nmt-logo.png'),
                                    toB64('https://mediafile.nmtsecurity.com/nmtweb-security-img/image/emailTemp/linkedin.png'),
                                    toB64('https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/twitter.png'),
                                    toB64('https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/youtube.png'),
                                    toB64('https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/facebook.png'),
                                    toB64('https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/instagram.png'),
                                ]);
                                const htmlStr = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>KRI Matrix – ${now}</title><style>
                                    *{margin:0;padding:0;box-sizing:border-box;}
                                    body{font-family:'Inter',Arial,sans-serif;color:#000;background:#fff;font-size:11px;line-height:1.4;}
                                    .main-logo{text-align:center;padding:0;}
                                    .header{background:linear-gradient(90deg,#1a2a6c 0%,#b21f1f 100%);padding:14px 24px;border-radius:0;display:flex;justify-content:space-between;align-items:center;}
                                    .header-title{color:#fff;font-size:18px;font-weight:600;letter-spacing:1px;margin:0;text-align:right;}
                                    .header-date{color:rgba(255,255,255,0.75);font-size:10px;text-align:right;margin-top:3px;}
                                    .pdf-body{padding:12px 24px;}
                                    .kpi-row{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:10px;}
                                    .kpi-tile{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;text-align:center;background:#f8fafc;}
                                    .kpi-tile.highlight{border-color:#1a2a6c;background:#eff6ff;}
                                    .kpi-val{font-size:22px;font-weight:700;line-height:1;}
                                    .kpi-label{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin-top:4px;}
                                    .kpi-sub{font-size:9px;color:#94a3b8;margin-top:2px;}
                                    .tile{background:#fff;border:1px solid #d8dce3;border-radius:8px;padding:10px 12px;page-break-inside:avoid;}
                                    .tile-label{font-size:9px;font-weight:700;color:#7a869a;letter-spacing:0.3px;text-transform:uppercase;margin-bottom:5px;}
                                    .two-col{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;}
                                    table{width:100%;border-collapse:collapse;font-size:10px;}
                                    th{text-align:left;padding:5px 8px;font-size:9px;font-weight:600;color:#7a869a;border-bottom:1px solid #e2e8f0;}
                                    td{padding:5px 8px;border-bottom:1px solid #f1f5f9;vertical-align:middle;}
                                    tr:last-child td{border-bottom:none;}
                                    .badge{display:inline-block;padding:2px 7px;border-radius:3px;font-size:9px;font-weight:600;}
                                    .bs{background:#dcfce7;color:#16a34a;}.bw{background:#fef9c3;color:#ca8a04;}.bd{background:#fee2e2;color:#dc2626;}
                                    .bar-wrap{background:#e2e8f0;border-radius:3px;height:5px;overflow:hidden;display:inline-block;width:60px;vertical-align:middle;margin-right:5px;}
                                    .bar-fill{height:100%;border-radius:3px;}
                                    .nova{background:linear-gradient(135deg,#f5f7ff,#fafbff);border:1px solid #c7d2fe;border-radius:8px;padding:10px 12px;margin-bottom:10px;}
                                    .footer{page-break-inside:avoid;}
                                    .social{margin:10px 0 0 0;text-align:center;border-top:1px solid #F4F4F4;border-bottom:1px solid #F4F4F4;padding:8px 0;}
                                    .social img{width:auto;height:24px;margin:0 4px;vertical-align:middle;}
                                    .bottom-bar{padding:8px 0 0 0;text-align:center;font-size:11px;color:#666;}
                                    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}@page{size:A4;margin:8mm;}.header{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}}
                                </style></head><body>
                                <div class="header">
                                    ${nmtLogo ? `<img src="${nmtLogo}" alt="NMT Security" style="height:28px;width:auto;display:block;" />` : `<span style="color:#fff;font-size:17px;font-weight:700;letter-spacing:1.5px;font-family:Arial,sans-serif;">NMT Security</span>`}
                                    <div><div class="header-title">KRI Matrix Report</div><div class="header-date">${now}</div></div>
                                </div>

                                <div class="pdf-body">
                                    <!-- KPI ROW -->
                                    <div class="kpi-row">
                                        <div class="kpi-tile highlight"><div class="kpi-val" style="color:${ragColor}">${Math.round(overallScore)} %</div><div class="kpi-label">Overall Compliance</div><div class="kpi-sub">${totalHealthy.toLocaleString()} of ${totalAssets.toLocaleString()} assets healthy</div></div>
                                        <div class="kpi-tile"><div class="kpi-val" style="color:#1e3a5f">${totalAssets.toLocaleString()}</div><div class="kpi-label">Total Assets</div><div class="kpi-sub">In baseline</div></div>
                                        <div class="kpi-tile"><div class="kpi-val" style="color:#1e3a5f">${allTools.length}</div><div class="kpi-label">Tools Monitored</div><div class="kpi-sub">Integration${allTools.length !== 1 ? 's' : ''} active</div></div>
                                        <div class="kpi-tile"><div class="kpi-val" style="color:${totalGaps > 0 ? '#dc2626' : '#0f7b4f'}">${totalGaps.toLocaleString()}</div><div class="kpi-label">Coverage Gaps</div><div class="kpi-sub">${totalGaps === 0 ? 'All clear' : 'Require attention'}</div></div>
                                    </div>

                                    <!-- BY DISTINCT ASSET (inline summary) -->
                                    <div class="tile" style="margin-bottom:10px;display:flex;gap:32px;align-items:center;">
                                        <div><div class="tile-label">By Distinct Asset</div></div>
                                        <div style="font-size:10px;color:#0f7b4f">Healthy &nbsp;<strong>${totalHealthy.toLocaleString()}</strong></div>
                                        <div style="font-size:10px;color:#d97706">Unhealthy &nbsp;<strong>${totalUnhealthy.toLocaleString()}</strong></div>
                                        <div style="font-size:10px;color:#dc2626">Missing &nbsp;<strong>${totalMissing.toLocaleString()}</strong></div>
                                        <div style="font-size:10px;color:#64748b">Sums to ${totalAssets.toLocaleString()} assets in baseline.</div>
                                    </div>

                                    <!-- ROW: COMPLIANCE TREND + POLICY TARGET -->
                                    <div class="two-col">
                                        <div class="tile">
                                            <div class="tile-label">Compliance Trend · Last ${trendVals.length}d</div>
                                            ${trendVals.length < 2
                                        ? `<div style="font-size:10px;color:#3d4f6f">No daily snapshots captured yet — the trend chart populates once you have at least 2.</div>`
                                        : `<div style="font-size:10px;color:#3d4f6f;display:flex;justify-content:space-between;margin-bottom:4px;">
                                                    <span>${trend[0]?.snapshot_date}: <strong>${trendVals[0]}%</strong></span>
                                                    <span style="color:${(trendVals[trendVals.length - 1] - trendVals[0]) > 0 ? '#0f7b4f' : '#dc2626'};font-weight:600">${(trendVals[trendVals.length - 1] - trendVals[0]) > 0 ? '▲' : '▼'} ${Math.abs(Math.round((trendVals[trendVals.length - 1] - trendVals[0]) * 10) / 10)} pts</span>
                                                    <span>today: <strong>${trendVals[trendVals.length - 1]}%</strong></span>
                                                </div>`
                                    }
                                            ${pc ? `<div style="font-size:9px;color:#3d4f6f;margin-top:6px;padding-top:6px;border-top:1px dashed #e6e9ee"><strong>VS LAST QUARTER</strong> &nbsp;${pc.today_pct}% today vs ${pc.prior_pct}% on ${pc.prior_date} &nbsp;<span style="font-weight:700;color:${pc.delta_pts > 0 ? '#0f7b4f' : '#dc2626'}">(${pc.delta_pts > 0 ? '+' : ''}${pc.delta_pts} pts)</span></div>` : ''}
                                        </div>
                                        <div class="tile">
                                            <div class="tile-label">Policy Target</div>
                                            <div style="font-size:22px;font-weight:800;color:${ragColor}">${Math.round(overallScore)} %</div>
                                            <div style="font-size:10px;color:#7a869a">vs target ${policyTarget} %</div>
                                            <div style="margin-top:6px"><span class="badge ${onTarget ? 'bs' : 'bd'}">${onTarget ? `✓ On target (≥${policyTarget} %)` : ragLabel}</span></div>
                                            <div style="font-size:9px;color:#7a869a;margin-top:4px">Bands: red &lt;${ragRed} % · amber ${ragRed}–${ragAmber} % · green ≥${ragAmber} %</div>
                                        </div>
                                    </div>

                                    <!-- ROW: RISK-WEIGHTED + AGING -->
                                    <div class="two-col">
                                        ${weighted != null ? `
                                        <div class="tile">
                                            <div class="tile-label">Risk-Weighted Compliance</div>
                                            <div style="font-size:22px;font-weight:800;color:#1b2a4a">${weighted} %</div>
                                            <div style="font-size:10px;color:#3d4f6f;margin-top:4px">${weighted > overallScore ? `${(weighted - overallScore).toFixed(1)} pts higher than unweighted — Critical assets are doing well.` : weighted < overallScore ? `${(overallScore - weighted).toFixed(1)} pts lower — Critical assets are dragging the score.` : 'Matches the unweighted score — no risk-tier skew.'}</div>
                                            <div style="font-size:9px;color:#7a869a;margin-top:4px">Each asset weighted by criticality: Low ×1, Medium ×2, High ×3, Critical ×4.</div>
                                        </div>` : '<div></div>'}
                                        ${aging ? `
                                        <div class="tile">
                                            <div class="tile-label">Aging — Unresolved Coverage Gaps</div>
                                            <table><thead><tr><th>Age</th><th style="text-align:right">Missing</th><th style="text-align:right">Unhealthy</th></tr></thead><tbody>
                                                ${['0-7d', '8-30d', '31-90d', '90d+'].map(b => `<tr><td>${b}</td><td style="text-align:right;color:#dc2626;font-weight:600">${(aging.missing?.[b] ?? 0).toLocaleString()}</td><td style="text-align:right;color:#d97706;font-weight:600">${(aging.unhealthy?.[b] ?? 0).toLocaleString()}</td></tr>`).join('')}
                                            </tbody></table>
                                        </div>` : '<div></div>'}
                                    </div>

                                    <!-- ROW: MISSING SPLIT + BY BU -->
                                    <div class="two-col">
                                        ${missingSplit ? `
                                        <div class="tile">
                                            <div class="tile-label">Missing — Remediation Split</div>
                                            <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px"><span style="font-size:10px;color:#3d4f6f">Tool says <em>not enrolled</em></span><span style="font-size:13px;font-weight:700;color:#dc2626">${(missingSplit.non_compliant ?? 0).toLocaleString()}</span></div>
                                            <div style="font-size:9px;color:#7a869a;margin-bottom:8px">Deploy / configure the agent to fix.</div>
                                            <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px"><span style="font-size:10px;color:#3d4f6f">No tool has any record</span><span style="font-size:13px;font-weight:700;color:#dc2626">${(missingSplit.no_record ?? 0).toLocaleString()}</span></div>
                                            <div style="font-size:9px;color:#7a869a">Expand baseline scope or onboard a tool that covers these.</div>
                                        </div>` : '<div></div>'}
                                        <div class="tile">
                                            <div class="tile-label">By Business Unit</div>
                                            ${bu.length > 0 ? `<table><thead><tr><th>Business Unit</th><th style="text-align:right">Total</th><th style="text-align:right">Healthy</th><th style="text-align:right">Score</th></tr></thead><tbody>
                                                ${bu.slice(0, 8).map(r => `<tr><td style="font-weight:600">${r.business_unit_name || '(unassigned)'}</td><td style="text-align:right">${r.total.toLocaleString()}</td><td style="text-align:right;color:#0f7b4f">${r.healthy.toLocaleString()}</td><td style="text-align:right;color:${sc(r.score)};font-weight:700">${r.score} %</td></tr>`).join('')}
                                            </tbody></table>` : `<div style="font-size:10px;color:#7a869a">No assets have a Business Unit assigned yet.</div>`}
                                        </div>
                                    </div>

                                    <!-- ROW: BY TOOL CATEGORY + TOOL SYNC HEALTH -->
                                    <div class="two-col">
                                        ${byCatRows.length > 0 ? `
                                        <div class="tile">
                                            <div class="tile-label">By Tool Category</div>
                                            <table><thead><tr><th>Category</th><th style="text-align:right">Tools</th><th style="text-align:right">Score</th></tr></thead><tbody>
                                                ${byCatRows.map(r => `<tr><td style="font-weight:600">${r.cat}</td><td style="text-align:right">${r.count}</td><td style="text-align:right;color:${sc(r.score)};font-weight:700">${r.score} %</td></tr>`).join('')}
                                            </tbody></table>
                                            <div style="font-size:9px;color:#7a869a;margin-top:6px">Score = healthy ÷ total per category. Helps spot which security domain is the weakest.</div>
                                        </div>` : '<div></div>'}
                                        ${allTools.length > 0 ? `
                                        <div class="tile">
                                            <div class="tile-label">Tool Sync Health</div>
                                            <table><thead><tr><th>Tool</th><th>Last Sync</th><th>Status</th></tr></thead><tbody>
                                                ${allTools.map(t => { const st = t.last_sync_status || '—'; const badge = st === 'success' ? 'bs' : st === 'running' ? 'bw' : st === 'error' ? 'bd' : ''; return `<tr><td style="font-weight:600">${t.name || '—'}</td><td style="color:#64748b">${fmtAgo(t.last_sync_at)}</td><td>${badge ? `<span class="badge ${badge}">${st}</span>` : `<span style="color:#94a3b8">${st}</span>`}</td></tr>` }).join('')}
                                            </tbody></table>
                                            <div style="font-size:9px;color:#7a869a;margin-top:6px">Auto-syncs run every tool's sync_frequency_hours via the scheduler. Stale &gt;24h = amber, &gt;7d = red.</div>
                                        </div>` : '<div></div>'}
                                    </div>

                                    <!-- NOVA AI EXECUTIVE SUMMARY -->
                                    ${aiDashSummary && !aiDashSummary.error ? `
                                    <div class="nova">
                                        <div style="font-size:10px;font-weight:700;color:#4338ca;letter-spacing:0.5px;margin-bottom:4px">NOVA AI · EXECUTIVE SUMMARY</div>
                                        <div style="font-size:12px;font-weight:700;color:#1b2a4a;margin-bottom:4px">${aiDashSummary.headline || ''}</div>
                                        <div style="font-size:11px;color:#3d4f6f;line-height:1.5">${aiDashSummary.narrative || ''}</div>
                                        ${aiDashSummary.top_action ? `<div style="font-size:10px;color:#4338ca;margin-top:6px;font-weight:600">→ ${aiDashSummary.top_action}</div>` : ''}
                                    </div>` : ''}
                                </div>

                                <div class="footer">
                                <div class="social">
                                    ${[
                                        // Print renders in a real browser window, so we can load the
                                        // icon URLs directly (base64 pre-conversion often failed CORS
                                        // and dropped us to a text fallback). Always show the logo.
                                        [liIcon || 'https://mediafile.nmtsecurity.com/nmtweb-security-img/image/emailTemp/linkedin.png', 'https://www.linkedin.com/company/nmt-security', 'LinkedIn'],
                                        [NMT_SMALL_LOGO, 'https://www.nmtsecurity.com/', 'NMT Security'],
                                        [twIcon || 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/twitter.png', 'https://x.com/NMTSECURITY', 'Twitter'],
                                        [ytIcon || 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/youtube.png', 'https://www.youtube.com/channel/UCSVME80Lcdb1N_R8FNtbwIg', 'YouTube'],
                                        [fbIcon || 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/facebook.png', 'https://www.facebook.com/profile.php?id=61578217021594', 'Facebook'],
                                        [igIcon || 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/instagram.png', 'https://www.instagram.com/nmt_security', 'Instagram'],
                                    ].map(([src, href, label]) => `<a href="${href}" target="_blank" rel="noopener" style="display:inline-block;margin:0 6px;text-decoration:none;"><img src="${src}" alt="${label}" style="width:28px;height:28px;vertical-align:middle;border-radius:50%;"></a>`).join('')}
                                </div>
                                <div class="bottom-bar">
                                    <p>This report was generated on ${now} through the NMT Security platform. For queries, contact psignin <a href="mailto: support@nmtsecurity.com">support@nmtsecurity.com</a>.</p>
                                </div>
                                </div>
                                </body></html>`;
                                // Print to PDF via a real browser window instead of
                                // rasterising with html2canvas. The old canvas path
                                // baked everything into a flat image — links weren't
                                // clickable and text spacing was mangled. A print
                                // window keeps real, selectable text and working links.
                                const w = window.open('', '_blank');
                                if (!w) { flash('Please allow pop-ups to export the report.'); return; }
                                w.document.write(htmlStr);
                                w.document.close();
                                w.focus();
                                // Delay so the base64 logo/social icons paint before printing.
                                setTimeout(() => { w.print(); w.onafterprint = () => w.close(); }, 600);
                            } catch (e) { flash('Export failed: ' + (e?.message || 'unknown')); }
                        }}
                        className="ap-btn ap-btn--ghost kri-no-print"
                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                        title="Export KRI dashboard as PDF">
                        Export PDF
                    </button>
                )}
                {/* Scope filter (BU / Project / Region) was removed
                    2026-05-06: vendor adapters can't populate
                    business_unit_id / project_id / location on imported
                    assets, so the dropdowns produced confusing
                    zero-result dashboards. The mapping rules feature
                    (KRIAssetMappingRule + /asset-mapping-rules/
                    endpoints) is the long-term fix and remains
                    available via API; once the admin UI ships, the
                    scope row can be re-introduced with full coverage.
                    Backend `_scope_params` / `_filter_assets_qs` are
                    no-ops when no params arrive, so removing the UI
                    does NOT change semantics for existing data. */}
            </div>

            {/* Persistent sync banner — shows whenever any tool is mid-sync.
                Backend marks tool.last_sync_status='running' on async kick-off, so
                we just filter the tools list. Refreshes every time loadTools()
                runs, so the banner clears within ~5 sec of completion. */}
            {(() => {
                const inFlight = (tools || []).filter(
                    t => t.last_sync_status === 'running' || syncing === t.id
                );
                if (!inFlight.length) return null;
                // Cancel button. Confirms once, then sets a Redis flag
                // the sync loop checks every ~1000 records. The sync
                // honours the request at the next checkpoint and flips
                // its KRISyncLog row to `aborted` (not `error`). After
                // confirmation we trigger loadTools so the banner
                // clears as soon as the backend reflects the abort.
                const cancelInFlightSync = async (tool) => {
                    const ok = await confirmDialog({
                        title: 'Cancel sync?',
                        message: `Stop ${tool.name} mid-sync? Any compliance records already written this run will be kept; the rest will be skipped. The Sync Logs row will show "aborted" with the cancel reason.`,
                        confirmLabel: 'Cancel sync', cancelLabel: 'Keep running',
                        danger: true,
                    });
                    if (!ok) return;
                    try {
                        const r = await svc('kri_cancel_sync', tool.id);
                        const d = r?.response_data?.data || {};
                        if (d.nothing_running) {
                            flash('No sync in progress for this tool — banner will clear on next refresh.');
                        } else {
                            flash(`Cancellation requested for ${tool.name}. Will stop at the next checkpoint.`);
                        }
                        // Refresh tools so the banner clears (or, if
                        // the sync hasn't honoured the request yet,
                        // shows the running state until it does — the
                        // 30s polling timer picks it up either way).
                        loadTools();
                    } catch (e) {
                        _showLoadError(`cancel sync for ${tool.name}`, e);
                    }
                };
                return (
                    <div style={{
                        marginBottom: 12, padding: '10px 14px',
                        background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)',
                        border: '1px solid #c7d2fe', borderRadius: 8,
                        display: 'flex', alignItems: 'center', gap: 10
                    }}>
                        <span style={{
                            display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                            background: '#7c3aed',
                            animation: 'kri-pulse 1.5s ease-in-out infinite'
                        }} />
                        <div style={{ flex: 1, fontSize: 12, color: '#1b2a4a' }}>
                            <strong>Sync in progress</strong>
                            {' — '}
                            {inFlight.length === 1
                                ? `${inFlight[0].name} is fetching data from ${inFlight[0].vendor}.`
                                : `${inFlight.length} tools are syncing.`}
                            {' '}Feel free to navigate around — the dashboard will update automatically when sync completes.
                        </div>
                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                            onClick={() => handleTabChange('logs')}>View progress</button>
                        {inFlight.length === 1 && (
                            <button
                                className="ap-btn ap-btn--ghost"
                                style={{ fontSize: 11, color: '#dc2626', borderColor: '#dc2626' }}
                                onClick={() => cancelInFlightSync(inFlight[0])}
                            >Cancel sync</button>
                        )}
                    </div>
                );
            })()}

            {/* Tabs. The "Unregistered Devices" tab carries a count
                badge showing how many devices the integrated tools
                report that are NOT yet in the baseline — drives the
                user toward fixing the most common cause of "the
                dashboard says 0 agents but I see one in the vendor
                console". The total comes from the cached `tools`
                list's additional_config snapshot so the badge is
                accurate without an extra round-trip. */}
            <div className="kri-no-print" style={{ display: 'flex', gap: 0, borderBottom: '1px solid #d8dce3', marginBottom: 16 }}>
                {(() => {
                    const totalUnmatched = (tools || []).reduce((sum, t) => {
                        const snap = (t.additional_config || {}).last_sync_unmatched_tool_assets || {};
                        return sum + (snap.count || 0);
                    }, 0);
                    return [
                        ['dashboard', 'Dashboard', null],
                        ['library', 'KRI Library', null],
                        ['tools', 'Tool Integrations', null],
                        ['assets', 'Asset Baseline', null],
                        ['mapping', 'Mapping Rules', null],
                        ['matrix', 'Coverage Matrix', null],
                        ['gaps', 'Gap Report', null],
                        ['unmatched', 'Unregistered Devices', totalUnmatched],
                        ['logs', 'Sync Logs', null],
                        ['settings', 'Settings', null],
                    ].map(([id, label, badge]) => (
                        <button key={id} style={tabStyle(id)} onClick={() => handleTabChange(id)}>
                            {label}
                            {badge ? (
                                <span style={{
                                    marginLeft: 6, fontSize: 9, padding: '1px 6px', borderRadius: 8,
                                    background: '#dc2626', color: '#fff', fontWeight: 700,
                                }}>{badge}</span>
                            ) : null}
                        </button>
                    ));
                })()}
            </div>
            {/* Pulse keyframe + tab-refresh styling for the sync banner. */}
            <style>{`
                @keyframes kri-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: 0.5; transform: scale(0.9); }
                }
                .kri-tab-content { transition: opacity 180ms ease-in-out; }
                .kri-tab-content.kri-tab-refreshing { opacity: 0.55; pointer-events: none; }
                .kri-table-sticky thead th { position: sticky; top: 0; background: #f5f7fa; z-index: 2; box-shadow: 0 1px 0 #d8dce3; }
                /* Mobile-safe modal cap. Older modals (Add Tool, Add Asset,
                   Risk-create) set fixed maxWidth in inline styles; this
                   global cap ensures none of them overflow on tablet/phone
                   viewports. 16px gutter on each side reads naturally. */
                .ap-modal { max-width: min(var(--ap-modal-max, 760px), 92vw) !important; }
                /* Tables on narrow viewports — horizontal scroll instead of
                   squeezing column widths to illegible. */
                @media (max-width: 768px) {
                    .ap-table { display: block; overflow-x: auto; }
                }

            `}</style>

            {/* DASHBOARD */}
            {tab === 'dashboard' && dashData && (() => {
                const allTools = dashData.tools || [];
                // Per-asset distinct counts (preferred — added 2026-05-06).
                // Backend bucket each baseline asset into healthy / unhealthy
                // / missing using worst-state-wins across asset-shape tools,
                // so the three numbers sum to total_assets exactly. Falls
                // back to the per-record sums if an older backend isn't
                // emitting the field yet.
                const dah = dashData.distinct_asset_health || null;
                const totalHealthy = dah ? dah.healthy : allTools.reduce((s, t) => s + (t.healthy || 0), 0);
                const totalUnhealthy = dah ? dah.unhealthy : allTools.reduce((s, t) => s + (t.health_degraded || 0), 0);
                const totalMissing = dah ? dah.missing : allTools.reduce((s, t) => s + (t.not_installed || t.non_compliant || 0), 0);
                // The compliance gauge follows the same shift: when distinct
                // counts are available, score is `healthy / total_assets`
                // (fraction of devices fully healthy), not the per-record
                // weighted average.
                const overallScore = dah ? dah.score : dashData.overall_score;
                const donutDenominator = dah ? dah.total : null;
                const selTool = selectedToolId !== 'all' ? allTools.find(t => String(t.id) === String(selectedToolId)) : null;
                const allAssetGaps = gaps.asset_gaps || [];
                const allTicketGaps = gaps.ticket_gaps || [];
                // The backend truncates `asset_gaps` and `ticket_gaps` to
                // 500 entries each for response-size reasons (gap_report
                // endpoint, kri_views.py around line 8225). Using the
                // array length here makes the Coverage Gaps tile show
                // 500 even on tenants with thousands of real gaps —
                // misleading. The backend separately returns
                // `total_asset_gaps` / `total_ticket_gaps` with the
                // unbounded counts; prefer those, falling back to array
                // length only if the older response shape lacks them.
                const totalGaps = (
                    (typeof gaps.total_asset_gaps === 'number' ? gaps.total_asset_gaps : allAssetGaps.length)
                    + (typeof gaps.total_ticket_gaps === 'number' ? gaps.total_ticket_gaps : allTicketGaps.length)
                );
                const selGaps = selTool ? (matrixData?.rows?.filter(r => r.tools?.[selTool.id]?.status === 'non_compliant') || []) : allAssetGaps;
                const selUnhealthy = selTool ? (matrixData?.rows?.filter(r => { const rec = r.tools?.[selTool.id]; return rec?.status === 'compliant' && ['offline', 'disconnected', 'degraded', 'inactive', 'unknown'].includes(rec?.agent_status); }) || []) : [];

                return (
                    <div>
                        {allTools.length > 0 ? (<>
                            {/* Row 1: Gauge + Summary cards + Overall donut.
                            All three count by DISTINCT ASSET (each device
                            counted once), not by per-record sum across
                            tools — see distinct_asset_health on the
                            backend payload. The gauge percentage and the
                            donut tile-counts now reconcile with Total
                            Assets exactly. */}
                            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '16px 24px', textAlign: 'center', minWidth: 180 }}>
                                    <GaugeChart value={overallScore} threshold={95} criticalThreshold={80} size={170} />
                                    {dah && (
                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4 }}>
                                            {dah.healthy.toLocaleString()} of {dah.total.toLocaleString()} assets fully healthy
                                        </div>
                                    )}
                                </div>
                                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                                    {[
                                        { label: 'Total Assets', value: dashData.total_assets, color: '#1b2a4a', sub: 'In baseline', go: () => handleTabChange('assets') },
                                        { label: 'Tools Monitored', value: dashData.total_tools, color: '#1b2a4a', sub: 'Integrations', go: () => handleTabChange('tools') },
                                        {
                                            // Coverage Gaps now means "distinct assets missing
                                            // from at least one asset-shape tool" — same scale as
                                            // the donut's Missing slice. Falls back to the per-record
                                            // gap count if the new field isn't on the response.
                                            label: 'Coverage Gaps',
                                            value: dah ? dah.missing : totalGaps,
                                            color: (dah ? dah.missing : totalGaps) > 0 ? '#dc2626' : '#0f7b4f',
                                            sub: (dah ? dah.missing : totalGaps) > 0 ? 'Click to view details' : 'All clear',
                                            go: () => handleTabChange('gaps'),
                                        },
                                    ].map((s, i) => (
                                        <div key={i} role="button" tabIndex={0} title={`View ${s.label}`}
                                            onClick={s.go}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') s.go(); }}
                                            style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(27,42,74,0.08)'; e.currentTarget.style.borderColor = '#1b2a4a'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#d8dce3'; }}>
                                            <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1.1, textDecoration: 'underline', textDecorationColor: 'transparent', textUnderlineOffset: 3 }}>{s.value}</div>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', marginTop: 3 }}>{s.label}</div>
                                            <div style={{ fontSize: 9, color: '#7a869a' }}>{s.sub}</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, minWidth: 260 }}>
                                    {/* <DonutChart size={90} thickness={14} data={[
                                        { label: 'Healthy', value: totalHealthy, color: '#0f7b4f' },
                                        { label: 'Unhealthy', value: totalUnhealthy, color: '#d97706' },
                                        { label: 'Missing', value: totalMissing, color: '#dc2626' },
                                    ]} /> */}
                                    <div style={{ flex: 1 }}>
                                        {dah && (
                                            <div style={{ fontSize: 9, color: '#7a869a', marginBottom: 4, fontWeight: 600, letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span>BY DISTINCT ASSET</span>
                                                <button
                                                    onClick={() => setDashGlossaryOpen(true)}
                                                    aria-label="What do these buckets mean?"
                                                    title="What do Healthy / Unhealthy / Missing mean?"
                                                    style={{ width: 14, height: 14, borderRadius: '50%', border: '1px solid #c1c7d0', background: 'transparent', color: '#7a869a', fontSize: 9, fontWeight: 700, lineHeight: 1, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    i
                                                </button>
                                            </div>
                                        )}
                                        {[
                                            { label: 'Healthy', value: totalHealthy, color: '#0f7b4f', go: () => { setMatrixFilter('compliant'); handleTabChange('matrix'); } },
                                            { label: 'Unhealthy', value: totalUnhealthy, color: '#d97706', go: () => { setMatrixFilter(''); handleTabChange('matrix'); } },
                                            { label: 'Missing', value: totalMissing, color: '#dc2626', go: () => handleTabChange('gaps') },
                                        ].map((s, i) => (
                                            <div key={i} role="button" tabIndex={0} title={`View ${s.label} assets`}
                                                onClick={s.go}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') s.go(); }}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, padding: '2px 6px', borderRadius: 4, cursor: 'pointer' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                                                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }}></span>
                                                <span style={{ fontSize: 10, color: '#3d4f6f' }}>{s.label}</span>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: s.color, marginLeft: 'auto', textDecoration: 'underline', textUnderlineOffset: 2 }}>{s.value}</span>
                                            </div>
                                        ))}
                                        {dah && (
                                            <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4, paddingTop: 4, borderTop: '1px dashed #e6e9ee' }}>
                                                Sums to {donutDenominator.toLocaleString()} assets in baseline.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Enterprise tiles — trend / target / weighted / BU / aging / missing-split.
                            Each is independently null-safe; an older backend that doesn't return
                            these fields just renders the existing dashboard with no new tiles. */}
                            {(() => {
                                const target = dashData?.target || null;
                                const trend = Array.isArray(dashData?.trend) ? dashData.trend : [];
                                const pc = dashData?.period_comparison || null;
                                const weighted = dashData?.weighted_score;
                                const bu = Array.isArray(dashData?.bu_breakdown) ? dashData.bu_breakdown : [];
                                const aging = dashData?.aging || null;
                                const missingSplit = dashData?.missing_split || null;
                                const inRollout = Array.isArray(dashData?.tools_in_rollout) ? dashData.tools_in_rollout : [];
                                const score = dah?.score ?? dashData?.overall_score ?? 0;
                                // RAG colour for the gauge / target indicator
                                const ragColor = !target ? '#7a869a'
                                    : score >= target.rag_amber_threshold ? '#0f7b4f'
                                        : score >= target.rag_red_threshold ? '#d97706' : '#dc2626';
                                const ragLabel = !target ? '—'
                                    : score >= target.target_compliance_pct ? `On target (≥${target.target_compliance_pct}%)`
                                        : score >= target.rag_amber_threshold ? 'Below target, in amber band'
                                            : score >= target.rag_red_threshold ? 'Amber — investigate'
                                                : 'Red — escalate';
                                // Render-only sparkline: render trend.compliance_pct as a tiny SVG.
                                // Skip if fewer than 2 points (can't draw a line).
                                const sparkline = (vals, color, w = 220, h = 40) => {
                                    if (!vals || vals.length < 2) return null;
                                    const min = Math.min(...vals);
                                    const max = Math.max(...vals);
                                    const span = Math.max(1, max - min);
                                    const stepX = w / (vals.length - 1);
                                    const pts = vals.map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(' ');
                                    return (
                                        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
                                            <polyline fill="none" stroke={color} strokeWidth={1.5} points={pts} />
                                        </svg>
                                    );
                                };
                                const trendVals = trend.map(t => Number(t.compliance_pct) || 0);
                                const trendStart = trendVals.length ? trendVals[0] : null;
                                const trendEnd = trendVals.length ? trendVals[trendVals.length - 1] : null;
                                const trendDelta = (trendStart != null && trendEnd != null) ? Math.round((trendEnd - trendStart) * 10) / 10 : null;

                                return (
                                    <>
                                        {inRollout.length > 0 && (
                                            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, fontSize: 11, color: '#9a3412' }}>
                                                <strong>{inRollout.length} tool{inRollout.length !== 1 ? 's' : ''} in rollout grace</strong> — their records are excluded from the org-wide rollup until grace expires.
                                                <span style={{ marginLeft: 6, color: '#7a869a' }}>
                                                    ({inRollout.map(t => t.name).join(', ')})
                                                </span>
                                            </div>
                                        )}

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
                                            {/* Tile 2 — Target / RAG */}
                                            <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>POLICY TARGET</div>
                                                {target ? (
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                                            <span style={{ fontSize: 24, fontWeight: 800, color: ragColor }}>{score}%</span>
                                                            <span style={{ fontSize: 11, color: '#7a869a' }}>vs target {target.target_compliance_pct}%</span>
                                                        </div>
                                                        <div style={{ marginTop: 6, height: 8, background: '#f0f2f5', borderRadius: 4, position: 'relative' }}>
                                                            <div style={{ position: 'absolute', left: 0, top: 0, height: 8, width: `${Math.min(100, score)}%`, background: ragColor, borderRadius: 4 }}></div>
                                                            <div title={`Target: ${target.target_compliance_pct}%`} style={{ position: 'absolute', left: `${target.target_compliance_pct}%`, top: -2, height: 12, width: 2, background: '#1b2a4a' }}></div>
                                                        </div>
                                                        <div style={{ fontSize: 11, color: ragColor, marginTop: 6, fontWeight: 600 }}>{ragLabel}</div>
                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4 }}>
                                                            Bands: red &lt;{target.rag_red_threshold}% · amber {target.rag_red_threshold}–{target.rag_amber_threshold}% · green ≥{target.rag_amber_threshold}%
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 8 }}>
                                                            No compliance target set yet — pick the SOC 2 / NIST CSF / internal SLA bar your org is held to and the gauge will colour itself accordingly.
                                                        </div>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                            onClick={() => handleTabChange('settings')}>
                                                            Configure in Settings →
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tile 3 — Risk-weighted score. Hide entirely when the field
                                            is missing (older backend) — a tech-debt placeholder doesn't
                                            belong on a CISO view. The grid auto-fits, so a hidden tile
                                            doesn't leave a gap. */}
                                            {weighted != null && (
                                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>RISK-WEIGHTED COMPLIANCE</div>
                                                    <div style={{ fontSize: 24, fontWeight: 800, color: '#1b2a4a' }}>{weighted}%</div>
                                                    <div style={{ fontSize: 11, color: '#3d4f6f', marginTop: 4 }}>
                                                        {weighted > score
                                                            ? `${(weighted - score).toFixed(1)} pts higher than unweighted — your Critical assets are doing well.`
                                                            : weighted < score
                                                                ? `${(score - weighted).toFixed(1)} pts lower than unweighted — Critical assets are dragging the score.`
                                                                : 'Matches the unweighted score — no risk-tier skew.'}
                                                    </div>
                                                    <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4 }}>Each asset weighted by criticality: Low ×1, Medium ×2, High ×3, Critical ×4.</div>
                                                </div>
                                            )}

                                            {/* Tile 4 — Aging on Missing + Unhealthy. Hide when not available. */}
                                            {aging && (
                                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>AGING — UNRESOLVED COVERAGE GAPS</div>
                                                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr style={{ color: '#7a869a' }}>
                                                                <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 4px' }}>Age</th>
                                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Missing</th>
                                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Unhealthy</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {['0-7d', '8-30d', '31-90d', '90d+'].map(b => (
                                                                <tr key={b}>
                                                                    <td style={{ padding: '2px 4px' }}>{b}</td>
                                                                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#dc2626', fontWeight: 600 }}>{(aging.missing?.[b] ?? 0).toLocaleString()}</td>
                                                                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#d97706', fontWeight: 600 }}>{(aging.unhealthy?.[b] ?? 0).toLocaleString()}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {/* Tile 5 — Missing split (deploy vs scope). Hide when not available. */}
                                            {missingSplit && (
                                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>MISSING — REMEDIATION SPLIT</div>
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                                            <span style={{ fontSize: 11, color: '#3d4f6f' }}>Tool says <em>not enrolled</em></span>
                                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>{(missingSplit.non_compliant ?? 0).toLocaleString()}</span>
                                                        </div>
                                                        <div style={{ fontSize: 9, color: '#7a869a', marginBottom: 8 }}>Deploy / configure the agent to fix.</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                            <span style={{ fontSize: 11, color: '#3d4f6f' }}>No tool has any record</span>
                                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>{(missingSplit.no_record ?? 0).toLocaleString()}</span>
                                                        </div>
                                                        <div style={{ fontSize: 9, color: '#7a869a' }}>Expand baseline scope or onboard a tool that covers these.</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Tile 6 — BU breakdown (compact, top 5) */}
                                            <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px', gridColumn: bu.length > 4 ? 'span 2' : 'auto' }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>BY BUSINESS UNIT</div>
                                                {bu.length > 0 ? (
                                                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr style={{ color: '#7a869a' }}>
                                                                <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 4px' }}>Business Unit</th>
                                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Total</th>
                                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Healthy</th>
                                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Score</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {bu.slice(0, 8).map(r => {
                                                                const rowColor = !target ? '#3d4f6f'
                                                                    : r.score >= target.rag_amber_threshold ? '#0f7b4f'
                                                                        : r.score >= target.rag_red_threshold ? '#d97706' : '#dc2626';
                                                                return (
                                                                    <tr key={r.business_unit_id || 'none'}>
                                                                        <td style={{ padding: '2px 4px', color: '#1b2a4a', fontWeight: 600 }}>{r.business_unit_name || '(unassigned)'}</td>
                                                                        <td style={{ textAlign: 'right', padding: '2px 4px' }}>{r.total.toLocaleString()}</td>
                                                                        <td style={{ textAlign: 'right', padding: '2px 4px', color: '#0f7b4f' }}>{r.healthy.toLocaleString()}</td>
                                                                        <td style={{ textAlign: 'right', padding: '2px 4px', color: rowColor, fontWeight: 700 }}>{r.score}%</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div>
                                                        <div style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 8 }}>
                                                            No assets have a Business Unit assigned yet. Vendor adapters can't infer BU — operators map them via hostname / CIDR rules.
                                                        </div>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                            onClick={() => handleTabChange('mapping')}>
                                                            Create a Mapping Rule →
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tile 7 — By Tool Category. Aggregates `dashData.tools`
                                            client-side by `tool_type` so the CISO can see
                                            "EDR 92.6% · MDM 62.3% · ITSM 88.1%" without
                                            expanding the operational detail row. Pure derived
                                            data — no new payload field needed. */}
                                            {(() => {
                                                const tools = Array.isArray(dashData?.tools) ? dashData.tools : [];
                                                if (tools.length === 0) return null;
                                                const byCat = {};
                                                for (const t of tools) {
                                                    const cat = (t.tool_type || 'other').toUpperCase();
                                                    const b = byCat[cat] || (byCat[cat] = { total: 0, healthy: 0, count: 0 });
                                                    b.total += t.total_checked || 0;
                                                    b.healthy += (t.healthy ?? t.installed ?? t.compliant) || 0;
                                                    b.count += 1;
                                                }
                                                const rows = Object.entries(byCat)
                                                    .map(([cat, b]) => ({
                                                        cat, count: b.count, total: b.total, healthy: b.healthy,
                                                        score: b.total ? Math.round(b.healthy / b.total * 1000) / 10 : 0,
                                                    }))
                                                    .sort((a, b) => -a.total);
                                                return (
                                                    <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>BY TOOL CATEGORY</div>
                                                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ color: '#7a869a' }}>
                                                                    <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 4px' }}>Category</th>
                                                                    <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Tools</th>
                                                                    <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Score</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {rows.map(r => {
                                                                    const rowColor = !target ? '#3d4f6f'
                                                                        : r.score >= target.rag_amber_threshold ? '#0f7b4f'
                                                                            : r.score >= target.rag_red_threshold ? '#d97706' : '#dc2626';
                                                                    return (
                                                                        <tr key={r.cat}>
                                                                            <td style={{ padding: '2px 4px', color: '#1b2a4a', fontWeight: 600 }}>{r.cat}</td>
                                                                            <td style={{ textAlign: 'right', padding: '2px 4px' }}>{r.count}</td>
                                                                            <td style={{ textAlign: 'right', padding: '2px 4px', color: rowColor, fontWeight: 700 }}>{r.score}%</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6 }}>
                                                            Score = healthy ÷ total per category. Helps spot which security domain (EDR vs MDM vs ITSM) is the weakest.
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Tile 8 — Critical Asset Coverage. Filters to assets
                                            with criticality=4 (Critical) — the crown jewels. CISOs
                                            care more about these than the average laptop. */}
                                            {(() => {
                                                const cah = dashData?.critical_asset_health;
                                                if (!cah || cah.total === 0) return null;
                                                const ragColorCrit = !target ? '#7a869a'
                                                    : cah.score >= target.rag_amber_threshold ? '#0f7b4f'
                                                        : cah.score >= target.rag_red_threshold ? '#d97706' : '#dc2626';
                                                return (
                                                    <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>CRITICAL-ASSET COVERAGE</div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                                            <span style={{ fontSize: 24, fontWeight: 800, color: ragColorCrit }}>{cah.score}%</span>
                                                            <span style={{ fontSize: 11, color: '#7a869a' }}>{cah.healthy.toLocaleString()} of {cah.total.toLocaleString()} fully healthy</span>
                                                        </div>
                                                        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                                            <span style={{ color: '#0f7b4f' }}>● Healthy <strong>{cah.healthy.toLocaleString()}</strong></span>
                                                            <span style={{ color: '#d97706' }}>● Unhealthy <strong>{cah.unhealthy.toLocaleString()}</strong></span>
                                                            <span style={{ color: '#dc2626' }}>● Missing <strong>{cah.missing.toLocaleString()}</strong></span>
                                                        </div>
                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6 }}>
                                                            Crown-jewel assets only — Domain Controllers, payment systems, etc. Tag a Critical (criticality=4) via the Asset Baseline tab or a Mapping Rule.
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Tile 10 — Tool Sync Health. Last sync per tool with a
                                            freshness indicator. Operators want to spot a stuck
                                            integration before a customer notices stale data. The
                                            scheduler (§49) auto-syncs every `sync_frequency_hours`
                                            but a misconfigured tool / vendor outage can still
                                            produce a stale sync — this tile surfaces that. */}
                                            {(() => {
                                                const tools = Array.isArray(dashData?.tools) ? dashData.tools : [];
                                                if (tools.length === 0) return null;
                                                const now = Date.now();
                                                const fmt = (ts) => {
                                                    if (!ts) return { label: 'never', color: '#dc2626' };
                                                    const age = now - new Date(ts).getTime();
                                                    const hrs = age / 3600000;
                                                    if (hrs < 1) return { label: `${Math.round(age / 60000)} min ago`, color: '#0f7b4f' };
                                                    if (hrs < 24) return { label: `${Math.round(hrs)} h ago`, color: '#0f7b4f' };
                                                    if (hrs < 24 * 7) return { label: `${Math.round(hrs / 24)} d ago`, color: '#d97706' };
                                                    return { label: `${Math.round(hrs / 24)} d ago`, color: '#dc2626' };
                                                };
                                                return (
                                                    <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, padding: '12px 14px' }}>
                                                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 6 }}>TOOL SYNC HEALTH</div>
                                                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ color: '#7a869a' }}>
                                                                    <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 4px' }}>Tool</th>
                                                                    <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Last sync</th>
                                                                    <th style={{ textAlign: 'right', fontWeight: 600, padding: '2px 4px' }}>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tools.map(t => {
                                                                    const f = fmt(t.last_sync);
                                                                    const statusColor = t.sync_status === 'success' ? '#0f7b4f'
                                                                        : t.sync_status === 'error' || t.sync_status === 'failed' ? '#dc2626'
                                                                            : t.sync_status === 'running' ? '#4338ca' : '#7a869a';
                                                                    return (
                                                                        <tr key={t.id}>
                                                                            <td style={{ padding: '2px 4px', color: '#1b2a4a', fontWeight: 600 }}>{t.name}</td>
                                                                            <td style={{ textAlign: 'right', padding: '2px 4px', color: f.color }}>{f.label}</td>
                                                                            <td style={{ textAlign: 'right', padding: '2px 4px', color: statusColor, textTransform: 'capitalize' }}>{t.sync_status || '—'}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6 }}>
                                                            Auto-syncs run every tool's <code style={{ background: '#f0f2f5', padding: '0 3px', borderRadius: 2 }}>sync_frequency_hours</code> via the scheduler (§49). Stale &gt; 24h = amber, &gt; 7d = red.
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </>
                                );
                            })()}

                            {/* NOVA AI executive summary — lazy-loaded narrative for the score */}
                            <div style={{ marginBottom: 16, background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', border: '1px solid #c7d2fe', borderRadius: 8, padding: '12px 16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', letterSpacing: 0.5, marginBottom: 4 }}>NOVA AI &middot; EXECUTIVE SUMMARY</div>
                                        {aiDashLoading && <div style={{ fontSize: 12, color: '#3d4f6f' }}>Analysing your KRI data...</div>}
                                        {!aiDashLoading && !aiDashSummary && (
                                            <div style={{ fontSize: 12, color: '#3d4f6f' }}>Get a plain-English read of where your coverage stands and what to act on next.</div>
                                        )}
                                        {!aiDashLoading && aiDashSummary?.error && (
                                            <div style={{ fontSize: 12, color: '#dc2626' }}>{aiDashSummary.error}</div>
                                        )}
                                        {!aiDashLoading && aiDashSummary && !aiDashSummary.error && (
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>{aiDashSummary.headline}</div>
                                                <div style={{ fontSize: 12, color: '#3d4f6f', lineHeight: 1.5 }}>{aiDashSummary.narrative}</div>
                                                {aiDashSummary.top_action && (
                                                    <div style={{ fontSize: 11, color: '#4338ca', marginTop: 6, fontWeight: 600 }}>&rarr; {aiDashSummary.top_action}</div>
                                                )}
                                                {aiDashSummary.confidence === 'heuristic' && (
                                                    <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4, fontStyle: 'italic' }}>{aiDashSummary.note || 'NOVA AI is temporarily unavailable. Showing deterministic summary.'}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <button className="ap-btn ap-btn--ghost" onClick={runAiDashSummary} disabled={aiDashLoading}
                                        style={{ fontSize: 11, whiteSpace: 'nowrap', borderColor: '#4338ca', color: '#4338ca' }}>
                                        {aiDashSummary ? 'Re-run' : 'Run'}
                                    </button>
                                </div>
                            </div>

                            {/* Row 2: Operational detail — per-tool tiles. Collapsed by
                            default so the executive view is uncluttered; click the
                            disclosure to expand for per-tool drill-down. Force the
                            selection back to 'all' on collapse so the right-side detail
                            block (line ~2776) doesn't keep rendering a stale per-tool view
                            that the user can no longer reach via the tile row. */}
                            <div style={{ marginBottom: 16 }} className="kri-no-print">
                                <button
                                    onClick={() => {
                                        if (toolsExpanded) setSelectedToolId('all');
                                        setToolsExpanded(v => !v);
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: '#7a869a', letterSpacing: 0.4, background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                                    <span style={{ display: 'inline-block', transform: toolsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms', fontSize: 10 }}>▶</span>
                                    OPERATIONAL DETAIL · PER-TOOL ({allTools.length})
                                </button>
                                {toolsExpanded && (
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, overflowX: 'auto', paddingBottom: 4 }}>
                                        <div role="button" tabIndex={0}
                                            onClick={() => setSelectedToolId('all')}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedToolId('all'); } }}
                                            style={{
                                                padding: '10px 16px', borderRadius: 6, cursor: 'pointer', flexShrink: 0, minWidth: 100, textAlign: 'center',
                                                border: `2px solid ${selectedToolId === 'all' ? '#1b2a4a' : '#d8dce3'}`, background: selectedToolId === 'all' ? '#1b2a4a08' : '#fff'
                                            }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: '#1b2a4a' }}>All</div>
                                            <div style={{ fontSize: 9, color: '#7a869a' }}>{allTools.length} tools</div>
                                        </div>
                                        {allTools.map(t => (
                                            <div key={t.id} role="button" tabIndex={0}
                                                onClick={() => setSelectedToolId(String(t.id))}
                                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedToolId(String(t.id)); } }}
                                                aria-label={`Select tool ${t.name}, score ${t.score} percent`}
                                                style={{
                                                    padding: '10px 16px', borderRadius: 6, cursor: 'pointer', flexShrink: 0, minWidth: 120, textAlign: 'center',
                                                    border: `2px solid ${String(selectedToolId) === String(t.id) ? scoreColor(t.score, t.threshold, t.critical_threshold) : '#d8dce3'}`,
                                                    background: String(selectedToolId) === String(t.id) ? scoreColor(t.score, t.threshold, t.critical_threshold) + '08' : '#fff'
                                                }}>
                                                <div style={{ fontSize: 16, fontWeight: 800, color: scoreColor(t.score, t.threshold, t.critical_threshold) }}>{t.score}%</div>
                                                <div style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a' }}>{t.name}</div>
                                                <div style={{ fontSize: 9, color: '#7a869a' }}>{(t.healthy ?? t.installed ?? t.compliant) || 0}/{t.total_checked} {(t.unit_label && t.unit_label.plural) || 'assets'}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Row 3: Selected tool detail OR all-tools summary. The whole
                            block is gated on `toolsExpanded` — collapsed view shows
                            neither (executive view stays clean). */}
                            {toolsExpanded && (selTool ? (() => {
                                const labels = getDashboardLabels(selTool);
                                return (
                                    <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, overflow: 'hidden' }}>
                                        {/* Tool header */}
                                        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f2f5', background: '#fafbfc' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: '#1b2a4a' }}>{selTool.name}</div>
                                                <span style={{
                                                    fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
                                                    color: selTool.status === 'healthy' ? '#0f7b4f' : selTool.status === 'warning' ? '#d97706' : '#dc2626',
                                                    background: (selTool.status === 'healthy' ? '#0f7b4f' : selTool.status === 'warning' ? '#d97706' : '#dc2626') + '12'
                                                }}>
                                                    {selTool.score}% {labels.score}
                                                </span>
                                                {selTool.has_label_overrides && (
                                                    <span title="Custom labels applied" style={{ fontSize: 9, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>custom</span>
                                                )}
                                                <button type="button" title="Customise dashboard labels"
                                                    onClick={() => openLabelEditor(selTool)}
                                                    style={{
                                                        background: 'transparent', border: '1px solid #d8dce3', cursor: 'pointer',
                                                        width: 22, height: 22, borderRadius: 4, padding: 0,
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#7a869a'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#4338ca'; e.currentTarget.style.color = '#4338ca'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d8dce3'; e.currentTarget.style.color = '#7a869a'; }}>
                                                    <span style={{ fontSize: 11 }}>&#9998;</span>
                                                </button>
                                                {/* NOVA AI: explain why the numbers are what they are.
                                            Useful when a customer expects N reopens but sees 0,
                                            or when an Unclassified count appears unexpectedly. */}
                                                <button type="button" title="NOVA AI · explain why these numbers are what they are"
                                                    onClick={() => openExplainUpload(selTool)}
                                                    style={{
                                                        background: 'transparent', border: '1px solid #c7d2fe', cursor: 'pointer',
                                                        padding: '2px 8px', borderRadius: 4, color: '#4338ca',
                                                        fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4
                                                    }}>
                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 2, background: '#7c3aed', color: '#fff' }}>NOVA AI</span>
                                                    Why these numbers?
                                                </button>
                                                {/* Bucket glossary — explains what Installed / Healthy /
                                            Unhealthy / Missing mean for this tool's category, and
                                            how the score is computed. Opens a modal with the
                                            current tool's labels mapped to the universal 4-bucket
                                            definitions, plus the score formula. */}
                                                <button type="button" title="What do these labels mean?"
                                                    onClick={() => setGlossaryToolId(selTool.id)}
                                                    style={{
                                                        background: 'transparent', border: '1px solid #d8dce3', cursor: 'pointer',
                                                        width: 22, height: 22, borderRadius: '50%', padding: 0,
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        color: '#7a869a', fontSize: 11, fontWeight: 700
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1b2a4a'; e.currentTarget.style.color = '#1b2a4a'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d8dce3'; e.currentTarget.style.color = '#7a869a'; }}>
                                                    i
                                                </button>
                                                <button type="button" title="Tool change history (audit log)"
                                                    className="ap-btn ap-btn--ghost"
                                                    style={{ fontSize: 10, padding: '3px 10px', marginLeft: 4 }}
                                                    onClick={async () => {
                                                        setAuditLogToolId(selTool.id);
                                                        setAuditLogEntries([]);
                                                        setAuditLogLoading(true);
                                                        try {
                                                            const r = await svc('kri_get_tool_audit_log', selTool.id);
                                                            setAuditLogEntries(r?.statusCode?.response_data?.data || []);
                                                        } catch (e) { _showLoadError('audit log', e); }
                                                        finally { setAuditLogLoading(false); }
                                                    }}>
                                                    History
                                                </button>
                                            </div>
                                            <span style={{ fontSize: 9, color: '#7a869a' }}>Last sync: {fmtDate(selTool.last_sync)}</span>
                                        </div>

                                        {/* Inline label editor — opens when user clicks the pencil */}
                                        {labelEditorToolId === selTool.id && (
                                            <div style={{ borderBottom: '1px solid #f0f2f5', background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', padding: '14px 20px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>
                                                        Customise dashboard labels for {selTool.name}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button type="button" className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', borderColor: '#7c3aed', color: '#7c3aed' }}
                                                            onClick={() => requestLabelSuggestion(selTool.id)}>
                                                            <span style={{ fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 2, background: '#7c3aed', color: '#fff', marginRight: 4 }}>NOVA AI</span>
                                                            Suggest
                                                        </button>
                                                        <button type="button" className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={closeLabelEditor}>Cancel</button>
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 10 }}>
                                                    Leave a field empty to use the {selTool.tool_type} category default. Max 30 characters.
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 8 }}>
                                                    {[
                                                        { key: 'score', label: 'Score caption' },
                                                        { key: 'installed', label: 'Primary count' },
                                                        { key: 'healthy', label: 'Healthy' },
                                                        { key: 'unhealthy', label: 'Unhealthy' },
                                                        { key: 'missing', label: 'Missing' },
                                                    ].map(f => (
                                                        <div key={f.key}>
                                                            <label style={{ fontSize: 10, fontWeight: 600, color: '#3d4f6f', display: 'block', marginBottom: 2 }}>{f.label}</label>
                                                            <input className="ap-input" maxLength={30}
                                                                placeholder={(selTool.dashboard_labels && selTool.dashboard_labels[f.key]) || ''}
                                                                value={labelDraft[f.key]}
                                                                onChange={e => setLabelDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                                                                style={{ fontSize: 11 }} />
                                                        </div>
                                                    ))}
                                                </div>
                                                {labelSuggestion && !labelSuggestion.error && labelSuggestion.suggested_labels && (
                                                    <div style={{ background: '#fff', border: '1px solid #d8c8f5', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                                <span style={{ fontSize: 10, color: '#1b2a4a', fontWeight: 600 }}>Suggested labels (from your tool's actual data)</span>
                                                                {labelSuggestion.confidence === 'heuristic' && <span style={{ fontSize: 9, color: '#d97706' }}>• keyword-match</span>}
                                                            </div>
                                                            <button type="button" className="ap-btn ap-btn--primary" style={{ fontSize: 10, padding: '3px 8px', background: '#7c3aed' }} onClick={applyLabelSuggestionToDraft}>Use this</button>
                                                        </div>
                                                        <div style={{ fontSize: 10, color: '#3d4f6f' }}>
                                                            {Object.entries(labelSuggestion.suggested_labels).map(([k, v]) => (
                                                                <span key={k} style={{ marginRight: 12 }}><strong>{k}</strong>: {v}</span>
                                                            ))}
                                                        </div>
                                                        {labelSuggestion.rationale && (
                                                            <div style={{ fontSize: 9, color: '#7a869a', marginTop: 4, fontStyle: 'italic' }}>{labelSuggestion.rationale}</div>
                                                        )}
                                                        {labelSuggestion.sample_values?.length > 0 && (
                                                            <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>
                                                                Sampled from: {labelSuggestion.sample_values.slice(0, 6).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {labelSuggestion?.error && (
                                                    <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 8 }}>{labelSuggestion.error}</div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <button type="button" className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', color: '#7a869a' }}
                                                        onClick={() => setLabelDraft({ score: '', installed: '', healthy: '', unhealthy: '', missing: '' })}>
                                                        Reset to category defaults
                                                    </button>
                                                    <button type="button" className="ap-btn ap-btn--primary" style={{ fontSize: 11 }}
                                                        disabled={labelSaving} onClick={() => saveLabelOverride(selTool)}>
                                                        {labelSaving ? 'Saving…' : 'Save labels'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {/* Charts. Donut now includes an Unclassified slice when there
                                    are unknown rows so the donut % matches the score % at the top.
                                    Without it the donut % used a smaller denominator and customers
                                    saw "100% Within SLA" while the tile showed e.g. 88.1%. */}
                                        {(() => {
                                            const unitPlural = (selTool.unit_label && selTool.unit_label.plural) || 'assets';
                                            const unknownCount = selTool.unknown || 0;
                                            const donutData = [
                                                { label: labels.healthy, value: selTool.healthy || 0, color: '#0f7b4f' },
                                                { label: labels.unhealthy, value: selTool.health_degraded || 0, color: '#d97706' },
                                                { label: labels.missing, value: selTool.not_installed || selTool.non_compliant || 0, color: '#dc2626' },
                                            ];
                                            if (unknownCount > 0) {
                                                donutData.push({ label: 'Unclassified', value: unknownCount, color: '#94a3b8' });
                                            }
                                            return (
                                                <div style={{ display: 'flex', borderBottom: '1px solid #f0f2f5' }}>
                                                    <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #f0f2f5' }}>
                                                        {/* centerValue + centerLabel keep the donut % in lock-step with
                                            the score chip at the top of this card. The slices still
                                            visualise the breakdown (healthy / unhealthy / missing /
                                            unclassified), so customers can see "3 stale" without seeing
                                            two competing scores side-by-side. */}
                                                        <DonutChart size={120} thickness={16}
                                                            data={donutData}
                                                            centerValue={selTool.score}
                                                            centerLabel={labels.score} />
                                                    </div>
                                                    <div style={{ flex: 1, padding: '16px 20px' }}>
                                                        <HBarChart height={18} data={[
                                                            { label: labels.installed, value: selTool.installed || selTool.compliant || 0, maxValue: selTool.total_checked || 1, color: '#0f7b4f' },
                                                            { label: labels.healthy, value: selTool.healthy || 0, maxValue: selTool.total_checked || 1, color: '#22c55e' },
                                                            { label: labels.unhealthy, value: selTool.health_degraded || 0, maxValue: selTool.total_checked || 1, color: '#d97706' },
                                                            { label: labels.missing, value: selTool.not_installed || selTool.non_compliant || 0, maxValue: selTool.total_checked || 1, color: '#dc2626' },
                                                            ...(unknownCount > 0 ? [{
                                                                label: 'Unclassified',
                                                                value: unknownCount,
                                                                maxValue: selTool.total_checked || 1,
                                                                color: '#94a3b8',
                                                            }] : []),
                                                        ]} />
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 9, color: '#7a869a' }}>
                                                            <span>Target: {selTool.threshold}%</span>
                                                            <span>
                                                                {selTool.total_checked} {unitPlural}
                                                                {unknownCount > 0 && (
                                                                    <span title="Unclassified rows had ambiguous status fields (missing dates, blank status). Fix the source CSV to count them."
                                                                        style={{ color: '#94a3b8', marginLeft: 8, cursor: 'help' }}>
                                                                        &middot; {unknownCount} unclassified
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        {/* Clickable count chips — drill into the matrix or gap report filtered to this tool */}
                                                        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                                                            {[
                                                                { label: labels.installed, value: selTool.installed || selTool.compliant || 0, color: '#0f7b4f', go: () => { setMatrixFilter('compliant'); handleTabChange('matrix'); } },
                                                                { label: labels.healthy, value: selTool.healthy || 0, color: '#22c55e', go: () => { setMatrixFilter('compliant'); handleTabChange('matrix'); } },
                                                                { label: labels.unhealthy, value: selTool.health_degraded || 0, color: '#d97706', go: () => { setMatrixFilter(''); handleTabChange('matrix'); } },
                                                                { label: labels.missing, value: selTool.not_installed || selTool.non_compliant || 0, color: '#dc2626', go: () => { setGapToolFilter(String(selTool.id)); handleTabChange('gaps'); } },
                                                            ].map((c, i) => (
                                                                <button key={i} type="button" onClick={c.go} title={`View ${c.label}`}
                                                                    style={{
                                                                        border: `1px solid ${c.color}30`, background: `${c.color}10`, color: c.color,
                                                                        padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                                                        display: 'inline-flex', gap: 6, alignItems: 'center'
                                                                    }}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.background = `${c.color}1a`; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.background = `${c.color}10`; }}>
                                                                    <span style={{ fontWeight: 800 }}>{c.value}</span>
                                                                    <span style={{ fontWeight: 600 }}>{c.label}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* NOVA AI · Why these numbers? — field-detection summary */}
                                        {explainOpen && explainToolId === selTool.id && (
                                            <div style={{ borderBottom: '1px solid #f0f2f5', background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', padding: '14px 20px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>Why these numbers?</span>
                                                        {explainData?.shape && (
                                                            <span style={{ fontSize: 9, color: '#7a869a' }}>· {explainData.total_rows} rows ingested · {explainData.shape}-shape</span>
                                                        )}
                                                    </div>
                                                    <button type="button" aria-label="Close" onClick={closeExplainUpload}
                                                        style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#7a869a', cursor: 'pointer', lineHeight: 1 }}>×</button>
                                                </div>

                                                {explainLoading && (
                                                    <div style={{ fontSize: 11, color: '#4338ca' }}>Analysing the last upload…</div>
                                                )}
                                                {!explainLoading && explainData?.error && (
                                                    <div style={{ fontSize: 11, color: '#dc2626' }}>{explainData.error}</div>
                                                )}
                                                {!explainLoading && explainData && !explainData.error && (
                                                    <div style={{ display: 'grid', gap: 10 }}>
                                                        {/* Per-count explanation table */}
                                                        {explainData.counts_explanation?.length > 0 && (
                                                            <div style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: 8 }}>
                                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#3d4f6f', marginBottom: 6, letterSpacing: 0.5 }}>WHY EACH COUNT IS WHAT IT IS</div>
                                                                <div style={{ display: 'grid', gap: 4 }}>
                                                                    {explainData.counts_explanation.map((c, i) => (
                                                                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 50px 1fr', gap: 8, fontSize: 11, padding: '4px 0', borderBottom: i < explainData.counts_explanation.length - 1 ? '1px solid #f5f7fa' : 'none' }}>
                                                                            <div style={{ fontWeight: 700, color: '#1b2a4a' }}>{c.count_label}</div>
                                                                            <div style={{ fontWeight: 800, color: '#4338ca', textAlign: 'right' }}>{c.value}</div>
                                                                            <div style={{ color: '#3d4f6f', fontSize: 10 }}>{c.explanation}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Per-field detection table */}
                                                        {explainData.fields?.length > 0 && (
                                                            <div style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: 8 }}>
                                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#3d4f6f', marginBottom: 6, letterSpacing: 0.5 }}>FIELD DETECTION</div>
                                                                <table className="ap-table" style={{ fontSize: 10 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Field</th>
                                                                            <th>Matched header</th>
                                                                            <th>Populated</th>
                                                                            <th>Top values</th>
                                                                            <th>Note</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {explainData.fields.map((f, i) => {
                                                                            const color = f.status === 'ok' ? '#0f7b4f' : f.status === 'partial' ? '#d97706' : f.status === 'warn' ? '#d97706' : f.status === 'missing' ? '#94a3b8' : '#dc2626';
                                                                            const icon = f.status === 'ok' ? '✓' : f.status === 'missing' ? '−' : f.status === 'warn' ? '⚠' : '!';
                                                                            return (
                                                                                <tr key={i}>
                                                                                    <td style={{ fontWeight: 600 }}>
                                                                                        <span style={{ color, marginRight: 4 }}>{icon}</span>
                                                                                        {f.canonical}
                                                                                    </td>
                                                                                    <td><code style={{ fontSize: 10, color: f.matched_header ? '#1b2a4a' : '#94a3b8' }}>{f.matched_header || '—'}</code></td>
                                                                                    <td style={{ color }}>{f.populated_count}/{explainData.total_rows} ({f.populated_pct}%)</td>
                                                                                    <td style={{ color: '#3d4f6f' }}>
                                                                                        {(f.distinct_sample || []).slice(0, 3).map(d => `${d.value} (${d.count})`).join(', ') || '—'}
                                                                                    </td>
                                                                                    <td style={{ color: '#7a869a', fontSize: 10 }}>{f.note}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}

                                                        {/* Recommended actions */}
                                                        {explainData.recommended_actions?.length > 0 && (
                                                            <div style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 6, padding: 8 }}>
                                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', marginBottom: 6, letterSpacing: 0.5 }}>RECOMMENDED ACTIONS</div>
                                                                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#1b2a4a', lineHeight: 1.6 }}>
                                                                    {explainData.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                                                                </ol>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/* NOVA AI Baseline Recommender + Usage + Drift + Policies (Phases 1-4) */}
                                        <ToolInsightPanels
                                            tool={selTool}
                                            aiRec={aiToolId === selTool.id ? aiRec : null}
                                            aiLoading={aiLoading && aiToolId === selTool.id}
                                            usageData={usageData?.tool_id === selTool.id ? usageData : null}
                                            driftData={driftData?.tool_id === selTool.id ? driftData : null}
                                            policiesData={policiesData?.tool_id === selTool.id ? policiesData : null}
                                            onLoadAi={async () => {
                                                setAiLoading(true); setAiToolId(selTool.id);
                                                try {
                                                    const { kri_request_baseline_recommendation } = await import('../services');
                                                    const r = await kri_request_baseline_recommendation(selTool.id);
                                                    const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                    setAiRec(d || null);
                                                } catch (e) { setAiRec({ error: e?.message || 'Failed' }); }
                                                setAiLoading(false);
                                            }}
                                            onLoadUsage={async () => {
                                                try {
                                                    const { kri_get_tool_usage_metrics } = await import('../services');
                                                    const r = await kri_get_tool_usage_metrics(selTool.id);
                                                    const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                    if (d) setUsageData({ ...d, tool_id: selTool.id });
                                                } catch (e) { }
                                            }}
                                            onLoadDrift={async () => {
                                                try {
                                                    const { kri_get_tool_drift_history } = await import('../services');
                                                    const r = await kri_get_tool_drift_history(selTool.id);
                                                    const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                    if (d) setDriftData({ ...d, tool_id: selTool.id });
                                                } catch (e) { }
                                            }}
                                            onLoadPolicies={async () => {
                                                try {
                                                    const { kri_get_baseline_policies } = await import('../services');
                                                    const r = await kri_get_baseline_policies(selTool.id);
                                                    const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                    if (d) setPoliciesData({ ...d, tool_id: selTool.id });
                                                } catch (e) { }
                                            }}
                                            onAfterApply={async () => {
                                                // Real-time refresh after a baseline change — reload everything
                                                // so the dashboard, policies and usage tiles reflect the new config.
                                                try { await loadDashboard({ silent: true }); } catch (e) { }
                                                try { await loadTools(); } catch (e) { }
                                                // Force the lazy-loaded panels to reload on next open.
                                                setUsageData(null); setDriftData(null); setPoliciesData(null);
                                            }}
                                        />
                                        {/* Unhealthy */}
                                        {selUnhealthy.length > 0 && (
                                            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f2f5' }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 6 }}>Unhealthy Agents ({selUnhealthy.length})</div>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                    <thead><tr style={{ background: '#fafbfc' }}>
                                                        {['HOSTNAME', 'IP', 'OS', 'STATUS', 'REASON', 'OWNER'].map(h => <th key={h} style={{ padding: '4px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a' }}>{h}</th>)}
                                                    </tr></thead>
                                                    <tbody>{selUnhealthy.slice(0, 20).map(a => {
                                                        // Reason explains WHY this row is in the Unhealthy bucket.
                                                        // Backend (matrix endpoint) computes it from agent_status +
                                                        // last_seen so the logic stays next to the source data; we
                                                        // fall back to agent_status verbatim if an older backend
                                                        // version isn't returning the field yet.
                                                        const cell = a.tools?.[selTool.id] || {};
                                                        const reason = cell.unhealthy_reason || cell.agent_status || 'offline';
                                                        return (
                                                            <tr key={a.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                                                                <td style={{ padding: '5px 10px', fontWeight: 600, color: '#1b2a4a' }}>{a.hostname}</td>
                                                                <td style={{ padding: '5px 10px', color: '#7a869a' }}>{a.ip_address || '-'}</td>
                                                                <td style={{ padding: '5px 10px', color: '#7a869a', textTransform: 'capitalize' }}>{a.os_type}</td>
                                                                <td style={{ padding: '5px 10px' }}><span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8, color: '#d97706', background: '#d9770610' }}>{cell.agent_status || 'offline'}</span></td>
                                                                <td style={{ padding: '5px 10px', color: '#3d4f6f', fontSize: 11 }}>{reason}</td>
                                                                <td style={{ padding: '5px 10px', color: '#7a869a' }}>{a.owner || '-'}</td>
                                                            </tr>
                                                        );
                                                    })}</tbody>
                                                </table>
                                            </div>
                                        )}
                                        {/* Gaps */}
                                        {selGaps.length > 0 && (
                                            <div style={{ padding: '12px 20px' }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>Missing ({selGaps.length})</div>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                    <thead><tr style={{ background: '#fafbfc' }}>
                                                        {['HOSTNAME', 'IP', 'OS', 'CRITICALITY', 'OWNER'].map(h => <th key={h} style={{ padding: '4px 10px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a' }}>{h}</th>)}
                                                    </tr></thead>
                                                    <tbody>{selGaps.slice(0, 20).map(a => (
                                                        <tr key={a.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                                                            <td style={{ padding: '5px 10px', fontWeight: 600, color: '#1b2a4a' }}>{a.hostname}</td>
                                                            <td style={{ padding: '5px 10px', color: '#7a869a' }}>{a.ip_address || '-'}</td>
                                                            <td style={{ padding: '5px 10px', color: '#7a869a', textTransform: 'capitalize' }}>{a.os_type}</td>
                                                            <td style={{ padding: '5px 10px' }}>{['', 'Low', 'Med', 'High', 'Crit'][a.criticality] || a.criticality}</td>
                                                            <td style={{ padding: '5px 10px', color: '#7a869a' }}>{a.owner || '-'}</td>
                                                        </tr>
                                                    ))}</tbody>
                                                </table>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, marginTop: 8 }} onClick={() => handleTabChange('gaps')}>View Full Gap Report</button>
                                            </div>
                                        )}
                                        {selGaps.length === 0 && selUnhealthy.length === 0 && (
                                            <div style={{ padding: '14px 20px', fontSize: 12, color: '#0f7b4f', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: 14 }}>&#10003;</span> All {selTool.total_checked} {(selTool.unit_label && selTool.unit_label.plural) || 'assets'} have {selTool.name} installed and reporting normally.
                                            </div>
                                        )}

                                        {/* Identity KRIs — Microsoft Entra ID adapter only.
                                    Renders the 7 user-and-identity metrics computed by
                                    `_compute_kri_metrics` on every successful sync and
                                    surfaced on `tool.additional_config.kri_metrics`
                                    (returned via list_tools as `selTool.kri_metrics`).
                                    Only shows when the tool's vendor is microsoft_entra_id
                                    AND the metrics dict is populated — degrades cleanly
                                    for other vendors / cold-start tenants. */}
                                        {selTool.vendor === 'microsoft_entra_id' && selTool.kri_metrics && (() => {
                                            const m = selTool.kri_metrics;
                                            const fmt = (entry, fn) => {
                                                if (!entry) return { value: '—', sub: 'no data yet' };
                                                if (entry.error) return { value: '✗', sub: entry.error.slice(0, 80) };
                                                return fn(entry);
                                            };
                                            // Build a passwordPolicy sub-detail string from per-check breakdown.
                                            const passwordChecks = (m.password_policy?.checks) || {};
                                            const ppSub = Object.entries(passwordChecks)
                                                .map(([k, v]) => `${k}: ${v.pass === null ? 'info' : v.pass ? '✓' : '✗'}`)
                                                .join(' · ') || '—';
                                            const rows = [
                                                // 1. % users with MFA enabled
                                                {
                                                    label: 'MFA enabled', ...fmt(m.mfa_coverage, e => ({
                                                        value: e.pct != null ? `${e.pct}%` : '—',
                                                        sub: `${e.value ?? 0} / ${e.denominator ?? 0} users`,
                                                    }))
                                                },
                                                // 2. Inactive accounts — 3-bucket histogram (30/60/90d)
                                                {
                                                    label: 'Inactive accounts', ...fmt(m.inactive_accounts, e => ({
                                                        value: `${e.inactive_30d ?? 0} / ${e.inactive_60d ?? 0} / ${e.inactive_90d ?? 0}`,
                                                        sub: `>30d / >60d / >90d (of ${e.denominator ?? 0} users)`,
                                                    }))
                                                },
                                                // 3. Provision/de-provision events
                                                {
                                                    label: 'Provisioning events (30d)', ...fmt(m.provisioning_events_30d, e => ({
                                                        value: `${e.adds ?? 0} / ${e.deletes ?? 0}`,
                                                        sub: `adds / deletes — avg time pending HRIS integration (§52)`,
                                                    }))
                                                },
                                                // 4. % privileged reviewed quarterly — now with exact decision count
                                                {
                                                    label: 'Privileged reviewed (90d)', ...fmt(m.privileged_review_pct_90d, e => ({
                                                        value: e.pct != null ? `${e.pct}%` : '—',
                                                        sub: e.decisions_walked != null
                                                            ? `${e.value ?? 0}/${e.denominator ?? 0} · ${e.decisions_walked} decisions walked`
                                                            : `${e.value ?? 0}/${e.denominator ?? 0} (Entra ID P2)`,
                                                    }))
                                                },
                                                // 5. Privilege escalation attempts
                                                {
                                                    label: 'Privilege escalation attempts (30d)', ...fmt(m.privilege_escalation_attempts_30d, e => ({
                                                        value: `${e.value ?? 0}`,
                                                        sub: 'failed RoleManagement events',
                                                    }))
                                                },
                                                // 6. Password policy compliance — composite check breakdown
                                                {
                                                    label: 'Password policy compliance', ...fmt(m.password_policy, e => ({
                                                        value: e.pct != null ? `${e.pct}%` : '—',
                                                        sub: ppSub,
                                                    }))
                                                },
                                                // 7a. Raw failed logins (volume)
                                                {
                                                    label: 'Failed login attempts (30d)', ...fmt(m.failed_logins_30d, e => ({
                                                        value: `${(e.value ?? 0).toLocaleString()}`,
                                                        sub: 'total across all users',
                                                    }))
                                                },
                                                // 7b. Real lockout events
                                                {
                                                    label: 'Account lockouts (30d)', ...fmt(m.account_lockouts_30d, e => ({
                                                        value: `${e.value ?? 0}`,
                                                        sub: '"Account locked" audit events',
                                                    }))
                                                },
                                                // 7c. Users above lockout-risk threshold (operational hint)
                                                {
                                                    label: 'Lockout-risk users', ...fmt(m.lockout_risk_users, e => ({
                                                        value: `${e.value ?? 0}`,
                                                        sub: `of ${e.denominator ?? 0} (>${e.threshold || 10} fails in 30d)`,
                                                    }))
                                                },
                                            ];
                                            return (
                                                <div style={{ borderTop: '1px solid #f0f2f5', padding: '14px 20px', background: '#fafbfc' }}>
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.4, marginBottom: 10 }}>
                                                        IDENTITY KRIs · ENTRA ID
                                                        {m.computed_at && (
                                                            <span style={{ fontWeight: 400, marginLeft: 8 }}>
                                                                computed {new Date(m.computed_at).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                                                        {rows.map(r => (
                                                            <div key={r.label} style={{ background: '#fff', border: '1px solid #e6e9ee', borderRadius: 6, padding: '10px 12px' }}>
                                                                <div style={{ fontSize: 9, fontWeight: 700, color: '#7a869a', letterSpacing: 0.3, marginBottom: 4, textTransform: 'uppercase' }}>{r.label}</div>
                                                                <div style={{ fontSize: 18, fontWeight: 800, color: '#1b2a4a', lineHeight: 1.1 }}>{r.value}</div>
                                                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4 }}>{r.sub}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div style={{ fontSize: 9, color: '#7a869a', marginTop: 8, fontStyle: 'italic' }}>
                                                        Source: Microsoft Graph (User.Read.All, AuditLog.Read.All, UserAuthenticationMethod.Read.All, Policy.Read.All, AccessReview.Read.All, RoleManagement.Read.Directory).
                                                        A "✗" on any tile means the corresponding Graph permission is missing or returned an error — see the Test connection result for the missing scope.
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })() : (
                                /* All tools summary table */
                                <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 8, overflow: 'hidden' }}>
                                    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f2f5', background: '#fafbfc' }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>All Tools Overview</span>
                                    </div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead><tr style={{ background: '#fafbfc' }}>
                                            {/* Generic headers — per-row category-specific labels appear on cell hover */}
                                            {['Tool', 'Type', 'Score', 'Active', 'Healthy', 'Issues', 'Gap', 'Threshold', 'Last Sync'].map(h => (
                                                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: '#7a869a', textTransform: 'uppercase' }}>{h}</th>
                                            ))}
                                        </tr></thead>
                                        <tbody>
                                            {allTools.map(t => {
                                                // Category-specific labels for hover tooltips on each cell — column
                                                // headers stay generic for cross-category sorting/scanning.
                                                const rl = getDashboardLabels(t);
                                                return (
                                                    <tr key={t.id} style={{ borderTop: '1px solid #f0f2f5', cursor: 'pointer' }}
                                                        onClick={() => setSelectedToolId(String(t.id))}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#f8f9fb'}
                                                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                                                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1b2a4a' }}>{t.name}</td>
                                                        <td style={{ padding: '10px 12px', fontSize: 11, color: '#7a869a', textTransform: 'capitalize' }}>{(t.tool_type || '').replace(/_/g, ' ')}</td>
                                                        <td style={{ padding: '10px 12px' }} title={`${rl.score}: ${t.score}%`}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <div style={{ width: 50, height: 6, background: '#f0f2f5', borderRadius: 3, overflow: 'hidden' }}>
                                                                    <div style={{ height: '100%', width: `${t.score}%`, background: scoreColor(t.score, t.threshold, t.critical_threshold), borderRadius: 3 }} />
                                                                </div>
                                                                <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(t.score, t.threshold, t.critical_threshold) }}>{t.score}%</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 12px', color: '#0f7b4f', fontWeight: 600 }} title={rl.installed}>{t.installed || t.compliant}</td>
                                                        <td style={{ padding: '10px 12px', color: '#0f7b4f' }} title={rl.healthy}>{t.healthy || 0}</td>
                                                        <td style={{ padding: '10px 12px', color: (t.health_degraded || 0) > 0 ? '#d97706' : '#7a869a', fontWeight: (t.health_degraded || 0) > 0 ? 600 : 400 }} title={rl.unhealthy}>{t.health_degraded || 0}</td>
                                                        <td style={{ padding: '10px 12px', color: (t.not_installed || 0) > 0 ? '#dc2626' : '#7a869a', fontWeight: (t.not_installed || 0) > 0 ? 600 : 400 }} title={rl.missing}>{t.not_installed || t.non_compliant || 0}</td>
                                                        <td style={{ padding: '10px 12px', fontSize: 11, color: '#7a869a' }}>{t.threshold}%</td>
                                                        <td style={{ padding: '10px 12px', fontSize: 10, color: '#7a869a' }}>{fmtDate(t.last_sync)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </>) : (
                            <EmptyState
                                icon="tools"
                                title="No tools configured yet"
                                description="Connect a security tool (CrowdStrike, SentinelOne, Wazuh, Microsoft Defender, custom) to start pulling compliance signals into the matrix."
                                actions={[
                                    { label: 'Go to Tool Integrations', primary: true, onClick: () => handleTabChange('tools') },
                                ]}
                            />
                        )}
                    </div>);
            })()}

            {/* TOOL INTEGRATIONS */}
            {tab === 'tools' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#3d4f6f' }}>{tools.length} tool integration{tools.length !== 1 ? 's' : ''} configured</div>
                        <button className="ap-btn ap-btn--primary" onClick={openAddTool}>+ Add Tool Integration</button>
                    </div>

                    {tools.length > 0 ? (
                        <table className="ap-table kri-table-sticky">
                            <thead><tr><th className="ap-cell-left">Tool</th><th>Vendor</th><th>Type</th><th>Threshold</th><th className="ap-cell-left">Last Connection Check</th><th className="ap-cell-actions">Actions</th></tr></thead>
                            <tbody>
                                {tools.map(t => (
                                    <tr key={t.id}>
                                        <td className="ap-cell-left" style={{ fontWeight: 600 }}>{t.name}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{t.vendor.replace(/_/g, ' ')}</td>
                                        <td style={{ textTransform: 'capitalize', fontSize: 11 }}>{t.tool_type.replace(/_/g, ' ')}</td>
                                        <td>{t.compliance_threshold}%</td>
                                        {/* Last Connection Check — replaces the old Verified Yes/No
                                            and the duplicate Last Sync + Sync Status columns. Shows
                                            *when* the platform last confirmed the integration is
                                            healthy + a one-line status. The technical error (if any)
                                            is collapsed behind a "Show details" toggle so the table
                                            stays readable for non-technical reviewers. */}
                                        <td className="ap-cell-left" style={{ fontSize: 11 }}>
                                            <KriLastCheckCell tool={t} />
                                        </td>
                                        <td className="ap-cell-actions" style={{ minWidth: 360 }}>
                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                {t.vendor !== 'custom' && (
                                                    <>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => testConnection(t.id)}>Test</button>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={syncing === t.id}
                                                            onClick={() => syncTool(t.id)}>{syncing === t.id ? 'Syncing…' : 'Sync'}</button>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={syncing === t.id}
                                                            onClick={async () => { setSyncing(t.id); try { const r = await svc('kri_sync_tool', t.id, { force_full: true }); flash(r?.statusCode?.response_data?.data?.message || 'Done.'); loadTools(); loadDashboard({ silent: true }); } catch (e) { flash('Failed.'); } setSyncing(null); }}
                                                            title="Force full re-sync of all devices">Full</button>
                                                    </>
                                                )}
                                                {t.vendor === 'custom' && (() => {
                                                    // Button label adapts to the tool's category. Backend auto-detects shape
                                                    // from the CSV headers regardless of label, but the button reads naturally
                                                    // for the customer's actual workflow (tickets / users / repos / secrets).
                                                    const cat = (t.tool_type || '').toLowerCase();
                                                    const isTicketLike = ['itsm'].includes(cat);
                                                    const label =
                                                        cat === 'itsm' ? 'Upload Tickets' :
                                                            cat === 'mfa' ? 'Upload Users' :
                                                                cat === 'pam' ? 'Upload Accounts' :
                                                                    cat === 'secrets_mgmt' ? 'Upload Secrets' :
                                                                        cat === 'code_security' ? 'Upload Repos' :
                                                                            cat === 'cspm' ? 'Upload Accounts' :
                                                                                cat === 'sspm' ? 'Upload SaaS Apps' :
                                                                                    'Upload Devices';
                                                    const tooltip = isTicketLike
                                                        ? 'Click to upload (or re-upload) your ticket export. Re-uploading REPLACES the existing ticket records — same file produces the same result.'
                                                        : 'Click to upload (or re-upload) your CSV. Re-uploading updates existing records by ID — no duplicates, new rows added, existing rows refreshed.';
                                                    return (
                                                        <label className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', cursor: 'pointer' }} title={tooltip}>
                                                            {label}
                                                            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={async (e) => {
                                                                if (!e.target.files[0]) return;
                                                                const file = e.target.files[0];
                                                                e.target.value = '';
                                                                setSyncing(t.id);  // drives the full-page loader
                                                                try {
                                                                    const r = await svc('kri_upload_tool_devices', t.id, file);
                                                                    flash(r?.statusCode?.response_data?.data?.message || 'Upload complete.');
                                                                    loadTools(); loadDashboard({ silent: true });
                                                                } catch (err) { flash('Upload failed.'); }
                                                                finally { setSyncing(null); }
                                                            }} />
                                                        </label>
                                                    );
                                                })()}
                                                {/* Reprocess: re-runs the last upload's stashed rows through the
                                                    pipeline. Useful after a category-mapping or threshold change. */}
                                                {t.vendor === 'custom' && (
                                                    <button className="ap-btn ap-btn--ghost"
                                                        style={{ fontSize: 10, padding: '3px 8px', opacity: t.has_last_upload ? 1 : 0.5 }}
                                                        disabled={!t.has_last_upload || syncing === t.id}
                                                        title={t.has_last_upload
                                                            ? 'Re-run the last uploaded CSV through the ingestion pipeline without re-uploading. Useful after changing thresholds or category mapping.'
                                                            : 'No previous upload to reprocess. Upload a CSV first.'}
                                                        onClick={async () => {
                                                            setSyncing(t.id);
                                                            try {
                                                                const r = await svc('kri_reprocess_tool', t.id);
                                                                flash(r?.statusCode?.response_data?.data?.message || 'Reprocessed.');
                                                                loadTools(); loadDashboard({ silent: true });
                                                            } catch (e) { flash('Reprocess failed.'); }
                                                            setSyncing(null);
                                                        }}>
                                                        {syncing === t.id ? 'Working…' : 'Reprocess'}
                                                    </button>
                                                )}
                                                {/* Edit: opens a focused modal for the runtime knobs operators
                                                    retune post-onboarding (name, threshold, record cap, sync
                                                    frequency). Separate from the Add wizard so users don't
                                                    walk through vendor select + credentials again. */}
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px' }}
                                                    onClick={() => openEditTool(t)}
                                                    title="Edit name, compliance threshold, record cap, and sync frequency.">Edit</button>
                                                {/* Pre-flight Check: shown for ALL tools (custom + native). For native it
                                                    explains the API field mapping; for custom it shows the CSV checklist. */}
                                                <button className="ap-btn ap-btn--ghost"
                                                    style={{ fontSize: 10, padding: '3px 8px', borderColor: '#7c3aed', color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                    title="NOVA AI · upload checklist — see what columns we expect before you export"
                                                    onClick={() => openUploadChecklist(t)}>
                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 2, background: '#7c3aed', color: '#fff' }}>NOVA AI</span>
                                                    Pre-flight
                                                </button>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', color: '#dc2626' }}
                                                    onClick={async () => {
                                                        const ok = await confirmDialog({
                                                            title: 'Delete tool?',
                                                            message: `This will remove "${t.name}" and stop pulling its compliance data. The history is retained.`,
                                                            confirmLabel: 'Delete', cancelLabel: 'Cancel', danger: true,
                                                        });
                                                        if (!ok) return;
                                                        const r = await svc('kri_delete_tool', t.id);
                                                        console.log("a" ,r);
                                                        
                                                        flash(r?.statusCode?.error?.message || r?.statusCode?.message );
                                                        loadTools();
                                                    }}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <EmptyState
                            icon="tools"
                            title="No tool integrations yet"
                            description='Click "+ Add Tool Integration" above to connect CrowdStrike, SentinelOne, Wazuh, or any custom security tool. Once connected, sync runs automatically and feeds into the dashboard, gaps, and compliance matrix.'
                        />
                    )}

                    {/* Add Tool Modal -3-step wizard */}
                    {showToolModal && (
                        // Backdrop click does NOT dismiss — multi-step setup wizard
                        // with significant data entry; customer must use Cancel/Close
                        // explicitly so accidental clicks don't wipe a half-filled form.
                        <div className="ap-modal-overlay">
                            <div className="ap-modal" style={{ maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
                                {/* Close button — explicit dismissal, prompts only if data entered */}
                                <button
                                    onClick={async () => {
                                        const hasData = !!(toolForm?.name || toolForm?.api_base_url || (selectedVendor && baselineStep > 1));
                                        if (!hasData) { setShowToolModal(false); return; }
                                        const ok = await confirmDialog({
                                            title: 'Close wizard?',
                                            message: 'Close the wizard? Your unsaved changes will be lost.',
                                            confirmLabel: 'Close', cancelLabel: 'Keep going', danger: true,
                                        });
                                        if (ok) setShowToolModal(false);
                                    }}
                                    aria-label="Close"
                                    style={{
                                        position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                        fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f2f5'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                    ×
                                </button>
                                {/* Step indicators */}
                                {(() => {
                                    const isCustom = selectedVendor?.id === 'custom';
                                    const steps = isCustom
                                        ? ['Select Vendor', 'Tool Details', 'Baseline & Upload']
                                        : ['Select Vendor', 'API Credentials', 'Preview & Select Fields', 'Baseline Configuration'];
                                    const currentStep = isCustom && baselineStep === 4 ? 3 : baselineStep;
                                    return (
                                        <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
                                            {steps.map((label, i) => (
                                                <div key={i} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderBottom: `2px solid ${currentStep > i ? '#1b2a4a' : '#d8dce3'}` }}>
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: currentStep > i ? '#1b2a4a' : '#7a869a', textTransform: 'uppercase' }}>Step {i + 1}</div>
                                                    <div style={{ fontSize: 10, color: currentStep > i ? '#1b2a4a' : '#7a869a' }}>{label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}

                                {/* Step 1: Select Vendor */}
                                {baselineStep === 1 && (
                                    <div>
                                        {/* NOVA AI Discovery — front door */}
                                        <div style={{ padding: 14, marginBottom: 14, border: '1px solid #d8c8f5', borderRadius: 6, background: 'linear-gradient(135deg, #faf7ff 0%, #f4ecff 100%)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>Not sure which adapter to pick?</span>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 8 }}>
                                                Describe your tool — NOVA AI will route you to the right adapter, or set up a Universal Connector with sensible defaults.
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <input className="ap-input" style={{ flex: 1, fontSize: 12 }}
                                                    placeholder='e.g. "we use Okta for SSO" or "Tanium for patch management"'
                                                    value={discoverInput}
                                                    onChange={e => setDiscoverInput(e.target.value)}
                                                    onKeyDown={async e => {
                                                        if (e.key === 'Enter' && discoverInput.trim()) {
                                                            setDiscoverLoading(true); setDiscoverResult(null);
                                                            try {
                                                                const { kri_ai_suggest_adapter } = await import('../services');
                                                                const r = await kri_ai_suggest_adapter({ description: discoverInput });
                                                                const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                                setDiscoverResult(d || { error: 'No response' });
                                                            } catch (err) { setDiscoverResult({ error: err?.message || 'Discovery failed' }); }
                                                            setDiscoverLoading(false);
                                                        }
                                                    }} />
                                                <button className="ap-btn ap-btn--primary" disabled={discoverLoading || !discoverInput.trim()}
                                                    style={{ background: '#7c3aed' }}
                                                    onClick={async () => {
                                                        setDiscoverLoading(true); setDiscoverResult(null);
                                                        try {
                                                            const { kri_ai_suggest_adapter } = await import('../services');
                                                            const r = await kri_ai_suggest_adapter({ description: discoverInput });
                                                            const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                            setDiscoverResult(d || { error: 'No response' });
                                                        } catch (err) { setDiscoverResult({ error: err?.message || 'Discovery failed' }); }
                                                        setDiscoverLoading(false);
                                                    }}>
                                                    {discoverLoading ? 'Asking NOVA AI…' : 'Ask NOVA AI'}
                                                </button>
                                            </div>
                                            {discoverResult && !discoverResult.error && (
                                                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #d8c8f5', borderRadius: 6 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                                                        <span style={{
                                                            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#fff',
                                                            background: discoverResult.confidence === 'high' ? '#0f7b4f' : discoverResult.confidence === 'medium' ? '#d97706' : '#dc2626'
                                                        }}>
                                                            {String(discoverResult.confidence || '').toUpperCase()} CONFIDENCE
                                                        </span>
                                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#fff', background: '#1b2a4a' }}>
                                                            {String(discoverResult.recommended_category || '').replace(/_/g, ' ').toUpperCase()}
                                                        </span>
                                                        <span style={{ fontSize: 9, color: '#7a869a' }}>
                                                            via {discoverResult.source === 'nova_ai' ? 'NOVA AI' : 'Heuristic fallback'}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#1b2a4a', marginBottom: 4 }}>
                                                        Recommended: <strong>{discoverResult.vendor_display?.name || discoverResult.recommended_vendor}</strong>
                                                    </div>
                                                    <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 8 }}>{discoverResult.reasoning}</div>
                                                    {discoverResult.recommended_vendor === 'universal' && discoverResult.universal_hint?.notes && (
                                                        <div style={{ fontSize: 10, color: '#7c3aed', marginBottom: 8, fontStyle: 'italic' }}>
                                                            Setup hint: {discoverResult.universal_hint.notes}
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button className="ap-btn ap-btn--primary" style={{ background: '#7c3aed', fontSize: 11 }}
                                                            onClick={() => {
                                                                if (discoverResult.recommended_vendor === 'universal') {
                                                                    const hint = discoverResult.universal_hint || {};
                                                                    setSelectedVendor({ id: 'universal', name: 'Build Your Own', match_fields: ['hostname', 'ip_address', 'mac_address', 'serial_number'], setup_fields: [] });
                                                                    updateToolForm({
                                                                        vendor: 'universal', name: discoverInput.split(' ').slice(0, 4).join(' '),
                                                                        auth_type: hint.auth_type || 'none', tool_type: discoverResult.recommended_category || 'custom'
                                                                    });
                                                                    setConnectorForm({
                                                                        base_url: hint.base_url || '', endpoint: hint.endpoint || '',
                                                                        auth_type: hint.auth_type || 'none',
                                                                        username: '', password: '', api_key: '', token: '',
                                                                        oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: '', oauth2_scope: '',
                                                                    });
                                                                    setDiscoveredData(null); setFieldMapping({});
                                                                } else {
                                                                    const target = vendors.find(v => v.id === discoverResult.recommended_vendor);
                                                                    if (target) {
                                                                        setSelectedVendor(target);
                                                                        updateToolForm({
                                                                            vendor: target.id, name: target.name,
                                                                            auth_type: 'oauth2', tool_type: target.tool_category || 'edr'
                                                                        });
                                                                    }
                                                                }
                                                                setDiscoverResult(null);
                                                                setBaselineStep(2);
                                                            }}>
                                                            Use {discoverResult.vendor_display?.name || 'This'} →
                                                        </button>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                            onClick={() => setDiscoverResult(null)}>
                                                            Pick manually
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {discoverResult?.error && (
                                                <div style={{ marginTop: 8, fontSize: 11, color: '#dc2626' }}>Error: {discoverResult.error}</div>
                                            )}
                                        </div>

                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Or pick a tool manually</h3>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                            {vendors.map(v => (
                                                <div key={v.id} role="button" tabIndex={0}
                                                    onClick={() => { setSelectedVendor(v); setToolForm({ vendor: v.id, name: v.name, auth_type: 'oauth2', tool_type: v.tool_category || 'edr' }); setPreviewData(null); setPreviewError(''); setPreviewTaskId(null); }}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedVendor(v); setToolForm({ vendor: v.id, name: v.name, auth_type: 'oauth2', tool_type: v.tool_category || 'edr' }); setPreviewData(null); setPreviewError(''); setPreviewTaskId(null); } }}
                                                    aria-label={`Select vendor ${v.name}`}
                                                    style={{ padding: 14, border: `2px solid ${selectedVendor?.id === v.id ? '#1b2a4a' : '#d8dce3'}`, borderRadius: 6, cursor: 'pointer', background: selectedVendor?.id === v.id ? '#1b2a4a08' : '#fff' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>{v.name}</div>
                                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 2 }}>Match by: {v.match_fields.join(', ')}</div>
                                                </div>
                                            ))}
                                            {/* Universal connector builder */}
                                            <div role="button" tabIndex={0}
                                                onClick={() => { setSelectedVendor({ id: 'universal', name: 'Build Your Own', match_fields: ['hostname', 'ip_address', 'mac_address', 'serial_number'], setup_fields: [] }); updateToolForm({ vendor: 'universal', name: '', auth_type: 'none' }); setConnectorForm({ base_url: '', endpoint: '', auth_type: 'none', username: '', password: '', api_key: '', token: '', oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: '', oauth2_scope: '' }); setDiscoveredData(null); setFieldMapping({}); }}
                                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedVendor({ id: 'universal', name: 'Build Your Own', match_fields: ['hostname', 'ip_address', 'mac_address', 'serial_number'], setup_fields: [] }); updateToolForm({ vendor: 'universal', name: '', auth_type: 'none' }); setConnectorForm({ base_url: '', endpoint: '', auth_type: 'none', username: '', password: '', api_key: '', token: '', oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: '', oauth2_scope: '' }); setDiscoveredData(null); setFieldMapping({}); } }}
                                                aria-label="Build your own connector"
                                                style={{ padding: 14, border: `2px solid ${selectedVendor?.id === 'universal' ? '#1b2a4a' : '#d8dce3'}`, borderRadius: 6, cursor: 'pointer', background: selectedVendor?.id === 'universal' ? '#1b2a4a08' : '#fff' }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>Build Your Own Connector</div>
                                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 2 }}>Connect any REST API - we discover fields automatically</div>
                                            </div>
                                            {/* CSV upload only */}
                                            <div role="button" tabIndex={0}
                                                onClick={() => { setSelectedVendor({ id: 'custom', name: 'CSV Upload Only', match_fields: ['hostname'], setup_fields: [] }); updateToolForm({ vendor: 'custom', name: '', auth_type: 'none' }); }}
                                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedVendor({ id: 'custom', name: 'CSV Upload Only', match_fields: ['hostname'], setup_fields: [] }); updateToolForm({ vendor: 'custom', name: '', auth_type: 'none' }); } }}
                                                aria-label="CSV upload only"
                                                style={{ padding: 14, border: `2px solid ${selectedVendor?.id === 'custom' ? '#1b2a4a' : '#d8dce3'}`, borderRadius: 6, cursor: 'pointer', background: selectedVendor?.id === 'custom' ? '#1b2a4a08' : '#fff' }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>CSV Upload Only</div>
                                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 2 }}>No API - manually upload device list</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                                            <button className="ap-btn ap-btn--ghost" onClick={() => setShowToolModal(false)}>Cancel</button>
                                            <button className="ap-btn ap-btn--primary" disabled={!selectedVendor} onClick={() => setBaselineStep(2)}>Next</button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: API Credentials / Connector Builder */}
                                {baselineStep === 2 && selectedVendor?.id === 'universal' && (
                                    <div>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Build Your Own Connector</h3>
                                        <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 16 }}>
                                            Enter the API details for your security tool. We'll call it and discover the available fields.
                                        </div>

                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Tool Name *</label>
                                            <input className="ap-input" value={toolForm.name || ''} onChange={e => updateToolForm({ name: e.target.value })} placeholder="e.g. Tenable, Qualys, Intune" />
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Tool Type *</label>
                                            <select className="ap-input" value={toolForm.tool_type || 'edr'} onChange={e => updateToolForm({ tool_type: e.target.value })}>
                                                {[['edr', 'EDR / Endpoint Protection'], ['siem', 'SIEM'], ['dlp', 'DLP'], ['vuln_scanner', 'Vulnerability Scanner'], ['backup', 'Backup Solution'], ['encryption', 'Encryption / DRM'], ['pam', 'PAM'], ['mfa', 'MFA / Identity'], ['patch_mgmt', 'Patch Management'], ['custom', 'Custom']].map(([v, l]) => (
                                                    <option key={v} value={v}>{l}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={{ padding: 12, background: '#f8f9fb', border: '1px solid #d8dce3', borderRadius: 6, marginBottom: 16 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 10 }}>API Connection</div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Base URL *</label>
                                                    <input className="ap-input" value={connectorForm.base_url} onChange={e => updateConnectorForm({ base_url: e.target.value })} placeholder="https://api.example.com" style={{ marginBottom: 0 }} />
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Device List Endpoint</label>
                                                    <input className="ap-input" value={connectorForm.endpoint} onChange={e => updateConnectorForm({ endpoint: e.target.value })} placeholder="e.g. /api/v1/agents" style={{ marginBottom: 0 }} />
                                                </div>
                                            </div>

                                            <div style={{ marginBottom: 10 }}>
                                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Authentication Type</label>
                                                <select className="ap-input" value={connectorForm.auth_type} onChange={e => updateConnectorForm({ auth_type: e.target.value })} style={{ marginBottom: 0 }}>
                                                    <option value="none">No Auth</option>
                                                    <option value="basic">Basic Auth (Username + Password)</option>
                                                    <option value="api_key">API Key / Token</option>
                                                    <option value="bearer">Bearer Token</option>
                                                    <option value="oauth2">OAuth2 Client Credentials</option>
                                                </select>
                                            </div>

                                            {connectorForm.auth_type === 'basic' && (
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Username</label>
                                                        <input className="ap-input" value={connectorForm.username} onChange={e => updateConnectorForm({ username: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Password</label>
                                                        <input className="ap-input" type="password" value={connectorForm.password} onChange={e => updateConnectorForm({ password: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                                </div>
                                            )}
                                            {connectorForm.auth_type === 'api_key' && (
                                                <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>API Key</label>
                                                    <input className="ap-input" type="password" value={connectorForm.api_key} onChange={e => updateConnectorForm({ api_key: e.target.value })} placeholder="Sent as: ApiToken {key}" style={{ marginBottom: 0 }} /></div>
                                            )}
                                            {connectorForm.auth_type === 'bearer' && (
                                                <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Bearer Token</label>
                                                    <input className="ap-input" type="password" value={connectorForm.token} onChange={e => updateConnectorForm({ token: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                            )}
                                            {connectorForm.auth_type === 'oauth2' && (
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Token URL *</label>
                                                        <input className="ap-input" value={connectorForm.oauth2_token_url} onChange={e => updateConnectorForm({ oauth2_token_url: e.target.value })} placeholder="https://login.example.com/oauth2/token" style={{ marginBottom: 0 }} /></div>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Scope (optional)</label>
                                                        <input className="ap-input" value={connectorForm.oauth2_scope} onChange={e => updateConnectorForm({ oauth2_scope: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Client ID *</label>
                                                        <input className="ap-input" value={connectorForm.oauth2_client_id} onChange={e => updateConnectorForm({ oauth2_client_id: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                                    <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Client Secret *</label>
                                                        <input className="ap-input" type="password" value={connectorForm.oauth2_client_secret} onChange={e => updateConnectorForm({ oauth2_client_secret: e.target.value })} style={{ marginBottom: 0 }} /></div>
                                                </div>
                                            )}
                                        </div>

                                        {previewError && (
                                            <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.6, background: '#dc262608', border: '1px solid #dc262630', color: '#dc2626' }}>
                                                <div style={{ fontWeight: 700, marginBottom: 2 }}>Connection Failed</div>{previewError}
                                            </div>
                                        )}

                                        {discoveredData && (
                                            <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 12, background: '#0f7b4f08', border: '1px solid #0f7b4f30', color: '#0f7b4f' }}>
                                                Found <strong>{discoveredData.total_devices}</strong> devices with <strong>{discoveredData.fields?.length}</strong> fields at path: <code>{discoveredData.array_path}</code>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                                            <button className="ap-btn ap-btn--ghost" onClick={() => setBaselineStep(1)}>Back</button>
                                            <button className="ap-btn ap-btn--primary" disabled={previewLoading || !connectorForm.base_url || !toolForm.name}
                                                onClick={async () => {
                                                    setPreviewError(''); setPreviewLoading(true); setDiscoveredData(null);
                                                    try {
                                                        const r = await svc('kri_universal_discover_fields', connectorForm);
                                                        if (r?.statusCode?.status === 0 || r?.errors?.message) {
                                                            setPreviewError(r?.errors?.message || r?.statusCode?.error?.message || 'Discovery failed.');
                                                        } else {
                                                            const d = r?.statusCode?.response_data?.data;
                                                            if (d && d.total_devices > 0) {
                                                                setDiscoveredData(d);
                                                                // Auto-trigger AI mapping
                                                                setAiMapping(true);
                                                                try {
                                                                    const mr = await svc('kri_ai_map_universal_fields', { fields: d.fields, sample_devices: d.sample_devices, tool_name: toolForm.name });
                                                                    const md = mr?.statusCode?.response_data?.data;
                                                                    if (md?.mapping) setFieldMapping(md.mapping);
                                                                } catch { }
                                                                setAiMapping(false);
                                                                setBaselineStep(3);
                                                            } else {
                                                                setPreviewError(r?.errors?.message || 'No devices found. Check the endpoint path.');
                                                            }
                                                        }
                                                    } catch (e) {
                                                        setPreviewError(e?.response?.data?.error?.message || e?.message || 'Discovery failed.');
                                                    }
                                                    setPreviewLoading(false);
                                                }}>
                                                {previewLoading ? (aiMapping ? 'AI mapping fields...' : 'Discovering API...') : 'Discover & Map Fields'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: API Credentials (existing vendors + custom) */}
                                {baselineStep === 2 && selectedVendor?.id !== 'universal' && (
                                    <div>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                                            {selectedVendor?.id === 'custom' ? 'Tool Details' : `API Credentials - ${selectedVendor?.name}`}
                                        </h3>
                                        <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 16 }}>
                                            {selectedVendor?.id === 'custom'
                                                ? 'Name your tool and select the type. You will upload the tool\'s device list as a CSV in the next step.'
                                                : `Enter the API credentials to connect to your ${selectedVendor?.name} instance.`}
                                        </div>

                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Display Name *</label>
                                            <input className="ap-input" value={toolForm.name || ''} onChange={e => updateToolForm({ name: e.target.value })} placeholder={selectedVendor?.name || 'My Tool'} />
                                        </div>

                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Tool Type *</label>
                                            <select className="ap-input" value={toolForm.tool_type || 'edr'} onChange={e => updateToolForm({ tool_type: e.target.value })}>
                                                {[['edr', 'EDR / Endpoint Protection'], ['siem', 'SIEM'], ['dlp', 'DLP'], ['vuln_scanner', 'Vulnerability Scanner'], ['backup', 'Backup Solution'], ['encryption', 'Encryption / DRM'], ['pam', 'Privileged Access Management'], ['mfa', 'MFA / Identity'], ['patch_mgmt', 'Patch Management'], ['custom', 'Custom']].map(([v, l]) => (
                                                    <option key={v} value={v}>{l}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Dynamic vendor-specific fields */}
                                        {(selectedVendor?.setup_fields || []).map(f => {
                                            const key = f.key.includes('.') ? f.key : f.key;
                                            const getValue = () => {
                                                if (f.key.startsWith('additional_config.')) {
                                                    const subKey = f.key.split('.')[1];
                                                    return (toolForm.additional_config || {})[subKey] || '';
                                                }
                                                return toolForm[f.key] || '';
                                            };
                                            const setValue = (val) => {
                                                if (f.key.startsWith('additional_config.')) {
                                                    const subKey = f.key.split('.')[1];
                                                    // Use prev inside the functional setter so the additional_config
                                                    // merge always sees the current state, not a captured copy.
                                                    setToolForm(prev => ({ ...prev, additional_config: { ...(prev.additional_config || {}), [subKey]: val } }));
                                                } else {
                                                    updateToolForm({ [f.key]: val });
                                                }
                                            };
                                            const isApiBaseUrl = (f.key === 'api_base_url') && f.type !== 'select';
                                            return (
                                                <div key={key} style={{ marginBottom: 10, position: 'relative' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                                        <label style={{ fontSize: 11, fontWeight: 600 }}>
                                                            {f.label} {f.required && '*'}
                                                        </label>
                                                        {isApiBaseUrl && (
                                                            <button type="button"
                                                                onClick={runUrlSuggestion}
                                                                disabled={urlSuggestionLoading}
                                                                title="Ask NOVA AI for known-good API URLs for this vendor"
                                                                style={{
                                                                    background: 'transparent', border: '1px solid #7c3aed', color: '#7c3aed',
                                                                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                                                                    display: 'inline-flex', gap: 4, alignItems: 'center'
                                                                }}>
                                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 2, background: '#7c3aed', color: '#fff' }}>NOVA AI</span>
                                                                Suggest URL
                                                            </button>
                                                        )}
                                                    </div>
                                                    {f.type === 'select' ? (
                                                        <select className="ap-input" value={getValue()} onChange={e => setValue(e.target.value)}>
                                                            <option value="">-- Select --</option>
                                                            {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                        </select>
                                                    ) : (
                                                        <input className="ap-input" type={f.type === 'password' ? 'password' : 'text'}
                                                            value={getValue()} onChange={e => setValue(e.target.value)}
                                                            placeholder={f.placeholder || ''} />
                                                    )}
                                                    {f.hint && f.type !== 'password' && <div style={{ fontSize: 10, color: '#7a869a', marginTop: 2 }}>{f.hint}</div>}

                                                    {/* NOVA AI URL suggestion popover */}
                                                    {isApiBaseUrl && urlSuggestionOpen && (
                                                        <div style={{ marginTop: 8, background: '#fff', border: '1px solid #d8c8f5', borderRadius: 6, padding: 10, boxShadow: '0 4px 12px rgba(124,58,237,0.12)' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a' }}>API URL Suggestions</span>
                                                                    {urlSuggestion?.confidence === 'heuristic' && (
                                                                        <span style={{ fontSize: 9, color: '#d97706' }}>• generic pattern</span>
                                                                    )}
                                                                </div>
                                                                <button type="button" aria-label="Close" onClick={() => setUrlSuggestionOpen(false)}
                                                                    style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#7a869a', cursor: 'pointer', lineHeight: 1 }}>×</button>
                                                            </div>
                                                            {urlSuggestionLoading && (
                                                                <div style={{ fontSize: 11, color: '#7c3aed' }}>Looking up known-good URLs for {selectedVendor?.name || 'this vendor'}…</div>
                                                            )}
                                                            {!urlSuggestionLoading && urlSuggestion?.error && (
                                                                <div style={{ fontSize: 11, color: '#dc2626' }}>{urlSuggestion.error}</div>
                                                            )}
                                                            {!urlSuggestionLoading && urlSuggestion?.suggestions?.length === 0 && (
                                                                <div style={{ fontSize: 11, color: '#7a869a' }}>No matches found. Check the vendor's API documentation.</div>
                                                            )}
                                                            {!urlSuggestionLoading && urlSuggestion?.suggestions?.length > 0 && (
                                                                <div style={{ display: 'grid', gap: 6 }}>
                                                                    {urlSuggestion.suggestions.map((s, i) => (
                                                                        <div key={i} style={{ border: '1px solid #e5e7f0', borderRadius: 5, padding: '7px 9px' }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                                                                                <code style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a', wordBreak: 'break-all' }}>{s.url}</code>
                                                                                <button type="button" onClick={() => applyUrlSuggestion(s.url)}
                                                                                    style={{ background: '#7c3aed', color: '#fff', border: 'none', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                                    Use this
                                                                                </button>
                                                                            </div>
                                                                            <div style={{ fontSize: 10, color: '#3d4f6f' }}>
                                                                                <strong>{s.label}</strong> — {s.when_to_use}
                                                                            </div>
                                                                            {s.warning && (
                                                                                <div style={{ fontSize: 10, color: '#d97706', marginTop: 3 }}>⚠ {s.warning}</div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {!urlSuggestionLoading && urlSuggestion?.note && (
                                                                <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6, fontStyle: 'italic' }}>{urlSuggestion.note}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Task polling loader */}
                                        {previewLoading && previewTaskId && (
                                            <FullPageLoader loading={previewLoading && previewTaskId} message="Connecting to the tool and fetching preview data…" />
                                            // <TaskLoader message="Connecting to the tool and fetching preview data…" />
                                        )}

                                        {/* Inline error/success display */}
                                        {(testResult || previewError) && (
                                            <div style={{
                                                padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.6,
                                                background: (testResult?.success) ? '#0f7b4f08' : '#dc262608',
                                                border: `1px solid ${(testResult?.success) ? '#0f7b4f30' : '#dc262630'}`,
                                                color: (testResult?.success) ? '#0f7b4f' : '#dc2626'
                                            }}>
                                                <div style={{ fontWeight: 700, marginBottom: 2 }}>{(testResult?.success) ? 'Connection Successful' : 'Connection Failed'}</div>
                                                {previewError || testResult?.message}

                                                {/* Diagnose with NOVA AI — only on failure */}
                                                {!testResult?.success && (previewError || testResult?.message) && (
                                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #dc262640' }}>
                                                        {/* {!diagnose && !diagnoseLoading && (
                                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, color: '#7c3aed', borderColor: '#7c3aed' }}
                                                                onClick={async () => {
                                                                    setDiagnoseLoading(true); setDiagnose(null);
                                                                    try {
                                                                        const { kri_ai_diagnose_connection_failure } = await import('../services');
                                                                        const r = await kri_ai_diagnose_connection_failure({
                                                                            vendor: toolForm.vendor || selectedVendor?.id || '',
                                                                            url: toolForm.api_base_url || connectorForm?.base_url || '',
                                                                            error: previewError || testResult?.message || '',
                                                                        });
                                                                        const d = r?.statusCode?.response_data?.data || r?.response_data?.data;
                                                                        setDiagnose(d || { error: 'No response' });
                                                                    } catch (err) { setDiagnose({ error: err?.message || 'Diagnose failed' }); }
                                                                    setDiagnoseLoading(false);
                                                                }}>
                                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: '#7c3aed', color: '#fff', marginRight: 6 }}>NOVA AI</span>
                                                                Diagnose this failure
                                                            </button>
                                                        )} */}
                                                        {/* {diagnoseLoading && (
                                                            <div style={{ fontSize: 11, color: '#7c3aed' }}>NOVA AI is probing the network and analysing the error…</div>
                                                        )} */}
                                                        {diagnose && !diagnose.error && (
                                                            <div style={{ background: '#fff', border: '1px solid #d8c8f5', borderRadius: 6, padding: 10, marginTop: 4 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                                    <span style={{
                                                                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff',
                                                                        background: diagnose.confidence === 'high' ? '#0f7b4f' : diagnose.confidence === 'medium' ? '#d97706' : '#dc2626'
                                                                    }}>
                                                                        {String(diagnose.confidence || '').toUpperCase()} CONFIDENCE
                                                                    </span>
                                                                    {diagnose.error_category && (
                                                                        <span style={{ fontSize: 9, color: '#7a869a' }}>
                                                                            • {diagnose.error_category.replace(/_/g, ' ')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: 11, color: '#1b2a4a', marginBottom: 6 }}>
                                                                    <strong>Diagnosis:</strong> {diagnose.diagnosis}
                                                                </div>
                                                                <div style={{ fontSize: 11, color: '#1b2a4a', marginBottom: 6 }}>
                                                                    <strong>Suggested fix:</strong> {diagnose.suggested_fix}
                                                                </div>
                                                                {diagnose.probe_results && Object.keys(diagnose.probe_results).length > 0 && (
                                                                    <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 8 }}>
                                                                        Port probe: {Object.entries(diagnose.probe_results).map(([port, status]) => (
                                                                            <span key={port} style={{ marginRight: 8, color: status === 'open' ? '#0f7b4f' : '#dc2626' }}>
                                                                                {port}: {status}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {diagnose.suggested_url && (
                                                                    <button className="ap-btn ap-btn--primary" style={{ background: '#7c3aed', fontSize: 11 }}
                                                                        onClick={() => {
                                                                            updateToolForm({ api_base_url: diagnose.suggested_url });
                                                                            setDiagnose(null);
                                                                            setPreviewError(''); setTestResult(null);
                                                                        }}>
                                                                        Apply: {diagnose.suggested_url}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                        {diagnose?.error && (
                                                            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Diagnose failed: {diagnose.error}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                                            <button className="ap-btn ap-btn--ghost" onClick={() => setBaselineStep(1)}>Back</button>
                                            {selectedVendor?.id === 'custom' ? (
                                                <button className="ap-btn ap-btn--primary" disabled={!toolForm.name}
                                                    onClick={() => setBaselineStep(4)}>
                                                    Next - Configure Baseline
                                                </button>
                                            ) : (
                                                <button className="ap-btn ap-btn--primary" disabled={previewLoading}
                                                    onClick={async () => {
                                                        setPreviewError(''); setTestResult(null);
                                                        const setupFields = selectedVendor?.setup_fields || [];
                                                        // Validate all required fields are filled before hitting the API
                                                        const missing = setupFields.filter(f => {
                                                            if (!f.required) return false;
                                                            const val = f.key.startsWith('additional_config.')
                                                                ? (toolForm.additional_config || {})[f.key.split('.')[1]]
                                                                : toolForm[f.key];
                                                            return !val || !String(val).trim();
                                                        });
                                                        if (missing.length > 0) {
                                                            setPreviewError(`Please fill in required fields: ${missing.map(f => f.label).join(', ')}`);
                                                            return;
                                                        }
                                                        const requiresUrl = setupFields.some(f => f.key === 'api_base_url');
                                                        const url = (toolForm.api_base_url || '').trim();
                                                        if (requiresUrl) {
                                                            if (!url) { setPreviewError('API URL is required.'); return; }
                                                            if (!url.startsWith('http://') && !url.startsWith('https://')) { setPreviewError('URL must start with http:// or https://'); return; }
                                                            if (url.includes(' ')) { setPreviewError(`URL contains a space. Did you mean "${url.replace(/\s+(\d+)/, ':$1')}"? Use a colon (:) before the port number.`); return; }
                                                        }
                                                        setPreviewLoading(true); setPreviewData(null); setPreviewTaskId(null);
                                                        try {
                                                            // Only send fields relevant to this vendor — strips stale keys from previously-selected vendors
                                                            const vendorKeys = new Set(['vendor', 'name', 'auth_type', 'tool_type', 'api_base_url', 'additional_config',
                                                                ...setupFields.map(f => f.key.includes('.') ? f.key.split('.')[0] : f.key)]);
                                                            const cleanPayload = Object.fromEntries(
                                                                Object.entries({ ...toolForm, api_base_url: url }).filter(([k]) => vendorKeys.has(k))
                                                            );
                                                            const r = await svc('kri_preview_tool_data', cleanPayload);
                                                            if (r?.statusCode?.status === 0 || r?.errors?.message) {
                                                                setPreviewError(r?.errors?.message || r?.statusCode?.error?.message || 'Connection failed. Check credentials and URL.');
                                                                setPreviewLoading(false);
                                                                return;
                                                            }
                                                            const initData = r?.statusCode?.response_data?.data;
                                                            const taskId = initData?.task_id;
                                                            if (!taskId) {
                                                                // Legacy sync response — process immediately
                                                                if (initData && initData.total_devices !== undefined) {
                                                                    setPreviewData(initData);
                                                                    setSelectedFields((initData.available_fields || []).map(f => f.field));
                                                                    setPreviewError('');
                                                                    setBaselineStep(3);
                                                                } else {
                                                                    setPreviewError(r?.statusCode?.error?.message || 'No data returned. Check credentials and URL.');
                                                                }
                                                                setPreviewLoading(false);
                                                                return;
                                                            }
                                                            setPreviewTaskId(taskId);
                                                            // Poll status until task completes
                                                            const poll = async () => {
                                                                try {
                                                                    const sr = await svc('kri_preview_tool_status', taskId);
                                                                    if (sr?.statusCode?.status === 0 || sr?.errors?.message) {
                                                                        setPreviewError(sr?.errors?.message || sr?.statusCode?.error?.message || 'Connection failed. Check credentials and URL.');
                                                                        setPreviewLoading(false);
                                                                        setPreviewTaskId(null);
                                                                        return;
                                                                    }
                                                                    const sd = sr?.statusCode?.response_data?.data;
                                                                    const state = sd?.state;
                                                                    if (!state || state === 'PENDING' || state === 'STARTED') {
                                                                        setTimeout(poll, 5000);
                                                                        return;
                                                                    }
                                                                    setPreviewTaskId(null);
                                                                    if (state === 'SUCCESS' || state === 'COMPLETED') {
                                                                        const d = sd?.result || sd;
                                                                        if (d && d.total_devices !== undefined) {
                                                                            setPreviewData(d);
                                                                            setSelectedFields((d.available_fields || []).map(f => f.field));
                                                                            setPreviewError('');
                                                                            setBaselineStep(3);
                                                                        } else {
                                                                            setPreviewError('No data returned. Check credentials and URL.');
                                                                        }
                                                                    } else {
                                                                        setPreviewError(sd?.error || sd?.result?.error || 'Connection failed. Check credentials and URL.');
                                                                    }
                                                                } catch (pe) {
                                                                    setPreviewLoading(false);
                                                                    setPreviewTaskId(null);
                                                                    setPreviewError(pe?.message || 'Failed to fetch preview status.');
                                                                }
                                                                setPreviewLoading(false);
                                                            };
                                                            poll();
                                                        } catch (e) {
                                                            const errData = e?.response?.data;
                                                            const errMsg = errData?.error?.message
                                                                || errData?.response_data?.error_msg
                                                                || e?.message
                                                                || 'Failed to connect. Check URL and credentials.';
                                                            setPreviewError(errMsg);
                                                            setPreviewLoading(false);
                                                        }
                                                    }}>
                                                    {previewLoading ? 'Connecting & Fetching Data...' : 'Next - Preview Data'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Universal Connector - Field Mapping */}
                                {baselineStep === 3 && selectedVendor?.id === 'universal' && discoveredData && (
                                    <div>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Map Fields</h3>
                                        <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 12 }}>
                                            {discoveredData.total_devices} devices found. Map the API fields to our standard format.
                                            {Object.values(fieldMapping).filter(v => v).length > 0 && (
                                                <span style={{ color: '#0f7b4f', fontWeight: 600 }}> AI has pre-filled the mappings.</span>
                                            )}
                                        </div>

                                        {/* Field mapping table */}
                                        <div style={{ border: '1px solid #d8dce3', borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                <thead><tr style={{ background: '#f8f9fb' }}>
                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a', width: '30%' }}>OUR FIELD</th>
                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a', width: '40%' }}>MAP TO API FIELD</th>
                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a' }}>SAMPLE VALUE</th>
                                                </tr></thead>
                                                <tbody>
                                                    {[
                                                        { key: 'hostname', label: 'Hostname', required: true },
                                                        { key: 'ip_address', label: 'IP Address' },
                                                        { key: 'mac_address', label: 'MAC Address' },
                                                        { key: 'serial_number', label: 'Serial Number' },
                                                        { key: 'os_type', label: 'OS Type' },
                                                        { key: 'os_version', label: 'OS Version' },
                                                        { key: 'agent_version', label: 'Agent Version' },
                                                        { key: 'agent_status', label: 'Agent Status' },
                                                        { key: 'last_seen', label: 'Last Seen' },
                                                        { key: 'device_id', label: 'Device ID' },
                                                    ].map(f => {
                                                        const mapped = fieldMapping[f.key] || '';
                                                        const sample = mapped ? (discoveredData.sample_devices?.[0] || {})[mapped] || '' : '';
                                                        return (
                                                            <tr key={f.key} style={{ borderTop: '1px solid #f0f2f5' }}>
                                                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1b2a4a' }}>
                                                                    {f.label} {f.required && <span style={{ color: '#dc2626' }}>*</span>}
                                                                </td>
                                                                <td style={{ padding: '8px 12px' }}>
                                                                    <select style={{ width: '100%', padding: '4px 8px', fontSize: 11, border: '1px solid #d8dce3', borderRadius: 4, background: mapped ? '#0f7b4f08' : '#fff' }}
                                                                        value={mapped} onChange={e => setFieldMapping({ ...fieldMapping, [f.key]: e.target.value })}>
                                                                        <option value="">-- Not mapped --</option>
                                                                        {(discoveredData.fields || []).map(df => (
                                                                            <option key={df.path} value={df.path}>{df.path} ({df.type})</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                                <td style={{ padding: '8px 12px', color: '#7a869a', fontSize: 10, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {typeof sample === 'object' ? JSON.stringify(sample) : String(sample || '-')}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Sample data preview */}
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a', marginBottom: 6 }}>
                                                Data Preview (first {discoveredData.sample_devices?.length || 0} devices)
                                            </div>
                                            <div style={{ overflowX: 'auto', border: '1px solid #d8dce3', borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                    <thead><tr style={{ background: '#f8f9fb', position: 'sticky', top: 0 }}>
                                                        {Object.entries(fieldMapping).filter(([, v]) => v).map(([k]) => (
                                                            <th key={k} style={{ padding: '5px 8px', fontWeight: 700, color: '#7a869a', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid #d8dce3' }}>{k}</th>
                                                        ))}
                                                    </tr></thead>
                                                    <tbody>
                                                        {(discoveredData.sample_devices || []).map((d, i) => (
                                                            <tr key={i} style={{ borderBottom: '1px solid #f0f2f5' }}>
                                                                {Object.entries(fieldMapping).filter(([, v]) => v).map(([k, path]) => {
                                                                    let val = d;
                                                                    for (const p of path.split('.')) val = val?.[p];
                                                                    return <td key={k} style={{ padding: '4px 8px', color: '#3d4f6f', whiteSpace: 'nowrap' }}>{val != null ? String(val) : '-'}</td>;
                                                                })}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                            <button className="ap-btn ap-btn--ghost" onClick={() => setBaselineStep(2)}>Back</button>
                                            <button className="ap-btn ap-btn--primary" disabled={!fieldMapping.hostname}
                                                onClick={() => {
                                                    // Store connector config and mapping in toolForm
                                                    setToolForm({
                                                        ...toolForm,
                                                        vendor: 'universal',
                                                        api_base_url: connectorForm.base_url,
                                                        auth_type: connectorForm.auth_type === 'api_key' ? 'api_key' : connectorForm.auth_type === 'bearer' ? 'token' : connectorForm.auth_type,
                                                        api_client_id: connectorForm.auth_type === 'oauth2' ? connectorForm.oauth2_client_id : connectorForm.username,
                                                        api_client_secret: connectorForm.auth_type === 'oauth2' ? connectorForm.oauth2_client_secret : connectorForm.password,
                                                        api_key: connectorForm.api_key || connectorForm.token,
                                                        additional_config: {
                                                            ...toolForm.additional_config,
                                                            device_endpoint: connectorForm.endpoint,
                                                            array_path: discoveredData.array_path,
                                                            field_mapping: fieldMapping,
                                                            oauth2_token_url: connectorForm.oauth2_token_url,
                                                            oauth2_scope: connectorForm.oauth2_scope,
                                                        },
                                                        baseline_tool_field: 'hostname',
                                                    });
                                                    setBaselineStep(4);
                                                }}>
                                                Next - Set Baseline ({Object.values(fieldMapping).filter(v => v).length} fields mapped)
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Preview Data & Select Fields (existing vendors) */}
                                {baselineStep === 3 && selectedVendor?.id !== 'universal' && previewData && (
                                    <div>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Preview Data -{selectedVendor?.name}</h3>

                                        {/* Connection status */}
                                        <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontSize: 11, background: '#0f7b4f10', border: '1px solid #0f7b4f30', color: '#0f7b4f' }}>
                                            {previewData.connection_message}
                                        </div>

                                        {/* Field Selection */}
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>Available Fields ({previewData.available_fields?.length || 0})</div>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 10px' }}
                                                        onClick={() => setSelectedFields((previewData.available_fields || []).map(f => f.field))}>Select All</button>
                                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 10px' }}
                                                        onClick={() => setSelectedFields([])}>Clear All</button>
                                                </div>
                                            </div>
                                            <div style={{ border: '1px solid #d8dce3', borderRadius: 6, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8f9fb', position: 'sticky', top: 0 }}>
                                                            <th style={{ padding: '6px 10px', textAlign: 'left', width: 30 }}></th>
                                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a' }}>FIELD</th>
                                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a' }}>COVERAGE</th>
                                                            <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#7a869a' }}>SAMPLE VALUES</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(previewData.available_fields || []).map(f => {
                                                            const checked = selectedFields.includes(f.field);
                                                            const pct = f.total_count > 0 ? Math.round(f.populated_count / f.total_count * 100) : 0;
                                                            return (
                                                                <tr key={f.field} style={{ borderBottom: '1px solid #f0f2f5', cursor: 'pointer', background: checked ? '#f0f7ff' : '#fff' }}
                                                                    onClick={() => setSelectedFields(prev => checked ? prev.filter(x => x !== f.field) : [...prev, f.field])}>
                                                                    <td style={{ padding: '6px 10px' }}>
                                                                        <input type="checkbox" checked={checked} readOnly style={{ cursor: 'pointer' }} />
                                                                    </td>
                                                                    <td style={{ padding: '6px 10px', fontWeight: 600, color: '#1b2a4a' }}>{f.field}</td>
                                                                    <td style={{ padding: '6px 10px' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                            <div style={{ width: 50, height: 4, background: '#f0f2f5', borderRadius: 2, overflow: 'hidden' }}>
                                                                                <div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? '#0f7b4f' : pct > 50 ? '#d97706' : '#dc2626', borderRadius: 2 }} />
                                                                            </div>
                                                                            <span style={{ fontSize: 10, color: '#7a869a' }}>{pct}%</span>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '6px 10px', color: '#7a869a', fontSize: 10, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {f.sample_values?.join(', ') || '-'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4 }}>{selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected for onboarding</div>
                                        </div>

                                        {/* Data Preview Table */}
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 8 }}>
                                                Data Preview (first {previewData.preview_rows?.length || 0} of {previewData.total_devices} devices)
                                            </div>
                                            <div style={{ overflowX: 'auto', border: '1px solid #d8dce3', borderRadius: 6, maxHeight: 300, overflowY: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8f9fb', position: 'sticky', top: 0 }}>
                                                            {selectedFields.map(f => (
                                                                <th key={f} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#7a869a', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid #d8dce3' }}>{f}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(previewData.preview_rows || []).map((row, i) => (
                                                            <tr key={i} style={{ borderBottom: '1px solid #f0f2f5' }}>
                                                                {selectedFields.map(f => (
                                                                    <td key={f} style={{ padding: '5px 10px', color: '#3d4f6f', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                        {typeof row[f] === 'object' ? JSON.stringify(row[f]) : String(row[f] ?? '-')}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Store selected fields in toolForm */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                                            <button className="ap-btn ap-btn--ghost" onClick={() => setBaselineStep(2)}>Back</button>
                                            <button className="ap-btn ap-btn--primary" disabled={selectedFields.length === 0}
                                                onClick={() => { updateToolForm({ selected_fields: selectedFields }); setBaselineStep(4); }}>
                                                Next -Set Baseline ({selectedFields.length} fields)
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Baseline Configuration (or Step 3 for Custom) */}
                                {baselineStep === 4 && (() => {
                                    // Resolve active category once so every Step 4 sub-section uses it.
                                    const isUserCategoryPath_S4 = (selectedVendor?.id === 'universal' || selectedVendor?.id === 'custom');
                                    const activeCategory_S4 = isUserCategoryPath_S4
                                        ? (toolForm.tool_type || 'custom')
                                        : (selectedVendor?.tool_category || toolForm.tool_type || 'custom');
                                    const csvHints = getCsvUploadHints(activeCategory_S4);
                                    const isTicketShape = csvHints.match_kind === 'ticket';
                                    const isAssetShape = csvHints.match_kind === 'asset';
                                    return (
                                        <div>
                                            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                                                {selectedVendor?.id === 'custom' ? 'Baseline & Device Upload' : 'What we will monitor'}
                                            </h3>
                                            <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 16 }}>
                                                {selectedVendor?.id === 'custom'
                                                    ? 'Upload a CSV of devices managed by this tool. We will compare it against your asset baseline to find gaps.'
                                                    : `NOVA AI has analysed the data from ${selectedVendor?.name} and configured monitoring on your behalf. Review and save, or customise.`}
                                            </div>

                                            {/* NOVA AI auto-configuration summary card — replaces the blank form by default */}
                                            {selectedVendor?.id !== 'custom' && (
                                                <>
                                                    {autoConfigLoading && (
                                                        <div style={{ padding: 16, marginBottom: 16, border: '1px solid #d8c8f5', borderRadius: 6, background: '#faf7ff', fontSize: 12, color: '#7c3aed' }}>
                                                            NOVA AI is analysing your {selectedVendor?.name} data and configuring monitoring…
                                                        </div>
                                                    )}
                                                    {autoConfig && !showCustomise && (
                                                        <div style={{ padding: 16, marginBottom: 16, border: '1px solid #d8c8f5', borderRadius: 8, background: 'linear-gradient(135deg, #faf7ff 0%, #f4ecff 100%)' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a' }}>Auto-Configured</span>
                                                                {autoConfig.config?.category_display && (
                                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#fff', background: '#1b2a4a' }}>
                                                                        {String(autoConfig.config.category_display).toUpperCase()}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Plain-English summary lines */}
                                                            {autoConfig.summary_lines?.length > 0 && (
                                                                <div style={{ marginBottom: 12 }}>
                                                                    {autoConfig.summary_lines.map((line, i) => (
                                                                        <div key={i} style={{ fontSize: 11, color: '#1b2a4a', marginBottom: 4, paddingLeft: 4 }}>
                                                                            <span style={{ color: '#0f7b4f', marginRight: 6 }}>✓</span>{line}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Detected issues — what's already wrong */}
                                                            {autoConfig.detected_issues?.length > 0 && (
                                                                <div style={{ padding: 10, marginTop: 10, background: '#fff5f0', border: '1px solid #ffd0b8', borderRadius: 6 }}>
                                                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>
                                                                        DETECTED IN YOUR DATA — INVESTIGATE AFTER SAVE
                                                                    </div>
                                                                    {autoConfig.detected_issues.map((issue, i) => (
                                                                        <div key={i} style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 3 }}>
                                                                            <span style={{ color: issue.severity === 'warning' ? '#d97706' : '#1b2a4a', marginRight: 6 }}>
                                                                                {issue.severity === 'warning' ? '⚠' : '•'}
                                                                            </span>
                                                                            {issue.label}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Concise config recap (read-only here; full form is in Customise) */}
                                                            {autoConfig.config && (
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                                                                    {[
                                                                        { label: 'Match by', value: autoConfig.config.match_field },
                                                                        { label: 'Thresholds', value: `${autoConfig.config.compliance_threshold}% / ${autoConfig.config.critical_threshold}%` },
                                                                        { label: 'In scope', value: (autoConfig.config.scope_filters?.os_types || []).join(', ') || 'All types' },
                                                                        { label: 'Sync', value: `Every ${autoConfig.config.sync_frequency_hours}h • ${autoConfig.config.sync_mode}` },
                                                                    ].map((kv, i) => (
                                                                        <div key={i} style={{ padding: 8, background: '#fff', border: '1px solid #d8c8f5', borderRadius: 4 }}>
                                                                            <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700 }}>{kv.label.toUpperCase()}</div>
                                                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#1b2a4a' }}>{kv.value || '—'}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            <div style={{ marginTop: 14, fontSize: 10, color: '#7a869a' }}>
                                                                These settings have been pre-filled in the form. Click <strong>Create Integration</strong> to save them, or <strong>Customise</strong> to adjust.
                                                            </div>

                                                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                                    onClick={() => setShowCustomise(true)}>
                                                                    Customise settings
                                                                </button>
                                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                                    onClick={() => { setAutoConfig(null); setAutoConfigLoading(false); /* triggers useEffect to re-run */ setTimeout(() => { /* noop, useEffect handles it */ }, 0); }}>
                                                                    Re-analyse
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Custom tool: CSV upload for tool's device/ticket/user list — text adapts to category */}
                                            {selectedVendor?.id === 'custom' && (
                                                <div style={{ padding: 16, background: '#f8f9fb', border: '1px solid #d8dce3', borderRadius: 6, marginBottom: 16 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 8 }}>{csvHints.title}</div>
                                                    <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 10 }}>
                                                        {csvHints.blurb}
                                                    </div>
                                                    <label className="ap-btn ap-btn--ghost" style={{ fontSize: 11, cursor: 'pointer', display: 'inline-block' }}>
                                                        Choose CSV File
                                                        <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => {
                                                            if (e.target.files[0]) updateToolForm({ _deviceFile: e.target.files[0] });
                                                        }} />
                                                    </label>
                                                    {toolForm._deviceFile && (
                                                        <span style={{ marginLeft: 10, fontSize: 11, color: '#0f7b4f', fontWeight: 600 }}>{toolForm._deviceFile.name}</span>
                                                    )}
                                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 8 }}
                                                        dangerouslySetInnerHTML={{ __html: 'CSV columns: ' + csvHints.columns_str }} />
                                                    {!isAssetShape && (
                                                        <div style={{ fontSize: 9, color: '#7c3aed', marginTop: 6, fontStyle: 'italic' }}>
                                                            NOVA AI auto-detects this shape from your CSV headers — no manual field mapping needed.
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Detailed configuration form — visible by default for 'custom' (CSV upload),
                                            and behind the Customise toggle for the AI-driven flow. Wraps everything
                                            from "How matching works" through Sync Mode + Health-check section. */}
                                            {(selectedVendor?.id === 'custom' || showCustomise || (!autoConfig && !autoConfigLoading)) && (<>
                                                {showCustomise && (
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10 }}
                                                            onClick={() => setShowCustomise(false)}>
                                                            Hide customisation — use NOVA AI defaults
                                                        </button>
                                                    </div>
                                                )}
                                                {/* Matching callout + dropdowns — only meaningful for asset-shape data.
                                            Ticket / user / secret / repo shapes use the row's natural unique key
                                            (Ticket Number / user_email / secret_id / repo_name) and do not match
                                            against an asset baseline. NOVA AI handles that automatically. */}
                                                {isAssetShape ? (
                                                    <>
                                                        <div style={{ padding: 12, background: '#f0f7ff', border: '1px solid #bfd7f5', borderRadius: 6, marginBottom: 16, fontSize: 11, color: '#1b2a4a' }}>
                                                            <strong>How matching works:</strong> We match each baseline asset to a device in {selectedVendor?.name} using your selected primary field.
                                                            If no primary match, we try fallback fields (hostname, IP, serial, MAC) automatically.
                                                            Match found = <span style={{ color: '#0f7b4f', fontWeight: 700 }}>Compliant</span>.
                                                            No match = <span style={{ color: '#dc2626', fontWeight: 700 }}>Gap</span>.
                                                            If baseline is empty on first sync, devices are auto-imported from the tool.
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                                            <div>
                                                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Match Baseline Asset By</label>
                                                                <select className="ap-input" value={toolForm.baseline_asset_field || 'hostname'} onChange={e => updateToolForm({ baseline_asset_field: e.target.value })}>
                                                                    <option value="hostname">Hostname</option>
                                                                    <option value="ip_address">IP Address</option>
                                                                    <option value="mac_address">MAC Address</option>
                                                                    <option value="serial_number">Serial Number</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Match Tool Device By</label>
                                                                <select className="ap-input" value={toolForm.baseline_tool_field || 'hostname'} onChange={e => updateToolForm({ baseline_tool_field: e.target.value })}>
                                                                    {(previewData?.match_fields || selectedVendor?.match_fields || ['hostname']).map(f => (
                                                                        <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={{ padding: 12, background: '#fbf9ff', border: '1px solid #d8c8f5', borderRadius: 6, marginBottom: 16, fontSize: 11, color: '#1b2a4a' }}>
                                                        <strong>How matching works for {getCategoryDisplay(activeCategory_S4)}:</strong> Each row in your CSV becomes one KRI compliance record keyed by <code>{csvHints.required_label}</code>.
                                                        {isTicketShape ? (
                                                            ' Tickets within their priority-tiered SLA count as Compliant; past-SLA tickets count as Past SLA; reopened tickets count as a quality KRI on top.'
                                                        ) : (
                                                            ' Each row is evaluated against the category\'s health rules — no asset-baseline matching needed for this shape.'
                                                        )}
                                                    </div>
                                                )}

                                                {(() => {
                                                    // Category resolution:
                                                    //   - Native adapter (CrowdStrike, Wazuh, etc.): vendor's declared category wins (locked)
                                                    //   - Universal Connector or CSV Upload Only: user picks via the category dropdown below
                                                    const isUserCategoryPath = (selectedVendor?.id === 'universal' || selectedVendor?.id === 'custom');
                                                    const activeCategory = isUserCategoryPath
                                                        ? (toolForm.tool_type || 'custom')
                                                        : (selectedVendor?.tool_category || toolForm.tool_type || 'custom');
                                                    const ui = getCategoryUI(activeCategory);
                                                    const [defComp, defCrit] = ui.thresholds;
                                                    return (
                                                        <>
                                                            {/* Tool Category indicator / picker */}
                                                            <div style={{
                                                                padding: 10, marginBottom: 14, borderRadius: 6,
                                                                background: isUserCategoryPath ? '#fbf9ff' : '#f8f9fb',
                                                                border: `1px solid ${isUserCategoryPath ? '#d8c8f5' : '#d8dce3'}`
                                                            }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#1b2a4a' }}>Tool Category:</span>
                                                                    {isUserCategoryPath ? (
                                                                        <select className="ap-input" style={{ width: 'auto', minWidth: 280, marginBottom: 0, fontSize: 12 }}
                                                                            value={activeCategory}
                                                                            onChange={e => updateToolForm({ tool_type: e.target.value })}>
                                                                            {Object.keys(CATEGORY_UI).map(key => (
                                                                                <option key={key} value={key}>{getCategoryDisplay(key)}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4, color: '#fff', background: '#1b2a4a' }}>
                                                                            {String(activeCategory).replace(/_/g, ' ').toUpperCase()}
                                                                            <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 400, opacity: 0.8 }}>(set by adapter)</span>
                                                                        </span>
                                                                    )}
                                                                    <span style={{ fontSize: 10, color: '#7a869a', flex: 1 }}>
                                                                        {isUserCategoryPath
                                                                            ? 'Pick the category that best matches your tool — the labels and defaults below adapt accordingly.'
                                                                            : `${selectedVendor?.name} is a ${activeCategory.replace(/_/g, ' ')} tool — category is set automatically.`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                                                <div>
                                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Compliance Threshold %</label>
                                                                    <input className="ap-input" type="number" min={0} max={100} value={toolForm.compliance_threshold ?? defComp}
                                                                        onChange={e => updateToolForm({ compliance_threshold: parseInt(e.target.value) })} />
                                                                    <div style={{ fontSize: 10, color: '#7a869a' }}>Target: above this = healthy &nbsp;<span style={{ color: '#7c3aed' }}>(default for this category: {defComp}%)</span></div>
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Critical Threshold %</label>
                                                                    <input className="ap-input" type="number" min={0} max={100} value={toolForm.critical_threshold ?? defCrit}
                                                                        onChange={e => updateToolForm({ critical_threshold: parseInt(e.target.value) })} />
                                                                    <div style={{ fontSize: 10, color: '#7a869a' }}>Below this = critical risk &nbsp;<span style={{ color: '#7c3aed' }}>(default for this category: {defCrit}%)</span></div>
                                                                </div>
                                                            </div>

                                                            <div style={{ marginBottom: 12 }}>
                                                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>{ui.asset_types_label}</label>
                                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                    {ui.asset_types.map(t => {
                                                                        const filters = toolForm.baseline_filters || {};
                                                                        const selected = (filters.asset_type || []).includes(t);
                                                                        return (
                                                                            <button key={t} onClick={() => {
                                                                                setToolForm(prev => {
                                                                                    const prevFilters = prev.baseline_filters || {};
                                                                                    const current = prevFilters.asset_type || [];
                                                                                    const next = current.includes(t) ? current.filter(x => x !== t) : [...current, t];
                                                                                    return { ...prev, baseline_filters: { ...prevFilters, asset_type: next } };
                                                                                });
                                                                            }} style={{
                                                                                padding: '4px 12px', fontSize: 10, fontWeight: 600, borderRadius: 12, cursor: 'pointer',
                                                                                border: `1px solid ${selected ? '#1b2a4a' : '#d8dce3'}`,
                                                                                background: selected ? '#1b2a4a' : '#fff', color: selected ? '#fff' : '#3d4f6f'
                                                                            }}>
                                                                                {t.replace(/_/g, ' ')}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4 }}>Leave empty to apply to all types</div>
                                                            </div>

                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                                                <div>
                                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Sync Frequency</label>
                                                                    <select className="ap-input" value={toolForm.sync_frequency_hours ?? ui.sync_hours_default ?? 6} onChange={e => updateToolForm({ sync_frequency_hours: parseInt(e.target.value) })}>
                                                                        <option value={1}>Every hour</option>
                                                                        <option value={6}>Every 6 hours (default)</option>
                                                                        <option value={12}>Every 12 hours</option>
                                                                        <option value={24}>Daily</option>
                                                                        <option value={168}>Weekly</option>
                                                                        <option value={0}>Never (manual sync only)</option>
                                                                    </select>
                                                                    <div style={{ fontSize: 10, color: '#7a869a' }}>
                                                                        Auto-sync runs at this cadence via the scheduler.
                                                                        {ui.sync_hours_default && ui.sync_hours_default !== 6 && (
                                                                            <span style={{ color: '#7c3aed', marginLeft: 4 }}>
                                                                                (recommended for {ui.label || 'this category'}: {ui.sync_hours_default >= 168 ? 'Weekly' : ui.sync_hours_default >= 24 ? 'Daily' : `Every ${ui.sync_hours_default}h`})
                                                                            </span>
                                                                        )}
                                                                        {' '}You can change this any time from the Tools tab.
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Sync Mode</label>
                                                                    <select className="ap-input" value={(toolForm.additional_config || {}).sync_mode || 'incremental'} onChange={e => setToolForm(prev => ({ ...prev, additional_config: { ...(prev.additional_config || {}), sync_mode: e.target.value } }))}>
                                                                        <option value="incremental">Incremental (faster, only changes)</option>
                                                                        <option value="full">Full (re-check all devices)</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {/* Per-tool record cap. Operators rarely need to touch
                                                this — the 100k default protects worker memory in
                                                the OOM scenario. Tenants with very large fleets
                                                (>100k assets in a single tool) can bump it; tenants
                                                with tight memory budgets can lower it. 0 = unlimited
                                                (skip the cap entirely). */}
                                                            <div style={{ marginBottom: 12 }}>
                                                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Records per Sync (max)</label>
                                                                <input
                                                                    type="number"
                                                                    className="ap-input"
                                                                    min={0}
                                                                    step={10000}
                                                                    value={toolForm.sync_max_records ?? 100000}
                                                                    onChange={e => updateToolForm({ sync_max_records: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                                                    placeholder="100000"
                                                                />
                                                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
                                                                    Upper bound on records consumed per sync run. Default 100,000 caps worker memory at ~500MB worst-case. Set to 0 for unlimited (only safe for small tenants). When the cap is reached, the sync stops cleanly and the Sync Logs row shows "CAP REACHED" — counts reflect a partial fetch.
                                                                </div>
                                                            </div>

                                                            {/* Sync strategy — three knobs that prevent the
                                                shrinking-incremental-window data corruption we
                                                hit on 2026-05-06. Defaults are sensible for
                                                most tenants; admins tune per-tool. */}
                                                            <div style={{ padding: 14, background: '#f8f9fb', border: '1px solid #d8dce3', borderRadius: 6, marginBottom: 12 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 8 }}>Sync strategy</div>
                                                                <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 10 }}>
                                                                    Three guardrails against data drift. Each is per-tool. 0 disables the gate.
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                                                    <div>
                                                                        <label style={{ fontSize: 10, fontWeight: 600, color: '#3d4f6f', display: 'block', marginBottom: 2 }}>Manual sync min interval (sec)</label>
                                                                        <input
                                                                            type="number" className="ap-input"
                                                                            min={0} step={60}
                                                                            value={toolForm.min_sync_interval_seconds ?? 300}
                                                                            onChange={e => updateToolForm({ min_sync_interval_seconds: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                                                            placeholder="300" style={{ fontSize: 11 }}
                                                                        />
                                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>
                                                                            Reject Sync clicks that arrive sooner than this after the previous successful sync. 300 = 5 min.
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <label style={{ fontSize: 10, fontWeight: 600, color: '#3d4f6f', display: 'block', marginBottom: 2 }}>Auto-promote to Full (days)</label>
                                                                        <input
                                                                            type="number" className="ap-input"
                                                                            min={0} step={1}
                                                                            value={toolForm.full_sync_interval_days ?? 7}
                                                                            onChange={e => updateToolForm({ full_sync_interval_days: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                                                            placeholder="7" style={{ fontSize: 11 }}
                                                                        />
                                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>
                                                                            When a Sync click arrives and last Full sync was &gt; this many days ago, automatically promote it to Full. Catches retired devices.
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <label style={{ fontSize: 10, fontWeight: 600, color: '#3d4f6f', display: 'block', marginBottom: 2 }}>Stuck sync threshold (sec)</label>
                                                                        <input
                                                                            type="number" className="ap-input"
                                                                            min={0} step={300}
                                                                            value={toolForm.sync_stuck_threshold_seconds ?? 7200}
                                                                            onChange={e => updateToolForm({ sync_stuck_threshold_seconds: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                                                            placeholder="7200" style={{ fontSize: 11 }}
                                                                        />
                                                                        <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>
                                                                            Watchdog auto-marks a sync 'aborted' after running this long. 7200 = 2h. Lower for fast-cycle tools.
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Category-aware health-compliance section */}
                                                            <div style={{ padding: 14, background: '#f8f9fb', border: '1px solid #d8dce3', borderRadius: 6, marginBottom: 12 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 8 }}>{ui.health.title}</div>
                                                                <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 10 }}>{ui.health.blurb}</div>
                                                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer' }}>
                                                                        <input type="checkbox"
                                                                            checked={(toolForm.additional_config || {}).health_config?.health_as_compliance !== false}
                                                                            onChange={e => setToolForm(prev => ({ ...prev, additional_config: { ...(prev.additional_config || {}), health_config: { ...(prev.additional_config?.health_config || {}), health_as_compliance: e.target.checked } } }))} />
                                                                        {ui.health.checkbox}
                                                                    </label>
                                                                </div>
                                                                {ui.health.threshold_label && (
                                                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                                        <label style={{ fontSize: 11, color: '#3d4f6f' }}>{ui.health.threshold_label}</label>
                                                                        <select className="ap-input" style={{ width: 160, marginBottom: 0 }}
                                                                            value={(toolForm.additional_config || {}).health_config?.offline_threshold_days ?? ui.health.threshold_default}
                                                                            onChange={e => setToolForm(prev => ({ ...prev, additional_config: { ...(prev.additional_config || {}), health_config: { ...(prev.additional_config?.health_config || {}), offline_threshold_days: parseInt(e.target.value) } } }))}>
                                                                            <option value={1}>1 day</option>
                                                                            <option value={3}>3 days</option>
                                                                            <option value={7}>7 days</option>
                                                                            <option value={14}>14 days</option>
                                                                            <option value={30}>30 days</option>
                                                                            <option value={60}>60 days</option>
                                                                            <option value={90}>90 days</option>
                                                                        </select>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </>)}
                                            {/* end of customisation-gated detailed form */}

                                            {previewError && (
                                                <div style={{
                                                    padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.6,
                                                    background: '#dc262608', border: '1px solid #dc262630', color: '#dc2626'
                                                }}>
                                                    <div style={{ fontWeight: 700, marginBottom: 2 }}>Failed to Create</div>
                                                    {previewError}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                                                <button className="ap-btn ap-btn--ghost" onClick={() => setBaselineStep(selectedVendor?.id === 'custom' ? 2 : 3)}>Back</button>
                                                <button className="ap-btn ap-btn--primary" onClick={() => { setPreviewError(''); createTool(); }} disabled={!toolForm.name}>
                                                    Create Integration
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Edit Tool Modal — focused on the runtime knobs operators retune
                        post-onboarding. Vendor and credentials are not editable here;
                        re-add the tool to change those (rare). */}
                    {editTool && (
                        <div className="ap-modal-overlay">
                            <div className="ap-modal" style={{ maxWidth: 480, position: 'relative' }}>
                                <button onClick={() => { setEditTool(null); setEditToolForm({}); }}
                                    aria-label="Close"
                                    style={{
                                        position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                        fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                                    }}>×</button>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>Edit Tool</div>
                                <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 14 }}>
                                    {editTool.vendor.replace(/_/g, ' ')} · {editTool.tool_type.replace(/_/g, ' ')}
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Display Name</label>
                                    <input className="ap-input" value={editToolForm.name || ''}
                                        onChange={e => setEditToolForm(p => ({ ...p, name: e.target.value }))} />
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Compliance Threshold (%)</label>
                                    <input className="ap-input" type="number" min={0} max={100}
                                        value={editToolForm.compliance_threshold ?? 95}
                                        onChange={e => setEditToolForm(p => ({ ...p, compliance_threshold: e.target.value }))} />
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
                                        Devices below this % count as gaps in the dashboard.
                                    </div>
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Records per Sync (max)</label>
                                    <input className="ap-input" type="number" min={0} step={10000}
                                        value={editToolForm.sync_max_records ?? 100000}
                                        onChange={e => setEditToolForm(p => ({ ...p, sync_max_records: e.target.value }))} />
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
                                        Default 100,000 caps worker memory at ~500MB worst-case. Bump it for tenants with &gt;100k assets in one tool. Set to 0 for unlimited (small tenants only). When the cap is hit, the Sync Logs row shows "CAP REACHED" and counts reflect a partial fetch.
                                    </div>
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Sync Frequency (hours)</label>
                                    <input className="ap-input" type="number" min={1}
                                        value={editToolForm.sync_frequency_hours ?? 6}
                                        onChange={e => setEditToolForm(p => ({ ...p, sync_frequency_hours: e.target.value }))} />
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
                                        How often the scheduled background sync runs. Manual Sync/Full ignore this.
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                                    <button className="ap-btn ap-btn--ghost"
                                        onClick={() => { setEditTool(null); setEditToolForm({}); }}>Cancel</button>
                                    <button className="ap-btn ap-btn--primary"
                                        onClick={saveEditTool}
                                        disabled={editToolSaving || !editToolForm.name}>
                                        {editToolSaving ? 'Saving…' : 'Save changes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ASSET BASELINE */}
            {tab === 'assets' && (
                <div className={`kri-tab-content ${tabRefreshing ? 'kri-tab-refreshing' : ''}`}>
                    {/* NOVA AI baseline data-quality audit */}
                    <div style={{ marginBottom: 12, background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', border: '1px solid #c7d2fe', borderRadius: 8, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', letterSpacing: 0.5, marginBottom: 4 }}>NOVA AI &middot; BASELINE AUDIT</div>
                                {aiBaselineLoading && <div style={{ fontSize: 12, color: '#3d4f6f' }}>Auditing the baseline for duplicates, missing fields, and stale rows...</div>}
                                {!aiBaselineLoading && !aiBaselineAudit && (
                                    <div style={{ fontSize: 12, color: '#3d4f6f' }}>Find duplicates, missing classifications, default-named hosts, and rows no tool has reported on.</div>
                                )}
                                {!aiBaselineLoading && aiBaselineAudit?.error && <div style={{ fontSize: 12, color: '#dc2626' }}>{aiBaselineAudit.error}</div>}
                                {!aiBaselineLoading && aiBaselineAudit && !aiBaselineAudit.error && (
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
                                            {aiBaselineAudit.health_score !== null && aiBaselineAudit.health_score !== undefined && (
                                                <div style={{ fontSize: 22, fontWeight: 800, color: aiBaselineAudit.health_score >= 80 ? '#0f7b4f' : aiBaselineAudit.health_score >= 60 ? '#d97706' : '#dc2626' }}>
                                                    {aiBaselineAudit.health_score}<span style={{ fontSize: 11, color: '#7a869a', fontWeight: 600 }}>/100</span>
                                                </div>
                                            )}
                                            <div style={{ fontSize: 12, color: '#1b2a4a' }}>{aiBaselineAudit.summary}</div>
                                        </div>
                                        {aiBaselineAudit.top_priority && (
                                            <div style={{ fontSize: 11, color: '#4338ca', fontWeight: 600, marginBottom: 8 }}>&rarr; {aiBaselineAudit.top_priority}</div>
                                        )}
                                        {aiBaselineAudit.issues?.length > 0 ? (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
                                                {aiBaselineAudit.issues.map((iss, i) => (
                                                    <div key={i} style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: '8px 10px', borderLeft: `3px solid ${iss.severity === 'high' ? '#dc2626' : iss.severity === 'medium' ? '#d97706' : '#7a869a'}` }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a' }}>{iss.title}</div>
                                                        <div style={{ fontSize: 10, color: '#3d4f6f', margin: '3px 0' }}>{iss.evidence}</div>
                                                        <div style={{ fontSize: 10, color: '#4338ca', fontWeight: 600 }}>&rarr; {iss.fix}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: 11, color: '#0f7b4f' }}>No data quality issues detected.</div>
                                        )}
                                        {aiBaselineAudit.confidence === 'heuristic' && aiBaselineAudit.note && (
                                            <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6, fontStyle: 'italic' }}>{aiBaselineAudit.note}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button className="ap-btn ap-btn--ghost" onClick={runAiBaselineAudit} disabled={aiBaselineLoading}
                                style={{ fontSize: 11, whiteSpace: 'nowrap', borderColor: '#4338ca', color: '#4338ca' }}>
                                {aiBaselineAudit ? 'Re-run' : 'Run'}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="ap-input" placeholder="Search hostname or IP..." value={assetSearch}
                                onChange={e => setAssetSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadAssets()}
                                style={{ width: 240, marginBottom: 0 }} />
                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={loadAssets}>Search</button>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <label className="ap-btn ap-btn--ghost" style={{ fontSize: 11, cursor: 'pointer' }}>
                                Upload CSV
                                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadAssets(e.target.files[0])} />
                            </label>
                            <button className="ap-btn ap-btn--primary" onClick={() => { setAssetForm({ hostname: '', ip_address: '', os_type: 'windows', asset_type: 'endpoint', owner: '', department: '', criticality: 3 }); setShowAssetModal(true); }}>+ Add Asset</button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: '#7a869a' }}>
                            {assetTotal > assets.length
                                ? `${(assetPage - 1) * ASSET_PAGE_SIZE + 1}–${Math.min(assetPage * ASSET_PAGE_SIZE, assetTotal)} of ${assetTotal} assets`
                                : `${assets.length} assets in baseline`}
                        </div>
                        {assetTotal > ASSET_PAGE_SIZE && (() => {
                            const totalPages = Math.ceil(assetTotal / ASSET_PAGE_SIZE);
                            return (
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11 }}>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                                        disabled={assetPage <= 1}
                                        onClick={() => { setAssetPage(p => p - 1); setTimeout(loadAssets, 0); }}>‹ Prev</button>
                                    <span style={{ color: '#3d4f6f' }}>Page {assetPage} of {totalPages}</span>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                                        disabled={assetPage >= totalPages}
                                        onClick={() => { setAssetPage(p => p + 1); setTimeout(loadAssets, 0); }}>Next ›</button>
                                </div>
                            );
                        })()}
                    </div>

                    <table className="ap-table kri-table-sticky">
                        <thead><tr><th>Hostname</th><th>IP Address</th><th>OS</th><th>Type</th><th>Owner</th><th>Department</th><th>Criticality</th><th>BU</th><th>Project</th><th>Location</th><th>Source</th><th>Actions</th></tr></thead>
                        <tbody>
                            {assets.map(a => (
                                <tr key={a.id}>
                                    <td style={{ fontWeight: 600 }}>{a.hostname}</td>
                                    <td>{a.ip_address || '-'}</td>
                                    <td style={{ textTransform: 'capitalize' }}>{a.os_type}</td>
                                    <td style={{ textTransform: 'capitalize', fontSize: 11 }}>{a.asset_type.replace(/_/g, ' ')}</td>
                                    <td>{a.owner || '-'}</td>
                                    <td>{a.department || '-'}</td>
                                    <td>{['', 'Low', 'Medium', 'High', 'Critical'][a.criticality] || a.criticality}</td>
                                    <td>{a.business_unit || '-'}</td>
                                    <td>{a.project || '-'}</td>
                                    <td>{a.location || '-'}</td>
                                    <td><span className="ap-badge ap-badge--ghost">{a.source}</span></td>
                                    <td>
                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', color: '#dc2626' }}
                                            onClick={async () => {
                                                try {
                                                    const r = await svc('kri_delete_asset', a.id);
                                                    const err = r?.statusCode?.error?.message || r?.errors?.message;
                                                    if (err) { flash(err); return; }
                                                    flash('Asset removed.');
                                                    loadAssets();
                                                } catch (e) { _showLoadError('asset delete', e); }
                                            }}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                            {assets.length === 0 && (
                                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#7a869a' }}>
                                    <div style={{ fontSize: 12, marginBottom: 8 }}>No assets in baseline yet.</div>
                                    <div style={{ display: 'inline-flex', gap: 8 }}>
                                        <button className="ap-btn ap-btn--primary" style={{ fontSize: 11 }}
                                            onClick={() => { setAssetForm({ hostname: '', ip_address: '', os_type: 'windows', asset_type: 'endpoint', owner: '', department: '', criticality: 3 }); setShowAssetModal(true); }}>
                                            + Add asset manually
                                        </button>
                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                            onClick={() => handleTabChange('tools')}>
                                            Or sync from a tool →
                                        </button>
                                    </div>
                                </td></tr>
                            )}
                        </tbody>
                    </table>

                    {/* Add Asset Modal */}
                    {showAssetModal && (
                        // Backdrop click intentionally does not dismiss — see Add Tool Modal comment.
                        <div className="ap-modal-overlay">
                            <div className="ap-modal" style={{ maxWidth: 450, position: 'relative' }}>
                                <button
                                    onClick={async () => {
                                        const hasData = !!(assetForm?.hostname || assetForm?.ip_address || assetForm?.serial_number);
                                        if (!hasData) { setShowAssetModal(false); return; }
                                        const ok = await confirmDialog({
                                            title: 'Discard changes?',
                                            message: 'Close without saving? Your changes will be lost.',
                                            confirmLabel: 'Discard', cancelLabel: 'Keep editing', danger: true,
                                        });
                                        if (ok) setShowAssetModal(false);
                                    }}
                                    aria-label="Close"
                                    style={{
                                        position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                        fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f2f5'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                    ×
                                </button>
                                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Add Asset</h3>
                                {[
                                    { key: 'hostname', label: 'Hostname *', type: 'text' },
                                    { key: 'ip_address', label: 'IP Address', type: 'text' },
                                    { key: 'mac_address', label: 'MAC Address', type: 'text' },
                                    { key: 'serial_number', label: 'Serial Number', type: 'text' },
                                    { key: 'os_type', label: 'OS Type', type: 'select', options: [['windows', 'Windows'], ['linux', 'Linux'], ['macos', 'macOS'], ['other', 'Other']] },
                                    { key: 'asset_type', label: 'Asset Type', type: 'select', options: [['endpoint', 'Endpoint'], ['server', 'Server'], ['virtual_machine', 'Virtual Machine'], ['mobile', 'Mobile'], ['network_device', 'Network Device'], ['other', 'Other']] },
                                    { key: 'owner', label: 'Owner', type: 'text' },
                                    { key: 'department', label: 'Department', type: 'text' },
                                    { key: 'criticality', label: 'Criticality', type: 'select', options: [[1, 'Low'], [2, 'Medium'], [3, 'High'], [4, 'Critical']] },
                                ].map(f => (
                                    <div key={f.key} style={{ marginBottom: 8 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                                        {f.type === 'select' ? (
                                            <select className="ap-input" value={assetForm[f.key]} onChange={e => updateAssetForm({ [f.key]: e.target.value })}>
                                                {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        ) : (
                                            <input className="ap-input" type="text" value={assetForm[f.key] || ''} onChange={e => updateAssetForm({ [f.key]: e.target.value })} />
                                        )}
                                    </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                                    <button className="ap-btn ap-btn--ghost" onClick={() => setShowAssetModal(false)}>Cancel</button>
                                    <button className="ap-btn ap-btn--primary" disabled={!assetForm.hostname} onClick={async () => {
                                        try {
                                            const r = await svc('kri_create_asset', assetForm);
                                            const err = r?.statusCode?.error?.message || r?.errors?.message;
                                            if (err) { flash(err); return; }
                                            setShowAssetModal(false);
                                            flash('Asset added.');
                                            loadAssets();
                                        } catch (e) { _showLoadError('asset create', e); }
                                    }}>Add Asset</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CSV Format help */}
                    <div style={{ marginTop: 12, padding: 12, background: '#f0f7ff', border: '1px solid #bfd7f5', borderRadius: 6, fontSize: 11, color: '#1b2a4a' }}>
                        <strong>CSV Format:</strong> Headers should include: <code>hostname</code>, <code>ip_address</code>, <code>mac_address</code>, <code>serial_number</code>, <code>os_type</code>, <code>asset_type</code>, <code>owner</code>, <code>department</code>, <code>criticality</code>.
                        Only <code>hostname</code> is required.
                    </div>
                </div>
            )}

            {/* COVERAGE MATRIX */}
            {tab === 'matrix' && (
                <div className={`kri-tab-content ${tabRefreshing ? 'kri-tab-refreshing' : ''}`}>
                    {/* NOVA AI coverage insights — pattern detection over the full matrix */}
                    <div style={{ marginBottom: 12, background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', border: '1px solid #c7d2fe', borderRadius: 8, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', letterSpacing: 0.5, marginBottom: 4 }}>NOVA AI &middot; COVERAGE INSIGHTS</div>
                                {aiCoverageLoading && <div style={{ fontSize: 12, color: '#3d4f6f' }}>Scanning the matrix for patterns...</div>}
                                {!aiCoverageLoading && !aiCoverage && (
                                    <div style={{ fontSize: 12, color: '#3d4f6f' }}>Find patterns in your asset × tool grid — under-covered OS classes, departments, or critical assets failing multiple controls.</div>
                                )}
                                {!aiCoverageLoading && aiCoverage?.error && <div style={{ fontSize: 12, color: '#dc2626' }}>{aiCoverage.error}</div>}
                                {!aiCoverageLoading && aiCoverage?.note && (!aiCoverage.insights || aiCoverage.insights.length === 0) && (
                                    <div style={{ fontSize: 12, color: '#3d4f6f' }}>{aiCoverage.note}</div>
                                )}
                                {!aiCoverageLoading && aiCoverage?.insights?.length > 0 && (
                                    <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
                                        {aiCoverage.insights.map((ins, i) => (
                                            <div key={i} style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: '8px 10px' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 2 }}>{ins.title}</div>
                                                <div style={{ fontSize: 11, color: '#3d4f6f' }}>{ins.evidence}</div>
                                                <div style={{ fontSize: 11, color: '#4338ca', marginTop: 4, fontWeight: 600 }}>&rarr; {ins.suggested_action}</div>
                                            </div>
                                        ))}
                                        {aiCoverage.confidence === 'heuristic' && (
                                            <div style={{ fontSize: 9, color: '#7a869a', fontStyle: 'italic' }}>{aiCoverage.note || 'NOVA AI is temporarily unavailable. Showing deterministic insights.'}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button className="ap-btn ap-btn--ghost" onClick={runAiCoverage} disabled={aiCoverageLoading}
                                style={{ fontSize: 11, whiteSpace: 'nowrap', borderColor: '#4338ca', color: '#4338ca' }}>
                                {aiCoverage ? 'Re-run' : 'Run'}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="ap-input" placeholder="Search..." value={matrixSearch}
                                onChange={e => setMatrixSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setMatrixPage(1), setTimeout(loadMatrix, 0))}
                                style={{ width: 200, marginBottom: 0 }} />
                            <select className="ap-input" value={matrixFilter} onChange={e => { setMatrixFilter(e.target.value); }} style={{ width: 160, marginBottom: 0 }}>
                                <option value="">All Assets</option>
                                <option value="non_compliant">Gaps Only</option>
                                <option value="compliant">Compliant Only</option>
                            </select>
                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={() => { setMatrixPage(1); setTimeout(loadMatrix, 0); }}>Apply</button>
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, color: '#7a869a' }}>
                            {matrixData?.total > 0 && (
                                <span>
                                    {(matrixPage - 1) * MATRIX_PAGE_SIZE + 1}–{Math.min(matrixPage * MATRIX_PAGE_SIZE, matrixData.total)} of {matrixData.total} assets
                                </span>
                            )}
                            {matrixData?.total > MATRIX_PAGE_SIZE && (() => {
                                const totalPages = Math.ceil(matrixData.total / MATRIX_PAGE_SIZE);
                                return (
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                                            disabled={matrixPage <= 1}
                                            onClick={() => { setMatrixPage(p => p - 1); setTimeout(loadMatrix, 0); }}>‹ Prev</button>
                                        <span style={{ color: '#3d4f6f' }}>Page {matrixPage} of {totalPages}</span>
                                        <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                                            disabled={matrixPage >= totalPages}
                                            onClick={() => { setMatrixPage(p => p + 1); setTimeout(loadMatrix, 0); }}>Next ›</button>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {matrixData ? (() => {
                        const ASSET_COLS = new Set(['hostname', 'ip_address', 'ip', 'os_type', 'os', 'asset_type', 'type', 'criticality']);
                        // selected_fields resolution: matrix API → tool list → localStorage (persisted at creation time)
                        let storedFields = {};
                        try { storedFields = JSON.parse(localStorage.getItem('kri_tool_fields_v1') || '{}'); } catch (_) { }
                        const toolExtraFields = {};
                        let extraColTotal = 0;
                        (matrixData.tools || []).forEach(mt => {
                            const fullTool = tools.find(t => t.id === mt.id);
                            const sf = mt.selected_fields || fullTool?.selected_fields || storedFields[mt.id] || storedFields[String(mt.id)] || [];
                            const extras = sf.filter(f => !ASSET_COLS.has(f));
                            toolExtraFields[mt.id] = extras;
                            extraColTotal += extras.length;
                        });
                        const totalCols = 5 + (matrixData.tools?.length || 0) + extraColTotal;
                        return (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="ap-table kri-table-sticky">
                                    <thead>
                                        <tr>
                                            <th>Hostname</th><th>IP</th><th>OS</th><th>Type</th><th>Criticality</th>
                                            {matrixData.tools?.map(t => [
                                                <th key={`${t.id}_s`} style={{ textAlign: 'center' }}>{t.name}</th>,
                                                ...(toolExtraFields[t.id] || []).map(f => (
                                                    <th key={`${t.id}_${f}`} style={{ textAlign: 'center', fontSize: 10, color: '#7a869a', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                        {f.replace(/_/g, ' ')}
                                                    </th>
                                                ))
                                            ])}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matrixData.rows?.map(r => (
                                            <tr key={r.id}>
                                                <td style={{ fontWeight: 600 }}>{r.hostname}</td>
                                                <td>{r.ip_address || '-'}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{r.os_type}</td>
                                                <td style={{ textTransform: 'capitalize', fontSize: 11 }}>{r.asset_type?.replace(/_/g, ' ')}</td>
                                                <td>{['', 'Low', 'Med', 'High', 'Crit'][r.criticality] || r.criticality}</td>
                                                {matrixData.tools?.map(t => {
                                                    const rec = r.tools?.[t.id];
                                                    const s = rec?.status || 'unknown';
                                                    const color = s === 'compliant' ? '#0f7b4f' : s === 'non_compliant' ? '#dc2626' : '#7a869a';
                                                    const icon = s === 'compliant' ? '\u2713' : s === 'non_compliant' ? '\u2717' : '?';
                                                    return [
                                                        <td key={`${t.id}_s`} style={{ textAlign: 'center' }}>
                                                            <span style={{ display: 'inline-block', width: 22, height: 22, borderRadius: '50%', background: `${color}15`, color, fontWeight: 700, fontSize: 12, lineHeight: '22px', textAlign: 'center' }}>{icon}</span>
                                                        </td>,
                                                        ...(toolExtraFields[t.id] || []).map(f => {
                                                            const val = rec?.[f];
                                                            return (
                                                                <td key={`${t.id}_${f}`} style={{ fontSize: 11, color: '#3d4f6f', textAlign: 'center' }}>
                                                                    {val != null && val !== '' ? String(val) : '-'}
                                                                </td>
                                                            );
                                                        })
                                                    ];
                                                })}
                                            </tr>
                                        ))}
                                        {(!matrixData.rows || matrixData.rows.length === 0) && (
                                            <tr><td colSpan={totalCols} style={{ textAlign: 'center', padding: 32, color: '#7a869a' }}>
                                                <div style={{ fontSize: 12, marginBottom: 8 }}>No data yet. The matrix populates once you have assets in baseline AND at least one tool synced.</div>
                                                <div style={{ display: 'inline-flex', gap: 8 }}>
                                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                        onClick={() => handleTabChange('assets')}>
                                                        Add assets →
                                                    </button>
                                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }}
                                                        onClick={() => handleTabChange('tools')}>
                                                        Connect a tool →
                                                    </button>
                                                </div>
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })() : (
                        <div style={{ padding: 40, textAlign: 'center', color: '#7a869a' }}>Loading matrix...</div>
                    )}
                </div>
            )}

            {/* GAP REPORT */}
            {tab === 'gaps' && (() => {
                const assetGaps = gaps.asset_gaps || [];
                const ticketGaps = gaps.ticket_gaps || [];
                const total = assetGaps.length + ticketGaps.length;
                const slaLabel = (s) => ({
                    sla_breach: 'Past SLA (open)',
                    closed_late: 'Closed late',
                    reopened: 'Reopened',
                    within_sla: 'Within SLA',
                    closed_on_time: 'Closed on time',
                    unknown: 'Unknown',
                }[s] || s || 'Unknown');
                const slaColor = (s) => (s === 'sla_breach' || s === 'closed_late' || s === 'reopened') ? '#dc2626' : '#7a869a';
                const remediationSelect = (g) => (
                    <select style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #d8dce3', borderRadius: 3, background: '#fff' }}
                        value={g.remediation_status || ''} onChange={async (e) => {
                            await svc('kri_mark_gap_remediated', g.id, { remediation_status: e.target.value });
                            flash('Remediation status updated.'); loadGaps();
                        }}>
                        <option value="">-- Set --</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="accepted">Risk Accepted</option>
                    </select>
                );
                // Bulk actions: select multiple gaps, then mark or export.
                // Selection key = `${kind}:${id}` so asset and ticket IDs don't collide.
                const isSelected = (kind, id) => bulkSelected.has(`${kind}:${id}`);
                const toggleSelected = (kind, id) => {
                    setBulkSelected(prev => {
                        const next = new Set(prev);
                        const key = `${kind}:${id}`;
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                    });
                };
                const selectAll = (kind, items) => {
                    setBulkSelected(prev => {
                        const next = new Set(prev);
                        const allSelected = items.every(g => next.has(`${kind}:${g.id}`));
                        items.forEach(g => {
                            const key = `${kind}:${g.id}`;
                            if (allSelected) next.delete(key); else next.add(key);
                        });
                        return next;
                    });
                };
                const bulkApply = async (status) => {
                    const ids = Array.from(bulkSelected).map(k => parseInt(k.split(':')[1])).filter(Boolean);
                    if (!ids.length) { flash('Select at least one row.'); return; }
                    const ok = await confirmDialog({
                        title: 'Update remediation status?',
                        message: `Mark ${ids.length} gap${ids.length !== 1 ? 's' : ''} as "${status}"?`,
                        confirmLabel: 'Update', cancelLabel: 'Cancel',
                        danger: status === 'wont_fix' || status === 'risk_accepted',
                    });
                    if (!ok) return;
                    let succeeded = 0, fail = 0;
                    for (const id of ids) {
                        try { await svc('kri_mark_gap_remediated', id, { remediation_status: status }); succeeded++; }
                        catch (e) { fail++; }
                    }
                    flash(`Updated ${succeeded} gap${succeeded !== 1 ? 's' : ''}${fail ? `, ${fail} failed` : ''}.`);
                    setBulkSelected(new Set());
                    loadGaps();
                };

                return (
                    <div>
                        {/* NOVA AI gap analysis — clusters + priority actions */}
                        <div style={{ marginBottom: 12, background: 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)', border: '1px solid #c7d2fe', borderRadius: 8, padding: '12px 16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', letterSpacing: 0.5, marginBottom: 4 }}>NOVA AI &middot; GAP ANALYSIS</div>
                                    {aiGapLoading && <div style={{ fontSize: 12, color: '#3d4f6f' }}>Clustering gaps and proposing actions...</div>}
                                    {!aiGapLoading && !aiGapAnalysis && (
                                        <div style={{ fontSize: 12, color: '#3d4f6f' }}>Group these gaps by likely root cause and surface the 3 actions that will close the most ground.</div>
                                    )}
                                    {!aiGapLoading && aiGapAnalysis?.error && <div style={{ fontSize: 12, color: '#dc2626' }}>{aiGapAnalysis.error}</div>}
                                    {!aiGapLoading && aiGapAnalysis && !aiGapAnalysis.error && (
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a', marginBottom: 6 }}>{aiGapAnalysis.summary}</div>
                                            {aiGapAnalysis.clusters?.length > 0 && (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, marginBottom: 8 }}>
                                                    {aiGapAnalysis.clusters.map((c, i) => (
                                                        <div key={i} style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: '8px 10px' }}>
                                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a', marginBottom: 2 }}>
                                                                <span style={{ color: '#dc2626', marginRight: 6 }}>{c.count}</span>{c.title}
                                                            </div>
                                                            <div style={{ fontSize: 10, color: '#3d4f6f', marginBottom: 3 }}>{c.root_cause_hypothesis}</div>
                                                            <div style={{ fontSize: 10, color: '#4338ca', fontWeight: 600 }}>&rarr; {c.recommended_action}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {aiGapAnalysis.priority_actions?.length > 0 && (
                                                <div>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#3d4f6f', marginBottom: 4 }}>PRIORITY ACTIONS</div>
                                                    <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#1b2a4a', lineHeight: 1.6 }}>
                                                        {aiGapAnalysis.priority_actions.map((a, i) => <li key={i}>{a}</li>)}
                                                    </ol>
                                                </div>
                                            )}
                                            {aiGapAnalysis.confidence === 'heuristic' && (
                                                <div style={{ fontSize: 9, color: '#7a869a', marginTop: 6, fontStyle: 'italic' }}>{aiGapAnalysis.note || 'NOVA AI is temporarily unavailable. Showing deterministic analysis.'}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button className="ap-btn ap-btn--ghost" onClick={runAiGapAnalysis} disabled={aiGapLoading || total === 0}
                                    style={{ fontSize: 11, whiteSpace: 'nowrap', borderColor: '#4338ca', color: '#4338ca' }}>
                                    {aiGapAnalysis ? 'Re-run' : 'Run'}
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
                                {(() => {
                                    const at = gaps.total_asset_gaps ?? assetGaps.length;
                                    const tt = gaps.total_ticket_gaps ?? ticketGaps.length;
                                    return (
                                        <>
                                            {at.toLocaleString()} asset gap{at !== 1 ? 's' : ''}
                                            {tt > 0 && <span> &middot; {tt.toLocaleString()} SLA breach{tt !== 1 ? 'es' : ''}</span>}
                                        </>
                                    );
                                })()}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <select className="ap-input" value={gapToolFilter} onChange={e => { setGapToolFilter(e.target.value); }}
                                    style={{ width: 200, marginBottom: 0 }}>
                                    <option value="">All Tools</option>
                                    {tools.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={loadGaps}>Apply</button>
                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={async () => {
                                    try {
                                        const r = await svc('kri_export_gap_report', gapToolFilter ? { tool_id: gapToolFilter } : undefined);
                                        const url = window.URL.createObjectURL(new Blob([r]));
                                        const a = document.createElement('a'); a.href = url; a.download = 'kri_gap_report.csv';
                                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                                    } catch (e) { flash('Export failed.'); }
                                }}>Export CSV</button>
                            </div>
                        </div>

                        {total === 0 && (
                            <div style={{ padding: 24, textAlign: 'center', color: '#0f7b4f', border: '1px solid #d4eddc', borderRadius: 6, background: '#f3fbf6' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>✓ All clear</div>
                                <div style={{ fontSize: 12 }}>No coverage gaps. Every active asset is compliant in every tool that tracks it, and no SLA breaches are open.</div>
                            </div>
                        )}

                        {bulkSelected.size > 0 && (
                            <div style={{
                                marginBottom: 10, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
                            }}>
                                <span style={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                                    {bulkSelected.size} gap{bulkSelected.size !== 1 ? 's' : ''} selected
                                </span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => bulkApply('in_progress')}>Mark In Progress</button>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => bulkApply('resolved')}>Mark Resolved</button>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => bulkApply('accepted')}>Risk Accepted</button>
                                    <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11, padding: '3px 10px', color: '#7a869a' }} onClick={() => setBulkSelected(new Set())}>Clear</button>
                                </div>
                            </div>
                        )}

                        {assetGaps.length > 0 && (
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>Asset Coverage Gaps</div>
                                    {(() => {
                                        const total = gaps.total_asset_gaps ?? assetGaps.length;
                                        const start = (assetGapPage - 1) * GAP_PAGE_SIZE + 1;
                                        const end = Math.min(assetGapPage * GAP_PAGE_SIZE, total);
                                        const totalPages = Math.max(1, Math.ceil(total / GAP_PAGE_SIZE));
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#7a869a' }}>
                                                <span>{start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}</span>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={assetGapPage <= 1}
                                                    onClick={() => setAssetGapPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                                                <span style={{ fontSize: 10 }}>page {assetGapPage} / {totalPages}</span>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={assetGapPage >= totalPages}
                                                    onClick={() => setAssetGapPage(p => p + 1)}>Next ›</button>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <table className="ap-table kri-table-sticky">
                                    <thead><tr>
                                        <th style={{ width: 28 }}>
                                            <input type="checkbox" aria-label="Select all asset gaps"
                                                checked={assetGaps.length > 0 && assetGaps.every(g => isSelected('asset', g.id))}
                                                onChange={() => selectAll('asset', assetGaps)} />
                                        </th>
                                        <th>Hostname</th><th>IP</th><th>OS</th><th>Criticality</th><th>Owner</th><th>Missing Tool</th><th>Remediation</th><th>Risk</th><th>Last Checked</th>
                                    </tr></thead>
                                    <tbody>
                                        {assetGaps.map(g => {
                                            const linkedRisk = (g.remediation_notes || '').match(/Risk #(\d+)/);
                                            return (
                                                <tr key={g.id} style={isSelected('asset', g.id) ? { background: '#fef9c3' } : undefined}>
                                                    <td><input type="checkbox" aria-label={`Select ${g.hostname}`}
                                                        checked={isSelected('asset', g.id)}
                                                        onChange={() => toggleSelected('asset', g.id)} /></td>
                                                    <td style={{ fontWeight: 600 }}>{g.hostname}</td>
                                                    <td>{g.ip_address || '-'}</td>
                                                    <td style={{ textTransform: 'capitalize' }}>{g.os_type}</td>
                                                    <td>{['', 'Low', 'Med', 'High', 'Crit'][g.criticality] || g.criticality}</td>
                                                    <td>{g.owner || '-'}</td>
                                                    <td style={{ color: '#dc2626', fontWeight: 600 }}>{g.tool_name}</td>
                                                    <td>{remediationSelect(g)}</td>
                                                    <td>
                                                        {linkedRisk ? (
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#0f7b4f' }}>&#8594; Risk #{linkedRisk[1]}</span>
                                                        ) : (
                                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', borderColor: '#dc2626', color: '#dc2626' }}
                                                                title="Promote this gap to a Risk Registry entry"
                                                                onClick={() => openRiskFromAssetGap(g)}>+ Create Risk</button>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: 11 }}>{fmtDate(g.last_checked)}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {ticketGaps.length > 0 && (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1b2a4a' }}>SLA Breaches</div>
                                    {(() => {
                                        const total = gaps.total_ticket_gaps ?? ticketGaps.length;
                                        const start = (ticketGapPage - 1) * GAP_PAGE_SIZE + 1;
                                        const end = Math.min(ticketGapPage * GAP_PAGE_SIZE, total);
                                        const totalPages = Math.max(1, Math.ceil(total / GAP_PAGE_SIZE));
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#7a869a' }}>
                                                <span>{start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}</span>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={ticketGapPage <= 1}
                                                    onClick={() => setTicketGapPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                                                <span style={{ fontSize: 10 }}>page {ticketGapPage} / {totalPages}</span>
                                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={ticketGapPage >= totalPages}
                                                    onClick={() => setTicketGapPage(p => p + 1)}>Next ›</button>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <table className="ap-table kri-table-sticky">
                                    <thead><tr>
                                        <th style={{ width: 28 }}>
                                            <input type="checkbox" aria-label="Select all SLA breaches"
                                                checked={ticketGaps.length > 0 && ticketGaps.every(g => isSelected('ticket', g.id))}
                                                onChange={() => selectAll('ticket', ticketGaps)} />
                                        </th>
                                        <th>Ticket ID</th><th>SLA State</th><th>Tool</th><th>Last Activity</th><th>Remediation</th><th>Risk</th><th>Last Checked</th>
                                    </tr></thead>
                                    <tbody>
                                        {ticketGaps.map(g => {
                                            const linkedRisk = (g.remediation_notes || '').match(/Risk #(\d+)/);
                                            return (
                                                <tr key={g.id} style={isSelected('ticket', g.id) ? { background: '#fef9c3' } : undefined}>
                                                    <td><input type="checkbox" aria-label={`Select ${g.ticket_id || g.id}`}
                                                        checked={isSelected('ticket', g.id)}
                                                        onChange={() => toggleSelected('ticket', g.id)} /></td>
                                                    <td style={{ fontWeight: 600 }}>{g.ticket_id || '-'}</td>
                                                    <td style={{ color: slaColor(g.sla_state), fontWeight: 600, textTransform: 'capitalize' }}>{slaLabel(g.sla_state)}</td>
                                                    <td>{g.tool_name}</td>
                                                    <td style={{ fontSize: 11 }}>{fmtDate(g.last_seen)}</td>
                                                    <td>{remediationSelect(g)}</td>
                                                    <td>
                                                        {linkedRisk ? (
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#0f7b4f' }}>&#8594; Risk #{linkedRisk[1]}</span>
                                                        ) : (
                                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 8px', borderColor: '#dc2626', color: '#dc2626' }}
                                                                title="Promote this SLA breach to a Risk Registry entry"
                                                                onClick={() => openRiskFromTicketGap(g)}>+ Create Risk</button>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: 11 }}>{fmtDate(g.last_checked)}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Create Risk Modal — pre-filled from a Gap row, posts to Risk Registry */}
            {/* Pre-flight Upload Checklist modal — pre-export guidance + sample CSV */}
            {checklistOpen && (
                <div className="ap-modal-overlay">
                    <div className="ap-modal" style={{ maxWidth: 760, maxHeight: '88vh', overflow: 'auto', position: 'relative' }}>
                        <button onClick={closeUploadChecklist} aria-label="Close"
                            style={{
                                position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                            }}>×</button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: '#7c3aed' }}>NOVA AI</span>
                            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Upload Checklist</h3>
                        </div>
                        {checklistLoading && <div style={{ fontSize: 12, color: '#4338ca' }}>Loading checklist…</div>}
                        {!checklistLoading && checklistData?.error && (
                            <div style={{ fontSize: 12, color: '#dc2626' }}>{checklistData.error}</div>
                        )}
                        {!checklistLoading && checklistData && !checklistData.error && (() => {
                            const renderColumnList = (cols, kind, kindColor) => (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: kindColor, marginBottom: 6, letterSpacing: 0.5 }}>{kind.toUpperCase()}</div>
                                    <table className="ap-table" style={{ fontSize: 10 }}>
                                        <thead>
                                            <tr><th>Field</th><th>Accepted column names</th><th>Purpose</th><th>Examples</th></tr>
                                        </thead>
                                        <tbody>
                                            {cols.map((c, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: 600 }}>{c.canonical}</td>
                                                    <td><code style={{ fontSize: 10, color: '#1b2a4a' }}>{c.aliases.join(', ')}</code></td>
                                                    <td style={{ color: '#3d4f6f' }}>{c.purpose}</td>
                                                    <td style={{ color: '#7a869a' }}>{(c.example_values || []).join(', ') || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                            return (
                                <div>
                                    <div style={{ fontSize: 12, color: '#3d4f6f', marginBottom: 10, padding: '8px 10px', background: '#f5f7ff', border: '1px solid #c7d2fe', borderRadius: 6 }}>
                                        <strong>{checklistData.category_display}</strong> — {checklistData.shape}-shape upload.
                                        We'll match rows by <code>{checklistData.shape === 'ticket' ? 'ticket_id' : 'hostname'}</code> and group by <strong>{checklistData.unit_label}</strong>.
                                        {checklistData.sla_matrix_hours && (
                                            <div style={{ marginTop: 4, fontSize: 11 }}>
                                                Default SLA matrix: P1={checklistData.sla_matrix_hours.p1}h ·
                                                P2={checklistData.sla_matrix_hours.p2}h ·
                                                P3={checklistData.sla_matrix_hours.p3}h ·
                                                P4={checklistData.sla_matrix_hours.p4}h
                                            </div>
                                        )}
                                    </div>

                                    {renderColumnList(checklistData.required_columns || [], 'Required columns', '#dc2626')}
                                    {renderColumnList(checklistData.recommended_columns || [], 'Recommended', '#d97706')}
                                    {(checklistData.optional_columns?.length > 0) && renderColumnList(checklistData.optional_columns || [], 'Optional dimensions', '#7a869a')}

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', marginBottom: 6, letterSpacing: 0.5 }}>COMMON PITFALLS</div>
                                        <div style={{ display: 'grid', gap: 6 }}>
                                            {(checklistData.common_pitfalls || []).map((p, i) => (
                                                <div key={i} style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 6, padding: '8px 10px' }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1b2a4a', marginBottom: 2 }}>&#9888; {p.issue}</div>
                                                    <div style={{ fontSize: 10, color: '#3d4f6f' }}>{p.fix}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 4 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#3d4f6f', letterSpacing: 0.5 }}>SAMPLE CSV</div>
                                            <button type="button" className="ap-btn ap-btn--ghost" style={{ fontSize: 10, padding: '3px 10px' }}
                                                onClick={downloadSampleCsv}>Download .csv</button>
                                        </div>
                                        <pre style={{ fontSize: 10, background: '#0d1117', color: '#e2e8f0', padding: 10, borderRadius: 6, overflowX: 'auto', margin: 0 }}>
                                            {checklistData.sample_csv}
                                        </pre>
                                    </div>
                                </div>
                            );
                        })()}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                            <button className="ap-btn ap-btn--ghost" onClick={closeUploadChecklist}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {riskModalOpen && riskForm && (
                <div className="ap-modal-overlay">
                    <div className="ap-modal" style={{ maxWidth: 620, position: 'relative' }}>
                        <button onClick={() => { if (!riskSaving) setRiskModalOpen(false); }}
                            aria-label="Close"
                            style={{
                                position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                            }}>
                            ×
                        </button>
                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Create Risk</h3>
                        <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 12 }}>
                            Promote this {riskForm.kind === 'ticket_gap' ? 'SLA breach' : 'coverage gap'} to a formal entry in the Risk Registry. Pre-filled from the gap data — edit before saving.
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Risk Description *</label>
                            <textarea className="ap-input" value={riskForm.risk} rows={2}
                                onChange={e => setRiskForm(f => ({ ...f, risk: e.target.value }))} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Threat</label>
                                <input className="ap-input" value={riskForm.threat}
                                    onChange={e => setRiskForm(f => ({ ...f, threat: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Vulnerability</label>
                                <input className="ap-input" value={riskForm.vulnerability}
                                    onChange={e => setRiskForm(f => ({ ...f, vulnerability: e.target.value }))} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Risk Type</label>
                                <select className="ap-input" value={riskForm.risk_type}
                                    onChange={e => setRiskForm(f => ({ ...f, risk_type: e.target.value }))}>
                                    <option value="technical">Technical</option>
                                    <option value="operational">Operational</option>
                                    <option value="compliance_legal">Compliance / Legal</option>
                                    <option value="financial">Financial</option>
                                    <option value="reputational">Reputational</option>
                                    <option value="strategic">Strategic</option>
                                    <option value="environmental">Environmental</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Likelihood</label>
                                <select className="ap-input" value={riskForm.likelihood}
                                    onChange={e => setRiskForm(f => ({ ...f, likelihood: parseInt(e.target.value) }))}>
                                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Impact</label>
                                <select className="ap-input" value={riskForm.impact}
                                    onChange={e => setRiskForm(f => ({ ...f, impact: parseInt(e.target.value) }))}>
                                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Risk Owner (email)</label>
                                <input className="ap-input" value={riskForm.risk_owner} placeholder="owner@company.com"
                                    onChange={e => setRiskForm(f => ({ ...f, risk_owner: e.target.value }))}
                                    style={{ borderColor: (riskForm.risk_owner && !isEmail(riskForm.risk_owner)) ? '#dc2626' : undefined }} />
                                {riskForm.risk_owner && !isEmail(riskForm.risk_owner) && (
                                    <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>Enter a valid email address.</div>
                                )}
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Treatment</label>
                                <select className="ap-input" value={riskForm.treatment_option}
                                    onChange={e => setRiskForm(f => ({ ...f, treatment_option: e.target.value }))}>
                                    <option value="mitigate">Mitigate</option>
                                    <option value="accept">Accept</option>
                                    <option value="transfer">Transfer</option>
                                    <option value="avoid">Avoid</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Treatment Plan</label>
                            <textarea className="ap-input" value={riskForm.treatment_plan} rows={2}
                                onChange={e => setRiskForm(f => ({ ...f, treatment_plan: e.target.value }))} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Business Unit</label>
                                <select className="ap-input" value={riskForm.business_unit_id || ''}
                                    onChange={e => setRiskForm(f => ({ ...f, business_unit_id: e.target.value ? parseInt(e.target.value) : null }))}>
                                    <option value="">None</option>
                                    {scopeOptions.business_units.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Project</label>
                                <select className="ap-input" value={riskForm.project_id || ''}
                                    onChange={e => setRiskForm(f => ({ ...f, project_id: e.target.value ? parseInt(e.target.value) : null }))}>
                                    <option value="">None</option>
                                    {scopeOptions.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button className="ap-btn ap-btn--ghost" disabled={riskSaving}
                                onClick={() => setRiskModalOpen(false)}>Cancel</button>
                            <button className="ap-btn ap-btn--primary"
                                disabled={riskSaving || !riskForm.risk?.trim() || (riskForm.risk_owner && !isEmail(riskForm.risk_owner))}
                                onClick={submitRisk}>
                                {riskSaving ? 'Creating…' : 'Create Risk &rarr; Risk Registry'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* UNREGISTERED DEVICES — devices the tool reports but
                that are NOT in the baseline. Pre-fix, these were
                silently discarded by the sync algorithm (which only
                walks baseline assets), so a fresh CrowdStrike agent
                on a device the customer hadn't pre-added would never
                appear in the dashboard. Now we surface them per tool
                with one-click "Add to baseline" / "Dismiss" actions. */}
            {tab === 'unmatched' && (
                <div>
                    <div style={{
                        background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#9a3412',
                    }}>
                        <strong>Why this matters:</strong> these devices have an agent installed
                        and reporting to your security tool, but they are not in your asset
                        baseline — so they don&apos;t show up on the dashboard or count toward
                        compliance. Add them to the baseline (one click), or mark them out of
                        scope. Computed on every sync.
                    </div>
                    {unmatchedLoading && (
                        <div style={{ padding: 20, textAlign: 'center', color: '#7a869a' }}>
                            Loading unregistered devices…
                        </div>
                    )}
                    {!unmatchedLoading && Object.keys(unmatched).length === 0 && (
                        <div style={{
                            padding: 30, textAlign: 'center', color: '#7a869a',
                            background: '#fff', border: '1px dashed #d8dce3', borderRadius: 6
                        }}>
                            No tool integrations configured yet — once you connect a vendor
                            and run a sync, any devices reported by the tool but not in your
                            baseline will appear here.
                        </div>
                    )}
                    {!unmatchedLoading && Object.entries(unmatched).map(([toolId, snap]) => {
                        const tool = (tools || []).find(t => String(t.id) === String(toolId));
                        if (!tool) return null;
                        const samples = snap.samples || [];
                        if (snap.count === 0 && samples.length === 0) {
                            return (
                                <div key={toolId} style={{
                                    marginBottom: 16, padding: 14,
                                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                                    borderRadius: 6, fontSize: 12, color: '#065f46'
                                }}>
                                    ✓ <strong>{tool.name}</strong> — every device the tool
                                    reports is already in your baseline. Nothing to register.
                                </div>
                            );
                        }
                        return (
                            <div key={toolId} style={{
                                marginBottom: 16,
                                background: '#fff', border: '1px solid #d8dce3', borderRadius: 6
                            }}>
                                <div style={{
                                    padding: '10px 14px', borderBottom: '1px solid #f0f2f5',
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1b2a4a' }}>
                                            {tool.name}
                                            <span style={{
                                                marginLeft: 8, fontSize: 9, padding: '2px 6px',
                                                borderRadius: 8, background: '#dc2626', color: '#fff',
                                                fontWeight: 700
                                            }}>
                                                {snap.count} unregistered
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 10, color: '#7a869a', marginTop: 2 }}>
                                            Last sync: {snap.computed_at ? new Date(snap.computed_at).toLocaleString() : '—'}
                                            {snap.dismissed_count ? ` · ${snap.dismissed_count} previously dismissed` : ''}
                                        </div>
                                    </div>
                                </div>
                                <table className="ap-table kri-table-sticky" style={{ marginTop: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>Hostname</th>
                                            <th>IP / MAC / Serial</th>
                                            <th>OS</th>
                                            <th>Agent</th>
                                            <th>Last Seen</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {samples.map(d => (
                                            <tr key={d.tool_asset_id || d.hostname}>
                                                <td style={{ fontWeight: 600, color: '#1b2a4a' }}>
                                                    {d.hostname || <em style={{ color: '#7a869a' }}>(no hostname)</em>}
                                                </td>
                                                <td style={{ fontSize: 10, color: '#7a869a', lineHeight: 1.5 }}>
                                                    {d.local_ip ? <div>IP: {d.local_ip}</div> : null}
                                                    {d.mac_address ? <div>MAC: {d.mac_address}</div> : null}
                                                    {d.serial_number ? <div>S/N: {d.serial_number}</div> : null}
                                                </td>
                                                <td style={{ fontSize: 11 }}>
                                                    {d.os_type || '—'}
                                                    {d.os_version ? <div style={{ fontSize: 9, color: '#7a869a' }}>{d.os_version}</div> : null}
                                                </td>
                                                <td>
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 600,
                                                        padding: '2px 8px', borderRadius: 8,
                                                        background: d.agent_status === 'online' ? '#dcfce7' : '#fee2e2',
                                                        color: d.agent_status === 'online' ? '#15803d' : '#991b1b'
                                                    }}>
                                                        {d.agent_status || 'unknown'}
                                                    </span>
                                                    {d.agent_version ? <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>v{d.agent_version}</div> : null}
                                                </td>
                                                <td style={{ fontSize: 10, color: '#7a869a' }}>
                                                    {d.last_seen ? new Date(d.last_seen).toLocaleString() : '—'}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button className="ap-btn ap-btn--primary"
                                                            style={{ fontSize: 10, padding: '3px 8px' }}
                                                            onClick={async () => {
                                                                try {
                                                                    const r = await svc('kri_add_unmatched_to_baseline', toolId, d.tool_asset_id);
                                                                    flash(r?.statusCode?.response_data?.data?.message || 'Added to baseline.');
                                                                    loadUnmatched();
                                                                    loadDashboard({ silent: true });
                                                                } catch (e) { flash('Failed to add to baseline.'); }
                                                            }}>
                                                            + Add to baseline
                                                        </button>
                                                        <button className="ap-btn ap-btn--ghost"
                                                            style={{ fontSize: 10, padding: '3px 8px', color: '#7a869a' }}
                                                            onClick={async () => {
                                                                const ok = await confirmDialog({
                                                                    title: 'Dismiss device?',
                                                                    message: `${d.hostname || d.tool_asset_id} will be hidden and the next sync will skip it. You can un-dismiss later via the tool's advanced config.`,
                                                                    confirmLabel: 'Dismiss', cancelLabel: 'Cancel', danger: false,
                                                                });
                                                                if (!ok) return;
                                                                try {
                                                                    await svc('kri_dismiss_unmatched_tool_asset', toolId, d.tool_asset_id);
                                                                    flash('Device dismissed.');
                                                                    loadUnmatched();
                                                                } catch (e) { flash('Failed to dismiss.'); }
                                                            }}>
                                                            Dismiss
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* KRI LIBRARY — system-of-record catalogue. */}
            {tab === 'library' && (
                <div>
                    <div style={{
                        background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#3730a3',
                    }}>
                        <strong>The KRI Library is your KRI register.</strong> Every named
                        KRI — its thresholds, frequency, owner and framework mapping —
                        lives here. Link one or more tools to auto-populate the live
                        value, or keep it manual. Re-import the same workbook any time:
                        rows match on KRI ID and update in place.
                    </div>
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
                        marginBottom: 12,
                    }}>
                        <input className="ap-input" placeholder="Search by name or KRI ID…"
                            value={definitionSearch}
                            onChange={(e) => setDefinitionSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') loadDefinitions(); }}
                            style={{ minWidth: 220 }} />
                        <select className="ap-input" value={definitionCategory}
                            onChange={(e) => { setDefinitionCategory(e.target.value); }}>
                            <option value="">All risk categories</option>
                            <option value="strategic">Strategic</option>
                            <option value="operational">Operational</option>
                            <option value="compliance">Compliance / Regulatory</option>
                            <option value="financial">Financial</option>
                            <option value="cyber">Cyber / IT Security</option>
                            <option value="vendor">Third-party / Vendor</option>
                            <option value="reputational">Reputational</option>
                            <option value="other">Other</option>
                        </select>
                        <label style={{ fontSize: 11, color: '#3d4f6f', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="checkbox" checked={showInactiveDefinitions}
                                onChange={(e) => setShowInactiveDefinitions(e.target.checked)} />
                            Show archived
                        </label>
                        <button className="ap-btn ap-btn--ghost" onClick={loadDefinitions}>Apply</button>
                        <div style={{ flex: 1 }} />
                        <button className="ap-btn ap-btn--ghost" onClick={downloadDefinitionTemplate}>
                            Download template
                        </button>
                        <label className="ap-btn ap-btn--ghost" style={{ cursor: 'pointer' }}>
                            Import register
                            <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) previewImport(f);
                                    e.target.value = '';
                                }} />
                        </label>
                        <button className="ap-btn ap-btn--primary" onClick={() => openDefinitionForm(null)}>
                            + Add KRI
                        </button>
                    </div>

                    {definitionsLoading && (
                        <div style={{ padding: 20, textAlign: 'center', color: '#7a869a' }}>
                            Loading KRIs…
                        </div>
                    )}

                    {!definitionsLoading && definitions.length === 0 && (
                        <div style={{
                            padding: 30, textAlign: 'center', color: '#7a869a',
                            background: '#fff', border: '1px dashed #d8dce3', borderRadius: 6
                        }}>
                            No KRIs registered yet. Click <strong>+ Add KRI</strong> for a single entry,
                            or <strong>Import register</strong> to upload your existing register.
                        </div>
                    )}

                    {!definitionsLoading && definitions.length > 0 && (
                        <table className="ap-table kri-table-sticky">
                            <thead>
                                <tr>
                                    <th>KRI ID</th>
                                    <th className="ap-cell-left">Name</th>
                                    <th>Category</th>
                                    <th>Frequency</th>
                                    <th>Thresholds (G/A/R)</th>
                                    <th>Owner</th>
                                    <th>Linked Tools</th>
                                    <th>Status</th>
                                    <th className="ap-cell-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {definitions.map(def => (
                                    <tr key={def.id}>
                                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{def.kri_id}</td>
                                        <td className="ap-cell-left">
                                            <div style={{ fontWeight: 600, color: '#1b2a4a' }}>{def.name}</div>
                                            {def.description ? (
                                                <div style={{
                                                    fontSize: 10, color: '#7a869a', marginTop: 2,
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>{def.description}</div>
                                            ) : null}
                                        </td>
                                        <td style={{ fontSize: 11 }}>{def.risk_category_label || def.risk_category}</td>
                                        <td style={{ fontSize: 11 }}>{def.frequency_label || def.frequency}</td>
                                        <td style={{ fontSize: 10, lineHeight: 1.4 }}>
                                            <div style={{ color: '#0f7b4f' }}>{def.threshold_green || '—'}</div>
                                            <div style={{ color: '#d97706' }}>{def.threshold_amber || '—'}</div>
                                            <div style={{ color: '#dc2626' }}>{def.threshold_red || '—'}</div>
                                        </td>
                                        <td style={{ fontSize: 11 }}>
                                            {def.owner_name || '—'}
                                            {def.owner_email ? <div style={{ fontSize: 9, color: '#7a869a' }}>{def.owner_email}</div> : null}
                                        </td>
                                        <td style={{ fontSize: 10 }}>
                                            {(def.linked_tools && def.linked_tools.length)
                                                ? def.linked_tools.map(t => t.name).join(', ')
                                                : <span style={{ color: '#7a869a' }}>Manual</span>}
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                                                background: def.current_status === 'green' ? '#dcfce7'
                                                    : def.current_status === 'amber' ? '#fef3c7'
                                                        : def.current_status === 'red' ? '#fee2e2' : '#e5e7eb',
                                                color: def.current_status === 'green' ? '#15803d'
                                                    : def.current_status === 'amber' ? '#92400e'
                                                        : def.current_status === 'red' ? '#991b1b' : '#475569',
                                            }}>
                                                {def.current_status_label || def.current_status}
                                            </span>
                                            {def.current_value ? <div style={{ fontSize: 9, color: '#7a869a', marginTop: 2 }}>{def.current_value}</div> : null}
                                        </td>
                                        <td className="ap-cell-actions">
                                            <button className="ap-btn ap-btn--ghost"
                                                style={{ fontSize: 10, padding: '3px 8px' }}
                                                onClick={() => openDefinitionForm(def)}>
                                                Edit
                                            </button>
                                            <button className="ap-btn ap-btn--ghost"
                                                style={{ fontSize: 10, padding: '3px 8px', color: '#dc2626' }}
                                                title="Permanently delete this KRI. This cannot be undone."
                                                onClick={() => permanentDeleteDefinition(def)}>
                                                Delete
                                            </button>
                                            {def.is_active ? (
                                                <button className="ap-btn ap-btn--ghost"
                                                    style={{ fontSize: 10, padding: '3px 8px', color: '#dc2626' }}
                                                    onClick={() => deleteDefinition(def)}>
                                                    Archive
                                                </button>
                                            ) : (
                                                <button className="ap-btn ap-btn--ghost"
                                                    style={{ fontSize: 10, padding: '3px 8px', color: '#0f7b4f' }}
                                                    onClick={() => restoreDefinition(def)}>
                                                    Restore
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* KRI Definition Add/Edit modal. Same overlay/structure as
                the rest of KRI Matrix — explicit Cancel/Save, click on
                backdrop ignored so a stray click doesn't wipe the form. */}
            {showDefinitionModal && definitionForm && (
                <div className="ap-modal-overlay">
                    <div className="ap-modal" style={{ maxWidth: 760, maxHeight: '88vh', overflowY: 'auto', position: 'relative' }}>
                        <button
                            onClick={() => setShowDefinitionModal(false)}
                            aria-label="Close"
                            style={{
                                position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                            }}>
                            ×
                        </button>
                        <h3 className="ap-h3" style={{ marginRight: 32 }}>
                            {definitionForm.id ? `Edit ${definitionForm.kri_id || ''} ${definitionForm.name}` : 'Add KRI'}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>KRI Name *</label>
                                <input className="ap-input" value={definitionForm.name || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, name: e.target.value }))} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Description</label>
                                <textarea className="ap-input" rows={2} value={definitionForm.description || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, description: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Risk Category</label>
                                <select className="ap-input" value={definitionForm.risk_category || 'cyber'}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, risk_category: e.target.value }))}>
                                    <option value="strategic">Strategic</option>
                                    <option value="operational">Operational</option>
                                    <option value="compliance">Compliance / Regulatory</option>
                                    <option value="financial">Financial</option>
                                    <option value="cyber">Cyber / IT Security</option>
                                    <option value="vendor">Third-party / Vendor</option>
                                    <option value="reputational">Reputational</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Risk Type</label>
                                <input className="ap-input" value={definitionForm.risk_type || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, risk_type: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Frequency</label>
                                <select className="ap-input" value={definitionForm.frequency || 'monthly'}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, frequency: e.target.value }))}>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="semi_annual">Semi-Annual</option>
                                    <option value="annual">Annual</option>
                                    <option value="on_event">On Event</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Direction</label>
                                <select className="ap-input" value={definitionForm.direction || 'higher_is_better'}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, direction: e.target.value }))}>
                                    <option value="higher_is_better">Higher is better</option>
                                    <option value="lower_is_better">Lower is better</option>
                                    <option value="target_band">Target band</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Threshold Green</label>
                                <input className="ap-input" placeholder=">=95%" value={definitionForm.threshold_green || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, threshold_green: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Threshold Amber</label>
                                <input className="ap-input" placeholder="80-95%" value={definitionForm.threshold_amber || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, threshold_amber: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Threshold Red</label>
                                <input className="ap-input" placeholder="<80%" value={definitionForm.threshold_red || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, threshold_red: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Target Value</label>
                                <input className="ap-input" placeholder="100%" value={definitionForm.target_value || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, target_value: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Owner Name</label>
                                <input className="ap-input" value={definitionForm.owner_name || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, owner_name: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Owner Email</label>
                                <input className="ap-input" type="email" value={definitionForm.owner_email || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, owner_email: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Reporting Manager</label>
                                <input className="ap-input" value={definitionForm.reporting_manager || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, reporting_manager: e.target.value }))} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Measurement Method</label>
                                <textarea className="ap-input" rows={2}
                                    placeholder="Auto from CrowdStrike adapter, or manual quarterly entry by GRC team."
                                    value={definitionForm.measurement_method || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, measurement_method: e.target.value }))} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Calculation Formula</label>
                                <textarea className="ap-input" rows={2}
                                    placeholder="patched_in_sla / total_critical * 100"
                                    value={definitionForm.calculation_formula || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, calculation_formula: e.target.value }))} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Frameworks (semicolon-separated, e.g. "NIST CSF: PR.AC-7; ISO 27001: A.9.4.2")</label>
                                <input className="ap-input"
                                    value={(definitionForm.linked_frameworks || []).join('; ')}
                                    onChange={(e) => setDefinitionForm(f => ({
                                        ...f,
                                        linked_frameworks: e.target.value.split(';').map(s => s.trim()).filter(Boolean),
                                    }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Linked Risks</label>
                                <input className="ap-input"
                                    placeholder="RISK-014; RISK-021"
                                    value={(definitionForm.linked_risks || []).join('; ')}
                                    onChange={(e) => setDefinitionForm(f => ({
                                        ...f,
                                        linked_risks: e.target.value.split(';').map(s => s.trim()).filter(Boolean),
                                    }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Linked Controls</label>
                                <input className="ap-input"
                                    placeholder="CTRL-IAM-03; CTRL-AUD-02"
                                    value={(definitionForm.linked_controls || []).join('; ')}
                                    onChange={(e) => setDefinitionForm(f => ({
                                        ...f,
                                        linked_controls: e.target.value.split(';').map(s => s.trim()).filter(Boolean),
                                    }))} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Linked Tools (auto-populates current value)</label>
                                <select className="ap-input" multiple style={{ minHeight: 80 }}
                                    value={(definitionForm.linked_tool_ids || []).map(String)}
                                    onChange={(e) => setDefinitionForm(f => ({
                                        ...f,
                                        linked_tool_ids: Array.from(e.target.selectedOptions).map(o => Number(o.value)),
                                    }))}>
                                    {(tools || []).map(t => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.vendor})</option>
                                    ))}
                                </select>
                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4 }}>
                                    Hold ⌘ / Ctrl to select multiple. Leave empty for a manual KRI.
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Current Value (manual KRIs)</label>
                                <input className="ap-input" value={definitionForm.current_value || ''}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, current_value: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 10, fontWeight: 600, color: '#1b2a4a', display: 'block', marginBottom: 3 }}>Current Status</label>
                                <select className="ap-input" value={definitionForm.current_status || 'unknown'}
                                    onChange={(e) => setDefinitionForm(f => ({ ...f, current_status: e.target.value }))}>
                                    <option value="green">Green</option>
                                    <option value="amber">Amber</option>
                                    <option value="red">Red</option>
                                    <option value="unknown">Unknown / Not measured</option>
                                </select>
                            </div>
                            {definitionForm.id && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontSize: 11, color: '#3d4f6f', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="checkbox" checked={!!definitionForm.is_active}
                                            onChange={(e) => setDefinitionForm(f => ({ ...f, is_active: e.target.checked }))} />
                                        Active (uncheck to archive)
                                    </label>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                            <button className="ap-btn ap-btn--ghost" onClick={() => setShowDefinitionModal(false)}>Cancel</button>
                            <button className="ap-btn ap-btn--primary" disabled={definitionSaving} onClick={saveDefinition}>
                                {definitionSaving ? 'Saving…' : (definitionForm.id ? 'Save changes' : 'Create KRI')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* KRI register import preview modal. */}
            {importStage && (
                <div className="ap-modal-overlay">
                    <div className="ap-modal" style={{ maxWidth: 720, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
                        <button
                            disabled={importStage === 'committing'}
                            onClick={() => { setImportStage(null); setImportPreview(null); setImportFile(null); }}
                            aria-label="Close"
                            style={{
                                position: 'absolute', top: 10, right: 12, width: 28, height: 28, border: 'none', background: 'transparent',
                                fontSize: 20, lineHeight: 1, color: '#7a869a', cursor: 'pointer', borderRadius: 4
                            }}>
                            ×
                        </button>
                        <h3 className="ap-h3" style={{ marginRight: 32 }}>Import KRI Register</h3>
                        <div>
                            {importFile && (
                                <div style={{ fontSize: 11, color: '#3d4f6f', marginBottom: 8 }}>
                                    File: <strong>{importFile.name}</strong>
                                </div>
                            )}
                            {!importPreview && !importError && (
                                <div style={{ padding: 20, textAlign: 'center', color: '#7a869a' }}>
                                    Parsing workbook…
                                </div>
                            )}
                            {importError && (
                                <div style={{
                                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                                    padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#991b1b',
                                }}>{importError}</div>
                            )}
                            {importPreview && (
                                <>
                                    <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                                        <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 6, padding: 10, minWidth: 120 }}>
                                            <div style={{ fontSize: 11, color: '#7a869a' }}>Rows</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: '#1b2a4a' }}>
                                                {importPreview.summary?.total || 0}
                                            </div>
                                        </div>
                                        <div style={{ background: '#fff', border: '1px solid #d8dce3', borderRadius: 6, padding: 10, minWidth: 120 }}>
                                            <div style={{ fontSize: 11, color: '#7a869a' }}>With KRI ID</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: '#0f7b4f' }}>
                                                {importPreview.summary?.with_kri_id || 0}
                                            </div>
                                        </div>
                                    </div>
                                    {(importPreview.unrecognised_columns || []).length > 0 && (
                                        <div style={{
                                            background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6,
                                            padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#9a3412',
                                        }}>
                                            <strong>Unmatched columns will be ignored:</strong>{' '}
                                            {importPreview.unrecognised_columns.join(', ')}
                                        </div>
                                    )}
                                    <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid #d8dce3', borderRadius: 6 }}>
                                        <table className="ap-table" style={{ margin: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th>KRI ID</th>
                                                    <th className="ap-cell-left">Name</th>
                                                    <th>Category</th>
                                                    <th>Frequency</th>
                                                    <th>Owner</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {importPreview.rows.map((r, i) => (
                                                    <tr key={i}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.kri_id || <em style={{ color: '#7a869a' }}>auto</em>}</td>
                                                        <td className="ap-cell-left" style={{ fontWeight: 600 }}>{r.name}</td>
                                                        <td style={{ fontSize: 11 }}>{r.risk_category}</td>
                                                        <td style={{ fontSize: 11 }}>{r.frequency}</td>
                                                        <td style={{ fontSize: 11 }}>{r.owner_name || r.owner_email || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                            <button className="ap-btn ap-btn--ghost" disabled={importStage === 'committing'}
                                onClick={() => { setImportStage(null); setImportPreview(null); setImportFile(null); }}>
                                Cancel
                            </button>
                            <button className="ap-btn ap-btn--primary"
                                disabled={!importPreview?.rows?.length || importStage === 'committing'}
                                onClick={commitImport}>
                                {importStage === 'committing' ? 'Importing…' : `Import ${importPreview?.rows?.length || 0} KRIs`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SYNC LOGS */}
            {tab === 'logs' && (
                <div>
                    <div style={{ fontSize: 12, color: '#3d4f6f', marginBottom: 12 }}>Recent sync history</div>
                    <table className="ap-table kri-table-sticky">
                        <thead><tr><th>Tool</th><th>Started</th><th>Status</th><th>Baseline</th><th>In Tool</th><th>Compliant</th><th>Gaps</th><th>New Gaps</th><th>Triggered By</th><th>Details</th></tr></thead>
                        <tbody>
                            {syncLogs.map(l => {
                                // Surface the backend's `error` field for any non-success
                                // run. Pre-fix, failed/aborted rows showed a red badge with
                                // no explanation — the message was already in the response,
                                // just never rendered. Render the full text inline (wrapped,
                                // not truncated) so the operator can read it without
                                // hovering or clicking. Long messages still expand into a
                                // dedicated diagnostic panel below the row for easier copy.
                                const isFail = l.status === 'error' || l.status === 'aborted';
                                const detail = (l.error || '').trim();
                                return (
                                    <React.Fragment key={l.id}>
                                        <tr>
                                            <td style={{ fontWeight: 600 }}>{l.tool_name}</td>
                                            <td style={{ fontSize: 11 }}>{fmtDate(l.started_at)}</td>
                                            <td><span className={`ap-badge ${l.status === 'success' ? 'ap-badge--success' : l.status === 'error' ? 'ap-badge--danger' : 'ap-badge--warning'}`}>{l.status}</span></td>
                                            <td>{l.total_baseline}</td>
                                            <td>{l.total_in_tool}</td>
                                            <td style={{ color: '#0f7b4f' }}>{l.compliant}</td>
                                            <td style={{ color: '#dc2626' }}>{l.non_compliant}</td>
                                            <td>{l.new_gaps > 0 ? <span style={{ color: '#dc2626', fontWeight: 600 }}>+{l.new_gaps}</span> : '-'}</td>
                                            <td style={{ textTransform: 'capitalize' }}>{l.triggered_by}</td>
                                            <td style={{ fontSize: 11, maxWidth: 420, minWidth: 240 }}>
                                                {isFail
                                                    ? (detail
                                                        ? <span style={{ color: '#dc2626', display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>{detail}</span>
                                                        : <span style={{ color: '#7a869a', fontStyle: 'italic' }}>No detail captured for this run. The original failure happened in the catch block before a message could be saved — re-run sync to surface the underlying error.</span>)
                                                    : (l.status === 'running'
                                                        ? <span style={{ color: '#7c3aed' }}>In progress…</span>
                                                        : <span style={{ color: '#7a869a' }}>—</span>)}
                                            </td>
                                        </tr>
                                        {isFail && detail && detail.length > 200 && (
                                            <tr>
                                                <td colSpan={10} style={{ background: '#fff5f5', borderLeft: '3px solid #dc2626', padding: '10px 14px', fontSize: 11, color: '#7a1f1f', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                                                    <strong style={{ display: 'block', marginBottom: 4 }}>Full diagnostic detail</strong>
                                                    {detail}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {syncLogs.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#7a869a' }}>No sync logs yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'mapping' && (() => {
                const f = ruleForm;
                const onSave = async () => {
                    const name = (f?.name || '').trim();
                    const pattern = (f?.pattern || '').trim();
                    if (!name) { flash('Name is required.'); return; }
                    if (!pattern) { flash('Pattern is required.'); return; }
                    const crit = parseInt(f.criticality, 10) || 0;
                    const ownerVal = (f.owner || '').trim();
                    const deptVal = (f.department || '').trim();
                    if (!f.business_unit_id && !f.project_id && !(f.location || '').trim()
                        && !ownerVal && !deptVal && !crit) {
                        flash('Set at least one of Business Unit, Project, Location, Owner, Department, or Criticality — otherwise the rule has no effect.');
                        return;
                    }
                    setRuleSaving(true);
                    try {
                        const body = {
                            name, match_field: f.match_field || 'hostname', pattern,
                            business_unit_id: f.business_unit_id || null,
                            project_id: f.project_id || null,
                            location: (f.location || '').trim(),
                            owner: ownerVal,
                            department: deptVal,
                            criticality: crit,
                            priority: parseInt(f.priority, 10) || 100,
                            is_active: f.is_active !== false,
                        };
                        const r = f.id
                            ? await svc('kri_update_mapping_rule', f.id, body)
                            : await svc('kri_create_mapping_rule', body);
                        const err = r?.statusCode?.error?.message || r?.errors?.message;
                        if (err) { flash(err); return; }
                        flash(f.id ? 'Rule updated.' : 'Rule created.');
                        setRuleForm(null);
                        loadMappingRules();
                    } catch (e) { _showLoadError('rule save', e); }
                    finally { setRuleSaving(false); }
                };
                const sample = (f?.match_field === 'ip_address') ? '10.0.1.0/24' : 'fin-prod-*';
                return (
                    <div className={`kri-tab-content ${tabRefreshing ? 'kri-tab-refreshing' : ''}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1b2a4a' }}>Asset Mapping Rules</div>
                                <div style={{ fontSize: 11, color: '#7a869a', marginTop: 2, lineHeight: 1.5 }}>
                                    Backfill Business Unit / Project / Location / Owner / Department / Criticality on auto-imported assets via hostname globs (e.g. <code>fin-prod-*</code>) or CIDR ranges (e.g. <code>10.0.1.0/24</code>).
                                    Lower priority number wins on conflicts. By default Apply only fills empty fields — use Force to overwrite.
                                </div>
                            </div>
                            <button className="ap-btn ap-btn--primary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                onClick={() => setRuleForm({ match_field: 'hostname', priority: 100, is_active: true })}>
                                + New Rule
                            </button>
                        </div>

                        <table className="ap-table kri-table-sticky">
                            <thead><tr>
                                <th style={{ width: 60 }}>Priority</th><th>Name</th><th>Match</th><th>Pattern</th>
                                <th>Business Unit</th><th>Project</th><th>Location</th>
                                <th>Owner</th><th>Department</th><th>Criticality</th>
                                <th style={{ width: 60 }}>Active</th><th style={{ width: 220 }}>Actions</th>
                            </tr></thead>
                            <tbody>
                                {mappingRules.map(r => (
                                    <tr key={r.id}>
                                        <td>{r.priority}</td>
                                        <td style={{ fontWeight: 600 }}>{r.name}</td>
                                        <td><code style={{ fontSize: 10 }}>{r.match_field}</code></td>
                                        <td><code style={{ fontSize: 10 }}>{r.pattern}</code></td>
                                        <td>{r.business_unit_name || '-'}</td>
                                        <td>{r.project_name || '-'}</td>
                                        <td>{r.location || '-'}</td>
                                        <td>{r.owner || '-'}</td>
                                        <td>{r.department || '-'}</td>
                                        <td>{r.criticality ? ['', 'Low', 'Medium', 'High', 'Critical'][r.criticality] || r.criticality : '-'}</td>
                                        <td style={{ color: r.is_active ? '#0f7b4f' : '#7a869a' }}>{r.is_active ? 'yes' : 'no'}</td>
                                        <td>
                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, marginRight: 4 }}
                                                onClick={() => setRuleForm({ ...r })}>Edit</button>
                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, marginRight: 4 }}
                                                disabled={!r.is_active}
                                                title={r.is_active ? 'Backfill matching assets (only empty fields written)' : 'Activate the rule before applying'}
                                                onClick={async () => {
                                                    const ok = await confirmDialog({
                                                        title: 'Apply this rule?',
                                                        message: `Backfill the rule's targets (BU / Project / Location / Owner / Department / Criticality) on every active asset whose ${r.match_field} matches "${r.pattern}". Only empty fields will be written. To overwrite existing values, use Force Apply instead.`,
                                                        confirmLabel: 'Apply', cancelLabel: 'Cancel',
                                                    });
                                                    if (!ok) return;
                                                    try {
                                                        const res = await svc('kri_apply_mapping_rule', r.id);
                                                        const d = res?.statusCode?.response_data?.data;
                                                        flash(`Matched ${d?.matched ?? 0}, updated ${d?.updated ?? 0}.`);
                                                        loadDashboard({ silent: true });
                                                    } catch (e) { _showLoadError('rule apply', e); }
                                                }}>Apply</button>
                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, marginRight: 4, color: '#92400e' }}
                                                disabled={!r.is_active}
                                                title="Overwrite existing BU / Project / Location / Owner / Department / Criticality on matching assets"
                                                onClick={async () => {
                                                    const ok = await confirmDialog({
                                                        title: 'Force-apply this rule?',
                                                        danger: true,
                                                        message: `Overwrite existing BU / Project / Location / Owner / Department / Criticality on every active asset whose ${r.match_field} matches "${r.pattern}". This is destructive — assignments from earlier rules or vendor imports will be replaced.`,
                                                        confirmLabel: 'Force Apply', cancelLabel: 'Cancel',
                                                    });
                                                    if (!ok) return;
                                                    try {
                                                        const res = await svc('kri_apply_mapping_rule', r.id, { force: true });
                                                        const d = res?.statusCode?.response_data?.data;
                                                        flash(`Matched ${d?.matched ?? 0}, updated ${d?.updated ?? 0} (force).`);
                                                        loadDashboard({ silent: true });
                                                    } catch (e) { _showLoadError('rule force-apply', e); }
                                                }}>Force</button>
                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, color: '#dc2626' }}
                                                onClick={async () => {
                                                    const ok = await confirmDialog({
                                                        title: 'Delete rule?', danger: true,
                                                        message: `Delete "${r.name}"? Existing asset assignments produced by this rule are NOT reverted — only the rule itself is removed.`,
                                                        confirmLabel: 'Delete', cancelLabel: 'Cancel',
                                                    });
                                                    if (!ok) return;
                                                    try {
                                                        await svc('kri_delete_mapping_rule', r.id);
                                                        flash('Rule deleted.');
                                                        loadMappingRules();
                                                    } catch (e) { _showLoadError('rule delete', e); }
                                                }}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {mappingRules.length === 0 && (
                                    <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#7a869a' }}>
                                        No rules yet. Click "+ New Rule" to start mapping auto-imported assets to your BUs / Projects / Owners / Departments / Criticality.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>

                        {f && (
                            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => !ruleSaving && setRuleForm(null)}>
                                <div style={{ background: '#fff', borderRadius: 8, padding: 20, width: 520, maxWidth: '92vw' }}
                                    onClick={e => e.stopPropagation()}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1b2a4a', marginBottom: 12 }}>
                                        {f.id ? 'Edit Rule' : 'New Mapping Rule'}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Name</label>
                                            <input className="ap-input" value={f.name || ''} placeholder="Finance prod laptops"
                                                onChange={e => setRuleForm(s => ({ ...s, name: e.target.value }))} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Priority</label>
                                            <input className="ap-input" type="number" value={f.priority ?? 100}
                                                onChange={e => setRuleForm(s => ({ ...s, priority: e.target.value }))} />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, marginBottom: 8 }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Match field</label>
                                            <select className="ap-input" value={f.match_field || 'hostname'}
                                                onChange={e => setRuleForm(s => ({ ...s, match_field: e.target.value }))}>
                                                <option value="hostname">Hostname (glob)</option>
                                                <option value="ip_address">IP / CIDR</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Pattern</label>
                                            <input className="ap-input" value={f.pattern || ''} placeholder={sample}
                                                onChange={e => setRuleForm(s => ({ ...s, pattern: e.target.value }))} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 10, color: '#7a869a', marginBottom: 12 }}>
                                        Set at least one of Business Unit / Project / Location.
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Business Unit</label>
                                            <select className="ap-input" value={f.business_unit_id || ''}
                                                onChange={e => setRuleForm(s => ({ ...s, business_unit_id: e.target.value ? parseInt(e.target.value, 10) : null }))}>
                                                <option value="">None</option>
                                                {(scopeOptions.business_units || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Project</label>
                                            <select className="ap-input" value={f.project_id || ''}
                                                onChange={e => setRuleForm(s => ({ ...s, project_id: e.target.value ? parseInt(e.target.value, 10) : null }))}>
                                                <option value="">None</option>
                                                {(scopeOptions.projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Location</label>
                                        <input className="ap-input" value={f.location || ''} placeholder="e.g. Mumbai DC"
                                            onChange={e => setRuleForm(s => ({ ...s, location: e.target.value }))} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Owner</label>
                                            <input className="ap-input" value={f.owner || ''} placeholder="e.g. jane.doe@company.com"
                                                onChange={e => setRuleForm(s => ({ ...s, owner: e.target.value }))} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Department</label>
                                            <input className="ap-input" value={f.department || ''} placeholder="e.g. Finance"
                                                onChange={e => setRuleForm(s => ({ ...s, department: e.target.value }))} />
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Criticality</label>
                                        <select className="ap-input" value={f.criticality || 0}
                                            onChange={e => setRuleForm(s => ({ ...s, criticality: parseInt(e.target.value, 10) || 0 }))}>
                                            <option value={0}>Don't set (leave asset's existing value)</option>
                                            <option value={1}>1 — Low</option>
                                            <option value={2}>2 — Medium</option>
                                            <option value={3}>3 — High</option>
                                            <option value={4}>4 — Critical</option>
                                        </select>
                                        <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
                                            All vendor-imported assets default to High (3). Use a rule + Force Apply to mass-tag your Critical assets.
                                        </div>
                                    </div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 16 }}>
                                        <input type="checkbox" checked={f.is_active !== false}
                                            onChange={e => setRuleForm(s => ({ ...s, is_active: e.target.checked }))} />
                                        Active (inactive rules are skipped during sync auto-import + Apply)
                                    </label>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                        <button className="ap-btn ap-btn--ghost" disabled={ruleSaving}
                                            onClick={() => setRuleForm(null)}>Cancel</button>
                                        <button className="ap-btn ap-btn--primary" disabled={ruleSaving} onClick={onSave}>
                                            {ruleSaving ? 'Saving…' : (f.id ? 'Save changes' : 'Create rule')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {tab === 'settings' && (() => {
                const s = orgSettings;
                const ff = orgSettingsForm;
                if (!ff) {
                    return (
                        <div style={{ padding: 24, textAlign: 'center', color: '#7a869a', fontSize: 12 }}>
                            Loading settings…
                        </div>
                    );
                }
                const dirty = ff.ai_enabled !== !!s?.ai_enabled
                    || (ff.reason && !ff.ai_enabled)
                    || ff.target_compliance_pct !== (s?.target_compliance_pct ?? 95)
                    || ff.rag_amber_threshold !== (s?.rag_amber_threshold ?? 85)
                    || ff.rag_red_threshold !== (s?.rag_red_threshold ?? 70);
                const aiBlockSave = !ff.ai_enabled && !(ff.reason || '').trim();
                const ragOrderingValid = (
                    ff.rag_red_threshold <= ff.rag_amber_threshold
                    && ff.rag_amber_threshold <= ff.target_compliance_pct
                );
                const blockSave = aiBlockSave || !ragOrderingValid;
                const onSave = async () => {
                    if (aiBlockSave) { flash('A reason is required when disabling NOVA AI.'); return; }
                    if (!ragOrderingValid) { flash('Invalid RAG bands: red ≤ amber ≤ target ≤ 100.'); return; }
                    setOrgSettingsSaving(true);
                    try {
                        const r = await svc('kri_update_org_settings', {
                            ai_enabled: !!ff.ai_enabled,
                            reason: (ff.reason || '').trim(),
                            target_compliance_pct: ff.target_compliance_pct,
                            rag_amber_threshold: ff.rag_amber_threshold,
                            rag_red_threshold: ff.rag_red_threshold,
                        });
                        const err = r?.statusCode?.error?.message || r?.errors?.message;
                        if (err) { flash(err); return; }
                        flash('Settings saved.');
                        loadOrgSettings();
                    } catch (e) { _showLoadError('settings save', e); }
                    finally { setOrgSettingsSaving(false); }
                };
                const onRefreshTrend = async () => {
                    try {
                        const r = await svc('kri_save_snapshot');
                        const d = r?.statusCode?.response_data?.data;
                        if (d?.snapshot_date) {
                            flash(`Snapshot saved for ${d.snapshot_date}: ${d.compliance_pct}% compliance.`);
                            loadDashboard({ silent: true });
                        } else {
                            flash('Snapshot saved.');
                        }
                    } catch (e) { _showLoadError('snapshot', e); }
                };
                return (
                    <div className={`kri-tab-content ${tabRefreshing ? 'kri-tab-refreshing' : ''}`} style={{ maxWidth: 720 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1b2a4a', marginBottom: 4 }}>KRI Settings</div>
                        <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 16 }}>
                            Per-organisation defaults that apply across the KRI Matrix.
                        </div>

                        {/* AI-first summary card — current state at a glance. */}
                        <div style={{
                            background: s?.ai_enabled ? 'linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)' : '#fff7ed',
                            border: `1px solid ${s?.ai_enabled ? '#c7d2fe' : '#fdba74'}`,
                            borderRadius: 8, padding: '14px 16px', marginBottom: 16
                        }}>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: s?.ai_enabled ? '#4338ca' : '#9a3412', marginBottom: 4 }}>
                                NOVA AI &middot; {s?.ai_enabled ? 'ENABLED' : 'DISABLED'}
                            </div>
                            <div style={{ fontSize: 12, color: '#1b2a4a', lineHeight: 1.5 }}>
                                {s?.ai_enabled
                                    ? 'Gap-analysis, coverage insights, and dashboard narrative are generated by NOVA AI. Heuristic fallback runs when the LLM is unreachable.'
                                    : <>NOVA AI calls are disabled for this org. Every panel falls back to deterministic heuristics. Reason: <em>{s?.ai_disabled_reason || '(not recorded)'}</em>.</>}
                            </div>
                            {!s?.ai_enabled && (s?.ai_disabled_by_email || s?.ai_disabled_at) && (
                                <div style={{ fontSize: 10, color: '#7a869a', marginTop: 6 }}>
                                    Disabled by {s?.ai_disabled_by_email || '(unknown)'} on {s?.ai_disabled_at ? new Date(s.ai_disabled_at).toLocaleString() : '(unknown)'}.
                                </div>
                            )}
                        </div>

                        <div style={{ background: '#fff', border: '1px solid #e5e7f0', borderRadius: 8, padding: 16 }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!ff.ai_enabled}
                                    onChange={e => setOrgSettingsForm(x => ({ ...x, ai_enabled: e.target.checked, reason: e.target.checked ? '' : x.reason }))}
                                    style={{ marginTop: 3 }} />
                                <span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1b2a4a' }}>Enable NOVA AI for KRI</span>
                                    <span style={{ display: 'block', fontSize: 11, color: '#7a869a', marginTop: 2 }}>
                                        Off = every NOVA panel uses the deterministic heuristic and never sends data to the LLM.
                                        Required-by-policy environments (regulated tenants, no-egress deployments) should leave this off.
                                    </span>
                                </span>
                            </label>

                            {!ff.ai_enabled && (
                                <div style={{ marginTop: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Reason for disabling <span style={{ color: '#dc2626' }}>*</span></label>
                                    <textarea className="ap-input" rows={3} value={ff.reason || ''}
                                        placeholder="e.g. Regulated environment — LLM egress prohibited per security policy SP-2026-04."
                                        onChange={e => setOrgSettingsForm(x => ({ ...x, reason: e.target.value }))} />
                                    <div style={{ fontSize: 10, color: '#7a869a', marginTop: 4 }}>
                                        Captured in the audit log alongside your email and the timestamp.
                                    </div>
                                </div>
                            )}

                            <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '16px 0' }} />

                            {/* Compliance target + RAG bands */}
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1b2a4a', marginBottom: 4 }}>Compliance target &amp; RAG bands</div>
                                <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 8 }}>
                                    Defines the org's policy bar. The dashboard's gauge colors red below {ff.rag_red_threshold}%, amber {ff.rag_red_threshold}–{ff.rag_amber_threshold}%, green ≥{ff.rag_amber_threshold}%. Targets typically come from your compliance regime — e.g. SOC 2 CC6.1 / NIST CSF / internal SLA.
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Target %</label>
                                        <input className="ap-input" type="number" min={0} max={100} value={ff.target_compliance_pct ?? 95}
                                            onChange={e => setOrgSettingsForm(x => ({ ...x, target_compliance_pct: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) }))} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Amber threshold %</label>
                                        <input className="ap-input" type="number" min={0} max={100} value={ff.rag_amber_threshold ?? 85}
                                            onChange={e => setOrgSettingsForm(x => ({ ...x, rag_amber_threshold: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) }))} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Red threshold %</label>
                                        <input className="ap-input" type="number" min={0} max={100} value={ff.rag_red_threshold ?? 70}
                                            onChange={e => setOrgSettingsForm(x => ({ ...x, rag_red_threshold: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) }))} />
                                    </div>
                                </div>
                                {!ragOrderingValid && (
                                    <div style={{ fontSize: 10, color: '#dc2626', marginTop: 6 }}>
                                        Invalid ordering — must be: red ≤ amber ≤ target ≤ 100.
                                    </div>
                                )}
                            </div>

                            <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '16px 0' }} />

                            {/* Compliance trend snapshot */}
                            {/* <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1b2a4a', marginBottom: 4 }}>Compliance trend</div>
                                <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 8 }}>
                                    A daily snapshot anchors the dashboard's trend chart. The cron job runs once a day; click below to write today's snapshot now (e.g. immediately after a large rollout to anchor the trend from a known-good moment).
                                </div>
                                <button className="ap-btn ap-btn--ghost" style={{ fontSize: 11 }} onClick={onRefreshTrend}>
                                    Save snapshot for today
                                </button>
                            </div> */}

                            <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '16px 0' }} />

                            {/* Tool rollout grace period — quick controls */}
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1b2a4a', marginBottom: 4 }}>Tool rollout grace period</div>
                                <div style={{ fontSize: 11, color: '#7a869a', marginBottom: 8 }}>
                                    During the first days after adding a new tool, its records can flip every asset to "missing" until the rollout reconciles. Setting a grace window excludes that tool from the org-wide rollup until it expires — per-tool tile still shows the real numbers.
                                </div>
                                {(tools || []).length === 0 ? (
                                    <div style={{ fontSize: 11, color: '#7a869a' }}>Load the Tool Integrations tab once to populate this list.</div>
                                ) : (
                                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ color: '#7a869a' }}>
                                                <th style={{ textAlign: 'left', fontWeight: 600, padding: '4px 6px' }}>Tool</th>
                                                <th style={{ textAlign: 'left', fontWeight: 600, padding: '4px 6px' }}>Grace until</th>
                                                <th style={{ textAlign: 'right', fontWeight: 600, padding: '4px 6px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tools.map(t => (
                                                <tr key={t.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                                                    <td style={{ padding: '6px', color: '#1b2a4a', fontWeight: 600 }}>{t.name}</td>
                                                    <td style={{ padding: '6px', color: t.in_rollout_until ? '#9a3412' : '#7a869a' }}>
                                                        {t.in_rollout_until ? new Date(t.in_rollout_until).toLocaleString() : '—'}
                                                    </td>
                                                    <td style={{ padding: '6px', textAlign: 'right' }}>
                                                        {[7, 14, 30].map(d => (
                                                            <button key={d} className="ap-btn ap-btn--ghost" style={{ fontSize: 10, marginRight: 4 }}
                                                                onClick={async () => {
                                                                    try {
                                                                        await svc('kri_set_tool_rollout_grace', t.id, { days: d });
                                                                        flash(`${t.name}: grace set to ${d} days.`);
                                                                        loadTools(); loadDashboard({ silent: true });
                                                                    } catch (e) { _showLoadError('rollout grace', e); }
                                                                }}>+{d}d</button>
                                                        ))}
                                                        {t.in_rollout_until && (
                                                            <button className="ap-btn ap-btn--ghost" style={{ fontSize: 10, color: '#dc2626' }}
                                                                onClick={async () => {
                                                                    try {
                                                                        await svc('kri_set_tool_rollout_grace', t.id, { days: 0 });
                                                                        flash(`${t.name}: grace cleared.`);
                                                                        loadTools(); loadDashboard({ silent: true });
                                                                    } catch (e) { _showLoadError('rollout grace clear', e); }
                                                                }}>Clear</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                                <button className="ap-btn ap-btn--ghost" disabled={orgSettingsSaving || !dirty}
                                    onClick={() => setOrgSettingsForm({
                                        ai_enabled: !!s?.ai_enabled, reason: '',
                                        target_compliance_pct: s?.target_compliance_pct ?? 95,
                                        rag_amber_threshold: s?.rag_amber_threshold ?? 85,
                                        rag_red_threshold: s?.rag_red_threshold ?? 70,
                                    })}>Reset</button>
                                <button className="ap-btn ap-btn--primary" disabled={orgSettingsSaving || !dirty || blockSave}
                                    onClick={onSave}>
                                    {orgSettingsSaving ? 'Saving…' : 'Save changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
